import SpotlightCourses, { SPOTLIGHT_GRID } from "./spotlight/SpotlightCourses";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { toastPoints, toastEarned } from "./pointsToast";
import config from "./config";
import { injectCourseScript, claimCourseScript } from "./injectCustomScript";
import { InlineLoader } from "./PageLoader";
import { CardGridSkeleton, LessonSkeleton } from "./skeletons";
import {
    fetchCourses, fetchCourseDetail, fetchLessonDetail, markLessonComplete, submitLessonGoal,
    fetchQuiz, submitQuiz, fetchQuizAnswers,
    fetchLessonNotes, saveLessonNote, deleteLessonNote,
    fetchDownloads,
    fetchLessonDiscussion, createLessonPost, createLessonComment, deleteLessonDiscussion, toggleLessonLike, fetchLessonMentionMembers,
    toggleSavedLesson, fetchSavedLessons, fetchCourseReviews,
    fetchCompletion, fetchVideoPositions, saveVideoPosition,
    fetchMemberLevel, fetchMemberStreak, fetchMemberBadges, fetchNextBadge, fetchMemberRewards, redeemReward, fetchGamifyPoints,
} from "./nativeLearning";
import MockTestLauncher from "./MockTestLauncher";
import RichText from "./RichText";
import ClampedRichText from "./ClampedRichText";
import { HeartIcon as HeartSolid } from "@heroicons/react/24/solid";
import { HeartIcon as HeartOutline } from "@heroicons/react/24/outline";
import { SectionHeader, UpcomingEventBanner } from "./MembershipViews";
import CourseSearch from "./CourseSearch";
import Pagination from "./Pagination";
import Tooltip from "./Tooltip";
import { makeLabels } from "./labels";
import EmojiPicker from "./EmojiPicker";
import GifPicker from "./GifPicker";
import InlineBanner from "./InlineBanner";
// Shared with the Members page's course gate, so both lock screens are the same
// picture. Its own module because CourseViews already imports from
// MembershipViews — importing back the other way would close a cycle.
import LockIllustration from "./LockIllustration";
import CourseOfferModal from "./CourseOfferModal";
import { makeEmbedTimeTracker, loadHlsJs } from "./videoTime";
import { EMBED_ALLOW, EMBED_HEALTH_MS, WATCHABLE_EMBED, YT_EMBED_ALLOW, firstIframeSrc, normalizeEmbed } from "./videoEmbed";
import { attachVdoCipher, isVdoCipherSrc } from "./vdoCipher";
import { ytCommand } from "./youtubeTransport";
import VideoChrome, { useNativeTransport } from "./VideoChrome";
import { asset } from "./assetUrl";
import { instructorsOf, InstructorChips } from "./instructors";
import EmptyState from "./EmptyState";
import { addOutsidePress, removeOutsidePress } from "./outsidePress";
import { useMentions, MentionText, MENTION_RE, serializeMentions } from "./mentions";
import CommentsSheet, { useCompactViewport } from "./CommentsSheet";
import { useLongPressBinder, CommentActionSheet, EditIcon, DeleteIcon } from "./CommentActions";
import { withReferral } from "./affiliateReferral";

// Cards per page in the course grid.
const COURSES_PER_PAGE = 6;

/* ---------- helpers ---------- */
const ACCESS = {
    1: { label: "Free", cls: "bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300" },
    2: { label: "Paid", cls: "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300" },
    3: { label: "Locked", cls: "bg-gray-100 text-gray-500" },
};
const LOCK_MSG = {
    paid: "This is a paid course.",
    login: "Sign in to access this course.",
    level: "Reach a higher level to unlock this course.",
};

// Fallback shown when a course has no image, or its image URL fails to load.
// The same course cover the admin shows when a creator sets no image
// (fleximembership public/images/course-default.svg), copied here so both apps
// show one placeholder. The API used to override this with a legacy PNG — see
// FunnelPage::getCourse, which now leaves the image empty instead.
// Routed through asset(): a bare /images/... resolves against the CUSTOMER's
// domain, which answers 405, not against the origin this bundle is served from.
const DEFAULT_COURSE_IMAGE = asset("/images/course-default.svg");

/**
 * A course cover that always shows the WHOLE image, in every catalogue layout.
 *
 * Creators upload covers of every shape (the admin recommends 1280×720, but
 * square and portrait art are common), and each template framed them
 * differently — a fixed-height `object-cover` cropped a different slice of the
 * picture per layout, so one template showed the full cover and another half.
 * Here the image is `object-contain`, so nothing is ever cut off, and a blurred,
 * enlarged copy of the same picture fills whatever the frame has left over —
 * a 16:9 cover fills the frame exactly, any other shape sits on its own colours
 * instead of on black bars. Fills its (relative, overflow-hidden) parent.
 */
function CourseCover({ src }) {
    const [url, setUrl] = useState(src || DEFAULT_COURSE_IMAGE);
    useEffect(() => { setUrl(src || DEFAULT_COURSE_IMAGE); }, [src]);
    const fallback = () => setUrl(DEFAULT_COURSE_IMAGE);
    return (
        <>
            <img
                src={url}
                alt=""
                aria-hidden="true"
                loading="lazy"
                onError={fallback}
                className="absolute inset-0 h-full w-full scale-125 object-cover opacity-80 blur-2xl"
            />
            <img
                src={url}
                alt=""
                /* Catalogues run to dozens of covers; only the ones
                   scrolled into view need fetching. */
                loading="lazy"
                onError={fallback}
                className="relative h-full w-full object-contain transition duration-300 group-hover:scale-105"
            />
        </>
    );
}

// "2026-06-20" -> "Jun 20, 2026"
function fmtDripDate(d) {
    if (!d) return "";
    const dt = new Date(String(d).slice(0, 10) + "T00:00:00");
    if (isNaN(dt)) return String(d);
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Lock message — level-gated courses + per-lesson drip schedules.
function lockMsg(reason, ctx = {}) {
    // The level's own name ("Level 5", "Gold", …) — its NUMBER is just the
    // creator's list position, so it can't be trusted to read as a rank.
    if (reason === "level" && (ctx.unlock_level_name || ctx.unlock_level)) {
        const name = ctx.unlock_level_name || `Level ${ctx.unlock_level}`;
        const pts = ctx.unlock_points ? ` (${ctx.unlock_points} pts)` : "";
        return `Reach ${name}${pts} to unlock this course.`;
    }
    // Whole-course restriction reported by api/lesson-details (restrictCourse):
    // the access window since purchase has ended, or the course hasn't been
    // released yet. The creator's own copy travels with it as upgrade_text and
    // IS the message; this is only the fallback the reference app used.
    if (reason === "course_restricted") {
        return ctx.upgrade_text || "Currently the course access has been restricted";
    }
    if (reason === "access_expired") return "Your access to this lesson has expired.";
    if (reason === "access") return "Access Forbidden!";
    // Module drip — the API's own copy ("THIS MODULE WILL UNLOCK IN 17 HOURS")
    // arrives as lock_message and is preferred by the caller; this is the fallback
    // when a module is locked but the bridge sent no text.
    if (reason === "drip_module") return "This module isn't available yet.";
    // Module upgrade lock (courses.upgrade_data). The creator's own copy arrives as
    // lock_message and is preferred by the caller; this is only the fallback, and
    // it deliberately does NOT promise the module opens with time the way the drip
    // wording above does — nothing but the upgrade opens it.
    if (reason === "upgrade_module") return "This module is locked.";
    // Per-lesson upgrade lock (lessons.upgrade_data). Same deal: the creator's copy
    // arrives as lock_message and wins; this is the fallback when they left it blank.
    if (reason === "upgrade") return "This lesson is locked.";
    if (reason === "drip_prev") return "Complete the previous lesson to unlock this one.";
    if (reason === "drip_days" || reason === "drip_date") {
        return ctx.unlock_on ? `Unlocks on ${fmtDripDate(ctx.unlock_on)}.` : "This lesson unlocks later.";
    }
    // Course Access & Visibility gates (funnel_pages.release_date_mode /
    // restrict_day_mode), resolved by the API into access_lock_reason.
    if (reason === "not_released") {
        // The API sends the same countdown copy the legacy member app used
        // ("You can access this course in 8 HOURS"); the date is the fallback.
        if (ctx.release_message) return ctx.release_message;
        return ctx.release_at
            ? `Opens on ${fmtDripDate(ctx.release_at)}.`
            : "This course opens at a later date.";
    }
    if (reason === "window_ended") {
        // The creator's own upgrade copy wins when they wrote one.
        if (ctx.upgrade_text) return ctx.upgrade_text;
        return ctx.access_expires_at
            ? `Your access ended on ${fmtDripDate(ctx.access_expires_at)}.`
            : "Your access to this course has ended.";
    }
    return LOCK_MSG[reason] || "This course is locked.";
}

function lessonTypeMeta(t) {
    if (t === 2) return { label: "Audio", color: "#8b5cf6", icon: "M9 19V6l12-2v13M9 19a3 3 0 11-6 0 3 3 0 016 0zm12-3a3 3 0 11-6 0 3 3 0 016 0z" };
    if (t === 3) return { label: "Video", color: "#ef4444", icon: "M15 10l4.55-2.28A1 1 0 0121 8.62v6.76a1 1 0 01-1.45.9L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" };
    return { label: "Content", color: "#0ea5e9", icon: "M9 12h6m-6 4h6M9 8h6M5 4h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z" };
}

// Circular progress ring used in the course header. `dark` tunes it for use
// over a dark banner (white track + white label).
function ProgressRing({ pct, primary, size = 46, dark = false }) {
    const r = (size - 6) / 2;
    const c = 2 * Math.PI * r;
    const off = c - (Math.min(pct, 100) / 100) * c;
    const color = pct >= 100 ? (dark ? "#4ade80" : "#16a34a") : primary;
    const track = dark ? "rgba(255,255,255,0.25)" : "rgb(var(--c-gray-200))";
    return (
        <span className="relative inline-flex shrink-0 items-center justify-center" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth="4" />
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .45s ease" }} />
            </svg>
            <span className="absolute font-extrabold" style={{ color: dark ? "#ffffff" : color, fontSize: Math.max(10, Math.round(size * 0.2)) }}>{pct}%</span>
        </span>
    );
}

function Icon({ d, className = "w-4 h-4" }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d={d} />
        </svg>
    );
}

// SVG paths for the course-card meta row (module + lesson count, created date).
const LESSON_ICON = "M9 12h6m-6 4h6M7 21h10a2 2 0 002-2V7.414a1 1 0 00-.293-.707l-4.414-4.414A1 1 0 0013.586 2H7a2 2 0 00-2 2v15a2 2 0 002 2z";
// Stacked layers — a module is the container a course's lessons sit in, so it
// reads as "several of something" next to the single-document lesson icon.
const MODULE_ICON = "M12 3l8 4-8 4-8-4 8-4zm8 9l-8 4-8-4m16 5l-8 4-8-4";
const CALENDAR_ICON = "M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z";

// Course creation date → "16, Jun 2026, 05:25 AM" (matches the reference card).
// Accepts a MySQL datetime ("2026-06-16 05:25:00") or ISO string.
function formatCourseDate(raw) {
    if (!raw) return "";
    const d = new Date(String(raw).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "";
    const mon = d.toLocaleString("en-US", { month: "short" });
    let h = d.getHours();
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    const mm = String(d.getMinutes()).padStart(2, "0");
    const hh = String(h).padStart(2, "0");
    return `${d.getDate()}, ${mon} ${d.getFullYear()}, ${hh}:${mm} ${ampm}`;
}

// Normalise a YouTube/Vimeo/file URL into an embeddable src.
function embedUrl(raw) {
    let url = (raw || "").trim();
    const iframe = url.match(/src="([^"]+)"/);
    if (iframe) url = iframe[1];
    if (url.includes("youtube.com/watch")) {
        try { const v = new URL(url).searchParams.get("v"); if (v) url = `https://www.youtube.com/embed/${v}`; } catch { /* */ }
    } else if (url.includes("youtu.be/")) {
        const id = url.split("youtu.be/")[1]?.split(/[?&]/)[0]; if (id) url = `https://www.youtube.com/embed/${id}`;
    } else if (url.includes("vimeo.com/") && !url.includes("player.vimeo")) {
        const id = url.split("vimeo.com/")[1]?.split(/[?&]/)[0]; if (id) return `https://player.vimeo.com/video/${id}`;
    }
    // YouTube embeds need enablejsapi=1 so the IFrame Player API can read the
    // current playback time (for note timestamps — see videoTime.js) and so the
    // guard overlay can drive play/pause. The rest trim YouTube's own chrome:
    // rel=0 keeps end-of-video suggestions on the same channel, iv_load_policy=3
    // drops annotation cards, modestbranding=1 shrinks the logo, playsinline=1
    // stops iOS from handing playback to the fullscreen system player (which
    // carries its own "open in YouTube" affordance).
    if (/youtube\.com\/embed\//i.test(url)) {
        url = withYtParams(url);
    }
    return url;
}

/* Can this browser put an ORDINARY ELEMENT fullscreen? Desktop and Android can;
   iPhone cannot — there only a <video> may go fullscreen, so an iframe player has
   to keep YouTube's own button or lose fullscreen altogether. */
function canElementFullscreen() {
    return typeof document !== "undefined" && !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);
}

/** The learner-facing param set for a YouTube embed URL (see embedUrl). */
function withYtParams(url) {
    /* YouTube's OWN controls are kept — deliberately. They are the only place a
       viewer can pick video QUALITY and PLAYBACK SPEED: the IFrame API accepts
       setPlaybackQuality and then ignores it (checked against a live player,
       which reported every level from tiny to hd2160 and stayed on `medium`
       through requests for both "small" and "hd1080"). So the settings gear is
       worth more here than a bar of our own, and the routes OFF the page that
       come with it are covered by YouTubeGuard's shields instead.

       fs=0 hides YouTube's fullscreen button — the officially supported way — so
       fullscreen goes through OUR button and lands on our wrapper instead of on
       the iframe. See YouTubePlayer for why that matters. Where an ordinary
       element cannot go fullscreen at all (iPhone), YouTube's button is left in
       place rather than losing fullscreen entirely.

       enablejsapi=1 opens the postMessage channel the shields and the
       note-timestamp tracker (videoTime.js) both speak. */
    const add = {
        enablejsapi: "1", rel: "0", modestbranding: "1", iv_load_policy: "3", playsinline: "1",
        fs: canElementFullscreen() ? "0" : "1",
    };
    let out = url;
    Object.keys(add).forEach((k) => {
        if (new RegExp(`[?&]${k}=`).test(out)) return;
        out += (out.includes("?") ? "&" : "?") + `${k}=${add[k]}`;
    });
    return out;
}

/* ytCommand — talking to an embed over the IFrame API's postMessage channel —
   lives in youtubeTransport.js. Every caller here needs enablejsapi=1 on the
   src, which withYtParams() guarantees for a lesson video; an author-pasted
   embed that lacks it simply gets no play/pause from the shields. */

/**
 * Send the "listening" handshake, then keep the player state YouTube reports
 * back. Only the state is needed now that YouTube draws its own controls: the
 * link shields use it to decide whether a click means play or pause, and whether
 * the end-screen cards are on screen. Note timestamps and resume come from
 * videoTime.js, which speaks the same channel.
 */
function useYtBridge(getFrame) {
    const [info, setInfo] = useState({ state: -1 });

    useEffect(() => {
        // The handshake can land before the iframe is ready, so repeat it briefly.
        let tries = 0;
        const ping = setInterval(() => {
            const f = getFrame();
            if (f) {
                try { f.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: 1 }), "*"); } catch { /* ignore */ }
            }
            if (++tries > 30) clearInterval(ping);
        }, 400);

        const onMsg = (e) => {
            let host = "";
            try { host = new URL(e.origin).hostname; } catch { return; }
            if (!/(^|\.)youtube(-nocookie)?\.com$/.test(host)) return;
            let d = e.data;
            if (typeof d === "string") { try { d = JSON.parse(d); } catch { return; } }
            const i = d?.info;
            const st = i?.playerState ?? (d?.event === "onStateChange" ? d.info : null);
            if (typeof st !== "number") return;
            setInfo({ state: st });
        };
        window.addEventListener("message", onMsg);
        return () => { clearInterval(ping); window.removeEventListener("message", onMsg); };
    }, [getFrame]);

    return info;
}

/* ---------- YouTube link guard ---------- */
/* A YouTube embed always ships its own routes OFF the page: the title/channel
   strip across the top, the share button, "More videos", the YouTube logo, and
   the end-screen cards after the video finishes. None can be turned off with a
   player parameter — modestbranding stopped removing the logo — and the iframe
   is cross-origin, so its DOM can't be touched. What CAN be done is cover them:
   transparent click shields over exactly those zones, where a click plays/pauses
   through the IFrame API instead of opening youtube.com.

   The geometry is measured from the real embed, at desktop AND phone widths,
   because YouTube's own controls have to stay reachable — the SETTINGS GEAR most
   of all: it is the only place a viewer can pick video quality, which the API
   refuses to set (setPlaybackQuality is accepted and then ignored — verified
   against a live player). Today's embed layout is:

     top row     avatar · title · channel  ..... mute · CC · GEAR (right)
     centre      big play/pause
     right       fullscreen, just above the rail
     bottom      time · progress rail
     lowest band share (left) · "More videos" · YouTube logo (right)

   So the top shield stops short of the right-hand buttons, and the bottom shield
   covers only the band BELOW the rail. Both are sized with clamp() because that
   band is a near-fixed ~58px at every frame size while the top strip grows.
   YT.PlayerState: -1 unstarted · 0 ended · 1 playing · 2 paused · 3 buffering · 5 cued. */

/* The fullscreen button rides with YouTube's chrome instead of floating over the
   picture the whole time.

   This has to be CSS, not React state: a cross-origin iframe swallows every
   pointer event inside it, so the wrapper never receives mousemove and JS cannot
   tell whether the cursor is on the player. The :hover STATE is different — the
   browser hit-tests the iframe element itself, so every ancestor of it (this
   wrapper included) is :hover while the pointer is anywhere over the video.

   Gated on (hover: hover): a touch device has no hover at all, and a button that
   can never appear is a player that can never go fullscreen. There it stays
   visible. */
const YT_FS_CSS = `
.yt-fsbtn { opacity: 1; transition: opacity .18s ease; }
@media (hover: hover) and (pointer: fine) {
  .yt-fsbtn { opacity: 0; pointer-events: none; }
  .yt-wrap:hover .yt-fsbtn,
  .yt-fsbtn:focus-visible { opacity: 1; pointer-events: auto; }
}
`;

/* ---------- Provider players: no route off the platform ---------- */
/* Vimeo and Gumlet ship their own logo in the control bar — Vimeo's sits to the
   RIGHT of the fullscreen button — alongside the staff-pick badge and the like /
   watch-later / share buttons. Every one is a link off the platform, none can be
   removed with a player parameter on a non-paid plan, and the iframe is
   cross-origin so its DOM cannot be reached.

   YouTube is handled by covering those zones with transparent click shields
   (YouTubeGuard, above) — it can be, because fs=0 moves fullscreen onto a
   wrapper the shields live in. That approach cannot work here. Vimeo's fullscreen
   button puts the IFRAME fullscreen, and a shield living outside the iframe is
   then not on screen at all — the logo becomes clickable again the moment a
   member expands the video, which is exactly where it was reported.

   So block the NAVIGATION rather than the click. In a sandbox that withholds
   `allow-popups` and `allow-top-navigation`, the logo's target="_blank" anchor,
   window.open() and top.location are all inert — in fullscreen exactly as in the
   page, because it is the navigation that is refused, not a region of pixels.

   What is granted is what a player actually needs: allow-scripts to run at all,
   allow-same-origin to reach its own storage (resume position, quality choice)
   and to speak postMessage with videoTime.js, allow-presentation for casting.
   Fullscreen rides on the allowFullScreen attribute, which sandbox does not gate.

   Checked in Chrome before shipping: inside this sandbox window.open returns
   null, top.location throws, and the anchor click does nothing — while a Gumlet
   embed still loads and plays. */
const PLAYER_SANDBOX = "allow-scripts allow-same-origin allow-presentation";

/* Room kept clear on the right of the top row — the SETTINGS GEAR alone.
   The gear is the only control worth keeping: it is where video QUALITY,
   PLAYBACK SPEED and CAPTIONS are chosen, and the IFrame API can set none of
   them (setPlaybackQuality is accepted and ignored — verified against a live
   player). Everything to its left — the title, the channel, mute, the subtitles
   button — goes under the top shield, so a click there cannot leave the page.
   The gear's own slot is not at a fixed offset (CC disappears on a video with no
   captions, sliding mute across), so the shield stops short by a proportion
   rather than a pixel count and clears the gear however the row is laid out.

   The sheet it opens is kept; its LAST ROW is the one that had to go, and that
   is the bottom band's job — see YT_BAND_LARGE.

   The height is a floor, not a proportion: YouTube's top row is 0..48px from the
   top and the avatar block reaches 56px at EVERY player size — none of its
   chrome scales — so 56px is the real minimum and the 22% only adds margin on a
   large player. */
const YT_TOP_RIGHT_CLEAR = "clamp(56px, 9%, 80px)";
const YT_TOP_STRIP = "clamp(56px, 22%, 78px)";
/* The lowest band: share · "More videos" · the "Watch on YouTube" pill · the
   logo — and the last row of the SETTINGS SHEET, which YouTube draws straight
   over its own controls. Never reaches the progress rail: the clock and the
   playhead sit above it and stay clickable. Also the anchor for our own
   fullscreen button, which is placed relative to it.

   IT IS FIXED PIXELS, NOT A PERCENTAGE. This used to be clamp(52px, 13%, 62px),
   and the percentage was the bug: YouTube's chrome does not scale with the
   player. Measured against a live embed at nine sizes (px above the player's
   bottom edge — see the harness note below):

     player          playhead   pill / More videos   settings sheet: last row
     361x203 / 380   50..62      6..46 /  2..50      off-screen (sheet scrolls)
     400x225         68..80     12..60               off-screen
     464x261         68..80     12..60               -30..14
     560x315         68..80     12..60                 2..46
     600x338         68..80     12..60                16..60
     640x360 +       68..80     12..60                26..70

   Every number is constant from 400px wide up; only the sheet slides, and it
   stops sliding at 640. So:

     under 560px wide -> 50px. The mini player's small-chrome layout: covers the
       "Watch on YouTube" pill (6..46) and stops flush under its playhead
       (50..62). Docked players this narrow scroll the sheet, which keeps its
       last row at or below 46.
     560px and wider  -> 74px. The sheet's "More options" row, in full and then
       some. Size it against the row's HOVER TARGET, not its text box: the inner
       ytListItemViewModelMainContainer is 26..70, but the row that lights up and
       takes the click is its host, yt-list-item-view-model, at 24..72 — and that
       2px lip at the top is exactly what a slow hover found, twice. 74px clears
       it with a pixel to spare against fractional layout, and takes only the
       bottom 2px of the Captions row above it (72..120), which keeps 46 of its
       48px live. It also takes 6px of the playhead (68..80) and 22 of the seek
       slider's 44px hit box (52..96), so the visible rail stays clickable.

   "More options" is why the large step exists: it opens a panel with Copy video
   URL, Copy video URL at current time and Copy embed code. On a narrow player
   the sheet SCROLLS, so no fixed geometry can promise that row is covered —
   which is why the frame is also denied `clipboard-write` (see YT_EMBED_ALLOW).
   Shield first, capability second, exactly as with the pill. */
const YT_BAND_BREAK = "560px";
const YT_BAND_SMALL = "50px";
const YT_BAND_LARGE = "74px";

/* The band steps on the PLAYER's width, not the viewport's: the same component
   renders docked in the lesson and as a 360px floating mini player on the very
   same page, so a media query would give both the same answer. A container
   query asks the wrapper itself. `container-type: inline-size` only contains the
   inline axis — the wrapper's width still comes from its parent and its height
   from aspect-video, in fullscreen as inline.

   The fullscreen button rides 26px above the band, so it moves with it. */
const YT_BAND_CSS = `
.yt-wrap { container-type: inline-size; }
.yt-band { height: ${YT_BAND_SMALL}; }
.yt-band-floor { bottom: ${YT_BAND_SMALL}; }
.yt-fsbtn { right: 6px; bottom: calc(${YT_BAND_SMALL} + 26px); }
@container (min-width: ${YT_BAND_BREAK}) {
  .yt-band { height: ${YT_BAND_LARGE}; }
  .yt-band-floor { bottom: ${YT_BAND_LARGE}; }
  .yt-fsbtn { bottom: calc(${YT_BAND_LARGE} + 26px); }
}
`;

/* The shields are TRANSPARENT: they block, they do not paint.

   They used to be filled black, which hid YouTube's title strip and its
   share / "More videos" / logo row outright. That was reported by a creator —
   two dark bands sitting over a lesson read as something broken in the player
   rather than as part of it — so the pixels are YouTube's again and only the
   NAVIGATION is taken away. A click anywhere in these zones plays or pauses
   instead of opening youtube.com, in the page and in fullscreen alike.

   Nothing here is conditional any more: a fully transparent element still
   hit-tests, so one always-mounted shield per zone blocks every click whether
   or not YouTube's chrome happens to be up. (The old .yt-mask fade existed only
   to keep the black off the picture while the video played.) */

function YouTubeGuard({ getFrame }) {
    const { state } = useYtBridge(getFrame);
    const playing = state === 1 || state === 3;
    const ended = state === 0;

    const toggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        ytCommand(getFrame(), playing ? "pauseVideo" : "playVideo");
    };
    // Invisible, always on: a transparent element hit-tests, so the click never
    // reaches YouTube's link underneath.
    const shield = "absolute z-20 cursor-pointer";

    return (
        <>
            {/* Avatar · title · channel, and with them mute and the subtitles
                button — everything left of the settings gear. */}
            <div
                onClick={toggle}
                aria-hidden="true"
                className={`${shield} left-0 top-0`}
                style={{ right: YT_TOP_RIGHT_CLEAR, height: YT_TOP_STRIP }}
            />

            {/* End-screen cards — every one is a link out AND a thumbnail of
                somewhere else, so once the video ends the whole frame above the
                band is covered in flat black and a click replays. */}
            {ended ? (
                <div
                    onClick={toggle}
                    aria-hidden="true"
                    className={`${shield} yt-band-floor inset-x-0 top-0`}
                    style={{ background: "#000" }}
                />
            ) : null}

            {/* share · watch-later · "More videos" · YouTube logo. */}
            <div
                onClick={toggle}
                aria-hidden="true"
                className={`${shield} yt-band inset-x-0 bottom-0`}
            />
        </>
    );
}

/* ---------- YouTube player ---------- */
/* YouTube's own controls, deliberately.

   This briefly buried YouTube's chrome (controls=0) and drew our own bar over
   the IFrame API instead. That bar could never offer VIDEO QUALITY: the API
   accepts setPlaybackQuality and then ignores it — checked against a live
   player, which reported every level from tiny to hd2160 and stayed on `medium`
   through requests for both "small" and "hd1080". Nor does it have YouTube's
   playback-speed menu or its captions. YouTube alone decides quality, and its
   settings gear is the only place a viewer can override it — so the gear is
   worth more here than a branded bar.

   What the gear arrives with is a set of routes OFF the page: the avatar, title
   and channel strip along the top, share and watch-later, "More videos", the
   YouTube logo, and the end-screen cards. None can be removed with a player
   parameter — modestbranding stopped removing the logo years ago — and the
   iframe is cross-origin, so its DOM cannot be reached. They are covered
   instead: YouTubeGuard lays transparent click shields over exactly those zones,
   and a click there plays or pauses rather than opening youtube.com. The shields
   are measured to stop short of YouTube's own buttons, so the gear, captions and
   the progress rail all stay reachable.

   Uploaded and HLS lessons keep our own bar (VideoChrome); nothing about them is
   decided by a third party. */
function YouTubePlayer({ src, title, frameRef }) {
    const innerRef = useRef(null);
    const wrapRef = useRef(null);
    const getFrame = useCallback(() => innerRef.current, []);
    const [isFs, setIsFs] = useState(false);
    // Where fullscreen can only be YouTube's own (iPhone), the shields cannot
    // follow and its links stay live there. Everywhere else we own it.
    const ownFs = canElementFullscreen();

    /* FULLSCREEN IS TAKEN ON OUR WRAPPER, NEVER ON THE IFRAME.
       A fullscreen element is promoted to the browser's top layer, above every
       other element on the page — so once the IFRAME went fullscreen nothing
       could be drawn over it, and YouTube's title, logo and "More videos" became
       live links out again. Making the wrapper the fullscreen element takes the
       whole player with it: the iframe fills it and the shields stay on top,
       exactly as they are inline. YouTube's own button is removed with fs=0. */
    useEffect(() => {
        const onChange = () => {
            const el = document.fullscreenElement || document.webkitFullscreenElement || null;
            setIsFs(el === wrapRef.current);
        };
        document.addEventListener("fullscreenchange", onChange);
        document.addEventListener("webkitfullscreenchange", onChange);
        return () => {
            document.removeEventListener("fullscreenchange", onChange);
            document.removeEventListener("webkitfullscreenchange", onChange);
        };
    }, []);

    const toggleFs = () => {
        const el = wrapRef.current;
        if (!el) return;
        if (document.fullscreenElement || document.webkitFullscreenElement) {
            (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
            return;
        }
        const req = el.requestFullscreen || el.webkitRequestFullscreen;
        const out = req?.call(el);
        if (out && typeof out.catch === "function") out.catch(() => { /* denied */ });
    };

    return (
        <div
            ref={wrapRef}
            // aspect-video has to go in fullscreen: the browser sizes the
            // fullscreen element to the screen, and a 16:9 ratio fights it.
            className={`yt-wrap relative overflow-hidden bg-black ${isFs ? "h-full w-full" : "aspect-video"}`}
        >
            <style>{YT_FS_CSS + YT_BAND_CSS}</style>
            <iframe
                ref={(el) => { innerRef.current = el; if (frameRef) frameRef.current = el; }}
                title={title}
                src={src}
                className="absolute inset-0 h-full w-full"
                style={{ border: 0 }}
                // THE shield the geometry below cannot be: without allow-popups
                // and allow-top-navigation, YouTube's own "Watch on YouTube"
                // anchor cannot open a tab or move the page wherever it is
                // clicked. Every other embed here was already sandboxed; this
                // player — the one lesson YouTube videos actually use — was not,
                // so a click that found any pixel the shields missed left the
                // course. In the floating mini player that was a real gap: the
                // small-player layout puts the "Watch on YouTube" pill's top
                // edge one pixel above the bottom band, and YouTube grows the
                // pill on hover, so the edge was reachable. Measured against a
                // live player: click at 46px from the bottom → shielded, at
                // 49px → youtube.com/watch opened in a new tab.
                sandbox={PLAYER_SANDBOX}
                // No clipboard-write: it is what "More options → Copy video URL"
                // needs, and that row lives inside the settings sheet the gear
                // has to keep opening. See YT_EMBED_ALLOW.
                allow={YT_EMBED_ALLOW}
                referrerPolicy="strict-origin-when-cross-origin"
                // Only meaningful in the fallback: with fs=0 YouTube draws no
                // button, so nothing inside the frame ever asks for fullscreen.
                allowFullScreen
            />
            <YouTubeGuard getFrame={getFrame} />

            {ownFs ? (
                // Sits where YouTube's own button was — the slot fs=0 just freed,
                // above the rail and clear of the shields.
                <button
                    type="button"
                    onClick={toggleFs}
                    title={isFs ? "Exit full screen" : "Full screen"}
                    aria-label={isFs ? "Exit full screen" : "Full screen"}
                    className="yt-fsbtn absolute z-30 flex h-9 w-9 items-center justify-center rounded-md text-white/80 hover:bg-white/15 hover:text-white"
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                        {isFs
                            ? <path d="M9 3v6H3M15 21v-6h6M3 15h6v6M21 9h-6V3" />
                            : <path d="M3 9V3h6M21 15v6h-6M15 3h6v6M9 21H3v-6" />}
                    </svg>
                </button>
            ) : null}
        </div>
    );
}

const isFile = (u) => /\.(mp4|webm|ogg|mov|m4v|mpd)(\?|$)/i.test(u || "");
// HLS stream (.m3u8) — played via hls.js (or native HLS on Safari), see HlsVideo.
const isHls = (u) => /\.m3u8(\?|$)/i.test(u || "");

/* A video lesson whose video did not resolve to anything playable.

   The reference learner app renders this from inside its players — VdoPlayer
   and GumletVdoplayer both fall back to "Sorry / This video is currently
   unavailable." when the signed embed they were handed is empty. This player
   used to render NOTHING in that situation: the lesson opened with its title
   and description and a blank space where the video belongs, which reads as a
   course-authoring mistake rather than a video that could not be served.

   It happens for real reasons, all of them worth distinguishing:
     • a `flexi` (VdoCipher) lesson — VdoCipher plays only from a server-signed
       OTP, so with no `embed` in the payload there is no source at all
     • a platform video still transcoding, or one whose upload row is gone
     • a lesson genuinely saved without a video */
function VideoUnavailable({ videoType, videoStatus }) {
    const platform = ["flexi", "flexi_gumlet", "gumlet"].includes(String(videoType || "").toLowerCase());
    const processing = platform && !videoStatus;
    return (
        <div className="relative flex aspect-video flex-col items-center justify-center gap-2 bg-black px-6 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white/70">
                <Icon d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" className="w-6 h-6" />
            </span>
            <p className="text-base font-bold text-white sm:text-lg">
                {processing ? "This video is still being prepared." : "This video is currently unavailable."}
            </p>
            <p className="max-w-sm text-[12.5px] leading-relaxed text-white/60">
                {processing
                    ? "It is still processing on the video platform. Please check back in a few minutes."
                    : "Nothing was returned for this lesson's video. If it stays this way, please contact your course instructor."}
            </p>
        </div>
    );
}

/* ================================================================== */
/* Course player (detail): modules + lessons + lesson viewer           */

/**
 * A module's row in the curriculum — the same header in both rail layouts.
 *
 * Every module reads as the same shape whether or not the creator gave it a
 * cover: thumbnail on the left, then name, summary and progress. The cover is a
 * THUMBNAIL rather than a background, because text laid over arbitrary artwork
 * is unreadable half the time and made covered modules look like a different
 * component from the plain ones.
 *
 * The thumbnail carries a corner badge — step number, green tick once every
 * lesson is done, padlock while the module is dripping — so the module's state
 * is legible at a glance, and the progress bar under the summary turns
 * "1/3 lessons" into something you can read without counting.
 *
 * Name and summary wrap to two lines before they clamp, with a tooltip for
 * whatever is still cut. Tapping the row opens the module's first lesson; the
 * chevron toggles it.
 */
/* The intro-video glyph — heroicons' VideoCameraIcon, the same one the admin's
   module row uses for this feature (CourseCurriculum.jsx). Kept identical on
   purpose: a creator who set the video behind a video-camera icon should find it
   behind a video-camera icon on the learner side too. */
const VIDEO_CAMERA_PATH =
    "m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z";

function ModuleHeader({
    module: m,
    index,
    compact = false,
    isOpen,
    mDone,
    mAll,
    mComplete,
    mLocked = false,
    primary,
    onOpenFirst,
    onToggle,
    onPlayIntro = null,
}) {
    // A cover that 404s falls back to the plain tile rather than leaving a
    // broken-image glyph in the rail.
    const [coverFailed, setCoverFailed] = useState(false);
    const cover = !coverFailed && m.image ? m.image : "";
    const description = String(m.description || "").trim();
    const pct = mAll > 0 ? Math.round((mDone / mAll) * 100) : 0;
    const tile = compact ? "h-11 w-11" : "h-12 w-12";

    // Corner badge: state first (locked / complete), step number otherwise.
    const badge = mLocked ? (
        <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-gray-500 text-white ring-2 ring-surface">
            <Icon d="M12 11V7a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6a1 1 0 011-1z" className="h-3 w-3" />
        </span>
    ) : mComplete ? (
        <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-green-500 text-white ring-2 ring-surface">
            <Icon d="M5 13l4 4L19 7" className="h-3 w-3" />
        </span>
    ) : (
        <span
            className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full text-[10px] font-extrabold text-white ring-2 ring-surface"
            style={{ backgroundColor: primary }}
        >
            {index + 1}
        </span>
    );

    return (
        <div className="flex items-stretch bg-surface">
            <button
                onClick={onOpenFirst}
                title={mLocked ? m.lock_message || "This module is locked." : undefined}
                className={`flex min-w-0 flex-1 items-start gap-3 text-left transition-colors hover:bg-gray-50/70 ${
                    compact ? "px-3 py-2.5" : "px-4 py-3.5"
                }`}
            >
                <span className="relative shrink-0">
                    {cover ? (
                        <img
                            src={cover}
                            alt=""
                            loading="lazy"
                            className={`${tile} rounded-xl object-cover ring-1 ring-gray-900/[0.08]`}
                            onError={() => setCoverFailed(true)}
                        />
                    ) : (
                        <span
                            className={`${tile} flex items-center justify-center rounded-xl`}
                            style={{ backgroundColor: `${primary}14`, color: primary, boxShadow: `inset 0 0 0 1px ${primary}2e` }}
                        >
                            <Icon d="M4 6h16M4 12h16M4 18h10" className="h-5 w-5" />
                        </span>
                    )}
                    {badge}
                </span>

                <span className="min-w-0 flex-1">
                    {/* No `block` alongside `line-clamp-2`: both set `display`,
                        and `.block` is emitted later in the stylesheet, so it
                        won a same-specificity tie and left the element as a
                        plain block — no -webkit-box, so -webkit-line-clamp did
                        nothing and long text ran on for as many lines as it
                        liked. `line-clamp-2` already makes the span a box, so
                        `block` was only ever redundant here.

                        That also silenced the tooltip: it opens only when the
                        trigger really overflows, and an unclamped element never
                        does. */}
                    <Tooltip label={m.name}>
                        <span
                            className={`line-clamp-2 break-words font-bold leading-snug ${compact ? "text-[13px]" : "text-sm tracking-tight"} ${
                                mLocked ? "text-gray-400" : "text-gray-900"
                            }`}
                        >
                            {m.name}
                        </span>
                    </Tooltip>

                    {description ? (
                        <Tooltip label={description}>
                            {/* break-words so a single long unbroken string
                                wraps into the two lines and gets ellipsised,
                                instead of being clipped to one. */}
                            <span className={`mt-0.5 line-clamp-2 break-words leading-relaxed text-gray-500 ${compact ? "text-[11.5px]" : "text-[12px]"}`}>
                                {description}
                            </span>
                        </Tooltip>
                    ) : null}

                    {/* A dripping module explains the wait instead of showing a bar
                        that can never move. */}
                    {mLocked && m.lock_message ? (
                        <span className="mt-1.5 block truncate text-[11px] font-semibold" style={{ color: primary }}>
                            {m.lock_message}
                        </span>
                    ) : (
                        <span className="mt-1.5 block">
                            <span className="flex items-center gap-2">
                                <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100">
                                    <span
                                        className="block h-full rounded-full transition-[width] duration-500"
                                        style={{ width: `${pct}%`, backgroundColor: mComplete ? "#22c55e" : primary }}
                                    />
                                </span>
                                <span className={`shrink-0 text-[11px] font-bold tabular-nums ${mComplete ? "text-green-600 dark:text-green-300" : "text-gray-500"}`}>
                                    {pct}%
                                </span>
                            </span>
                            <span className="mt-1 block text-[11px] font-medium text-gray-400">
                                {mDone}/{mAll} {mAll === 1 ? "lesson" : "lessons"} done
                            </span>
                        </span>
                    )}
                </span>
            </button>

            {/* The intro affordance in the header is COLLAPSED-ONLY, and icon-only.
                A labelled "Intro" pill here sat between the title column and the
                chevron and took ~60px of a narrow rail with it, so a long module
                name truncated to make room for a button that repeats what the
                poster card already offers. As a round glyph it reads as a peer of
                the chevron beside it and costs the title nothing; once the module
                is open, ModuleIntroCard is the affordance and this steps aside. */}
            {m.intro_video && onPlayIntro && !isOpen ? (
                <Tooltip label="Watch the module intro" always>
                    <button
                        type="button"
                        onClick={onPlayIntro}
                        aria-label="Watch the module intro"
                        className={`flex shrink-0 items-start ${compact ? "py-2.5 pl-2" : "py-3.5 pl-2.5"}`}
                    >
                        {/* A 28px chip, same footprint as the chevron beside it so
                            the two read as one control cluster. No hover BACKGROUND
                            — a grey wash behind a brand-tinted chip muddies both
                            colours. The lift is a hairline brand ring plus a
                            slight rise on hover; the ring is an inline boxShadow
                            (it takes the brand colour), so a Tailwind shadow
                            utility here would simply be overridden by it. */}
                        <span
                            className="flex h-7 w-7 items-center justify-center rounded-full transition-transform duration-150 hover:-translate-y-px hover:scale-105"
                            style={{
                                backgroundColor: `${primary}14`,
                                color: primary,
                                boxShadow: `inset 0 0 0 1px ${primary}2e`,
                            }}
                        >
                            <Icon d={VIDEO_CAMERA_PATH} className="h-3.5 w-3.5" />
                        </span>
                    </button>
                </Tooltip>
            ) : null}

            <button
                onClick={onToggle}
                aria-label={isOpen ? "Collapse module" : "Expand module"}
                className={`flex shrink-0 items-start transition-colors hover:bg-gray-50/70 ${compact ? "px-3 py-2.5" : "px-3.5 py-3.5"}`}
            >
                <span
                    className="flex h-7 w-7 items-center justify-center rounded-full transition-colors"
                    style={{ backgroundColor: `${primary}14`, color: primary }}
                >
                    <Icon d="M19 9l-7 7-7-7" className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </span>
            </button>
        </div>
    );
}

/**
 * The module's intro video, at the top of the expanded module — above its lessons.
 *
 * A module intro is not a lesson: it has no completion, no drip, no place in the
 * curriculum count, and skipping it must never look like skipping a lesson. So it
 * reads as a poster card rather than another row in the list, and it is the only
 * thing in there that is not a lesson.
 *
 * The poster falls back the same way the module tile does — the creator's chosen
 * thumbnail, then the module's own cover, then a plain brand-tinted tile — so a
 * Gumlet or generic pick with no thumbnail still gets a card worth clicking
 * instead of a black rectangle.
 */
function ModuleIntroCard({ module: m, primary, onPlay }) {
    const [posterFailed, setPosterFailed] = useState(false);
    const intro = m.intro_video;
    if (!intro) return null;
    const poster = !posterFailed ? intro.thumbnail || m.image || "" : "";

    return (
        <button
            type="button"
            onClick={onPlay}
            className="group mb-1.5 flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors hover:bg-gray-50"
        >
            <span className="relative h-12 w-20 shrink-0 overflow-hidden rounded-lg bg-ink ring-1 ring-gray-900/[0.08]">
                {poster ? (
                    <img
                        src={poster}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
                        onError={() => setPosterFailed(true)}
                    />
                ) : (
                    <span className="absolute inset-0" style={{ backgroundColor: `${primary}22` }} />
                )}
                <span className="absolute inset-0 flex items-center justify-center">
                    <span
                        className="flex h-7 w-7 items-center justify-center rounded-full shadow-sm ring-1 ring-black/10 transition-transform group-hover:scale-110"
                        style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                    >
                        <Icon d="M8 5v14l11-7z" className="ml-0.5 h-3.5 w-3.5" />
                    </span>
                </span>
            </span>

            <span className="min-w-0 flex-1">
                <span className="block text-[11px] font-bold uppercase tracking-wide" style={{ color: primary }}>
                    Module intro
                </span>
                <span className="mt-0.5 line-clamp-1 block text-[12.5px] font-semibold text-gray-700">
                    {intro.title || `Watch the intro to ${m.name}`}
                </span>
            </span>
        </button>
    );
}

/* ================================================================== */
// Goal (assignment) panel shown inside a lesson — gates lesson completion.
function GoalPanel({ goal, primary, form, setForm, submitting, onSubmit }) {
    const st = goal.my_status; // null=not submitted, 0=pending, 1=approved, 2=rejected
    const type = goal.submission_type; // 0 action, 1 text, 2 video, 3 audio
    return (
        <div className="mt-5 rounded-xl border-2 border-dashed p-4" style={{ borderColor: `${primary}55`, backgroundColor: `${primary}08` }}>
            <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>
                    <Icon d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: primary }}>Goal to complete this lesson</p>
                    <p className="text-sm font-bold text-gray-900">{goal.title}</p>
                </div>
            </div>
            {goal.short_description && <p className="mt-2 text-sm text-gray-600">{goal.short_description}</p>}
            {goal.description && <RichText html={goal.description} className="mt-1 text-sm leading-relaxed text-gray-600" />}

            {st === 1 ? (
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-green-50 dark:bg-green-500/15 px-3 py-2 text-sm font-semibold text-green-700 dark:text-green-300">
                    <Icon d="M5 13l4 4L19 7" className="w-4 h-4" /> Goal approved — lesson complete!
                </div>
            ) : st === 0 ? (
                <div className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-500/15 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
                    <p className="font-semibold">Submitted — awaiting instructor review.</p>
                    {goal.my_text && <p className="mt-1 whitespace-pre-wrap text-amber-800/80">{goal.my_text}</p>}
                    {goal.my_url && <a href={goal.my_url} target="_blank" rel="noreferrer" className="mt-1 block break-all underline">{goal.my_url}</a>}
                </div>
            ) : (
                <div className="mt-3">
                    {st === 2 && (
                        <div className="mb-2 rounded-lg bg-red-50 dark:bg-red-500/15 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                            <span className="font-semibold">Not approved.</span>{goal.review_note ? ` ${goal.review_note}` : ""} Please revise and resubmit.
                        </div>
                    )}
                    {type === 1 && (
                        <textarea
                            rows={4}
                            value={form.text}
                            onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                            placeholder="Write your response…"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
                        />
                    )}
                    {(type === 2 || type === 3) && (
                        <input
                            value={form.url}
                            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                            placeholder={type === 2 ? "Paste your video link…" : "Paste your audio link…"}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
                        />
                    )}
                    <button
                        onClick={onSubmit}
                        disabled={submitting}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                        style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                    >
                        <Icon d="M5 13l4 4L19 7" className="w-3.5 h-3.5" />
                        {submitting ? "Submitting…" : type === 0 ? "Mark goal done" : "Submit goal"}
                    </button>
                    {goal.approval_type === 2 && (
                        <p className="mt-2 text-xs text-gray-400">An instructor will review your submission before the lesson is marked complete.</p>
                    )}
                </div>
            )}
        </div>
    );
}

// Compact group banner shown on the right of the course detail page — also
// reused in the feed right rail so both pages share one design.
export function GroupMiniBanner({ group, className = "h-full min-h-[180px]" }) {
    if (!group) return null;
    const isPrivate = group.access_type === 2;
    const pricing = group.pricing_type || "free";
    const isFree = pricing === "free";
    const chip = "inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm ring-1 ring-white/25";
    return (
        <div className={`relative ${className} overflow-hidden rounded-2xl border border-gray-200 bg-ink shadow-sm`}>
            {group.banner && <img src={group.banner} alt="" className="absolute inset-0 h-full w-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/20" />

            {/* name + description, then a single bottom row of pills:
                Public/Private · Free/Paid · members · posts */}
            <div className="absolute inset-x-0 bottom-0 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wide text-white/65">Community</p>
                <h3 className="mt-0.5 line-clamp-1 text-lg font-bold text-white drop-shadow">{group.name}</h3>
                {group.description && <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-white/80">{group.description}</p>}
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-surface/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-700 shadow-sm backdrop-blur">
                        {isPrivate
                            ? <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1a5 5 0 00-5 5v3H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2v-9a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 8H9V6a3 3 0 016 0v3z" /></svg>
                            : <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 000 18M12 3a14 14 0 010 18" /></svg>}
                        {isPrivate ? "Private" : "Public"}
                    </span>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm ${isFree ? "bg-green-600" : "bg-red-600"}`}>
                        {pricing === "free_trial" ? "Trial" : isFree ? "Free" : "Paid"}
                    </span>
                    <span className={chip}>
                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z" /></svg>
                        {group.member_count ?? 0} {group.member_count === 1 ? "member" : "members"}
                    </span>
                    <span className={chip}>
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h4M9 8h6M6 3h12a1 1 0 011 1v16a1 1 0 01-1.5.87L15 19.5l-2.5 1.4a1 1 0 01-1 0L9 19.5l-2.5 1.37A1 1 0 015 20V4a1 1 0 011-1z" /></svg>
                        {group.post_count ?? 0} {group.post_count === 1 ? "post" : "posts"}
                    </span>
                </div>
            </div>
        </div>
    );
}

// Community (membership) version of the mini banner — image + name + description.
// Shown on community-wide pages (e.g. Courses) where a specific group's card
// would be out of place.
// Default membership banner used when none is configured (matches the admin
// "Create Membership" form's default image).
const DEFAULT_MEMBERSHIP_BANNER = "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80";

/**
 * True unless the owner explicitly switched the membership card off in the
 * admin (Brand settings → Membership card). The flag is missing on older API
 * builds and on memberships saved before the toggle shipped, and those all show
 * the card, so only an explicit 0 hides it.
 */
export const showMembershipCard = (community) =>
    !!community && Number(community.membership_card_show ?? 1) !== 0;

export function CommunityMiniBanner({ community, totalMembers, totalCourses, totalGroups, className = "h-full min-h-[180px]" }) {
    // Card off => nothing renders, banner artwork included. The image is the
    // card's own background, so there is no "hide the card, keep the banner"
    // state to fall back to.
    if (!showMembershipCard(community)) return null;
    const chip = "inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm ring-1 ring-white/25";
    /*
     * "Show banner image" OFF means no banner image — not a different one.
     *
     * This used to fall back: toggle off, and the artwork became the older
     * Create-Membership image, then the platform default. So the card always
     * drew something and the toggle appeared to do nothing, which is the bug
     * QA filed (19) — the image only disappeared when the whole card was
     * switched off, and that is the card going, not the toggle working.
     *
     * Off now renders the card with no artwork at all: the gradient and the
     * name, description and stats that sit on it.
     */
    const bannerOn = Number(community.dashboard_banner_show ?? 0) === 1;
    const image = bannerOn
        ? community.dashboard_banner || community.membership_banner || DEFAULT_MEMBERSHIP_BANNER
        : "";
    const name = community.membership_name || community.name;
    const description = community.membership_description || "";
    // Admin Settings → Features → "Members Count Display". Missing (older API,
    // untouched membership) shows the pill; only an explicit 0 hides it. Dropped
    // here rather than at each call site, so every page's card obeys it.
    if (Number(community.members_count_show ?? 1) === 0) totalMembers = undefined;
    return (
        <div className={`relative ${className} overflow-hidden rounded-2xl border border-gray-200 bg-ink shadow-sm`}>
            {image ? (
                <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => (e.currentTarget.src = DEFAULT_MEMBERSHIP_BANNER)} />
            ) : null}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/20" />

            {/* name + description + stats */}
            <div className="absolute inset-x-0 bottom-0 p-4">
                <Tooltip label={name}>
                    <h3 className="line-clamp-1 text-lg font-bold text-white drop-shadow">{name}</h3>
                </Tooltip>
                {/* Clamped to two lines against the artwork; the tooltip is how the
                    rest of a long description stays reachable. */}
                {description && (
                    <Tooltip label={description}>
                        <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-white/80">{description}</p>
                    </Tooltip>
                )}
                {(typeof totalMembers === "number" || typeof totalCourses === "number" || typeof totalGroups === "number") && (
                    <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                        {typeof totalMembers === "number" && (
                            <span className={chip}>
                                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z" /></svg>
                                {totalMembers} {totalMembers === 1 ? "member" : "members"}
                            </span>
                        )}
                        {typeof totalCourses === "number" && (
                            <span className={chip}>
                                <Icon d="M12 6.5C10.5 5.5 8.3 5 6 5a8 8 0 00-2 .26v12A8 8 0 016 17c2.3 0 4.5.5 6 1.5m0-12c1.5-1 3.7-1.5 6-1.5a8 8 0 012 .26v12A8 8 0 0018 17c-2.3 0-4.5.5-6 1.5m0-12V18.5" className="h-3 w-3" />
                                {totalCourses} {totalCourses === 1 ? "course" : "courses"}
                            </span>
                        )}
                        {typeof totalGroups === "number" && (
                            <span className={chip}>
                                <Icon d="M4 6h6v6H4V6zm10 0h6v6h-6V6zM4 16h6v4H4v-4zm10 0h6v4h-6v-4z" className="h-3 w-3" />
                                {totalGroups} {totalGroups === 1 ? "community" : "communities"}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// Platform default hero — mirrors the admin's Brand settings default so the
// banner always resolves even before the API responds.
const DEFAULT_DASHBOARD_BANNER = "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80";

/**
 * Modal that plays an intro video — the brand one (Brand → Options → "Intro
 * video") and a module's own.
 *
 * Wears the same sheet the reward modal does: a soft wash of the membership's
 * own colour behind dark type, an icon chip, a floating close button and a
 * footer. It used to be a black box with a grey strip on top — the one popup in
 * the app that ignored the brand entirely, and it read as a debug frame rather
 * than something the creator had designed.
 *
 * Reuses the lesson player's embed normalizer, so a YouTube / Vimeo / file URL
 * or a raw embed snippet all work. Portal-rendered so no overflow-hidden
 * ancestor clips it.
 */
function IntroVideoModal({ url, embedHtml = null, title, subtitle = "", primary = "#0274F7", onClose }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    const src = url ? embedUrl(url) : "";
    // A YouTube intro goes through the lesson player, not a bare iframe: that is
    // where the masks live, so an intro video does not put the channel's title,
    // avatar, share and logo on screen when the lesson beside it never does.
    const isYouTube = !!src && /youtube(-nocookie)?\.com\/embed\//i.test(src);

    const player = isYouTube ? (
        <YouTubePlayer src={src} title={title || "Intro video"} />
    ) : embedHtml ? (
        /* An author-pasted embed (generic/HTML5, customEmbed 1) — the same
           treatment a generic lesson gets, markup rendered as authored because
           rewriting it is not ours to do. */
        <div
            className="relative aspect-video w-full overflow-hidden bg-black [&>iframe]:absolute [&>iframe]:inset-0 [&>iframe]:h-full [&>iframe]:w-full"
            dangerouslySetInnerHTML={{ __html: normalizeEmbed(embedHtml) }}
        />
    ) : (
        <div className="relative aspect-video w-full bg-black">
            <iframe
                title={title || "Intro video"}
                src={src}
                className="absolute inset-0 h-full w-full"
                sandbox={PLAYER_SANDBOX}
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        </div>
    );

    return createPortal(
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center p-0 sm:p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />

            {/* Full-bleed on a phone, a floating sheet from `sm` up — the same
                shape the reward modal uses. max-w-3xl because a 16:9 player in a
                narrow sheet is all letterbox. */}
            <div className="relative flex h-full w-full flex-col overflow-hidden bg-surface shadow-2xl sm:h-auto sm:max-h-[92vh] sm:max-w-3xl sm:rounded-3xl sm:ring-1 sm:ring-black/5">
                {/* Header: a wash of the membership colour, dark type on top. */}
                <div
                    className="relative shrink-0 border-b border-gray-100 px-5 py-4 sm:px-6"
                    style={{ background: `linear-gradient(160deg, ${primary}1A 0%, ${primary}08 45%, rgb(var(--c-surface)) 100%)` }}
                >
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="absolute right-3.5 top-3.5 grid h-8 w-8 place-items-center rounded-full bg-surface/70 text-gray-400 shadow-sm ring-1 ring-black/5 transition hover:bg-surface hover:text-gray-600"
                    >
                        <Icon d="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                    </button>

                    <div className="flex items-center gap-3.5 pr-10">
                        <span
                            className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-surface shadow-sm ring-1 ring-black/5"
                            style={{ color: primary }}
                        >
                            <Icon d={VIDEO_CAMERA_PATH} className="h-5 w-5" />
                        </span>
                        <div className="min-w-0">
                            <span
                                className="block text-[11px] font-bold uppercase tracking-[0.12em]"
                                style={{ color: primary }}
                            >
                                {subtitle || "Intro video"}
                            </span>
                            <h3 className="mt-0.5 line-clamp-2 text-[15px] font-bold leading-snug text-gray-900 sm:text-base">
                                {title || "Welcome"}
                            </h3>
                        </div>
                    </div>
                </div>

                {/* Body: the player in its own rounded frame, so the black of the
                    video reads as a piece of the sheet rather than the sheet. */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-5">
                    <div className="overflow-hidden rounded-2xl bg-black shadow-sm ring-1 ring-black/10">
                        {player}
                    </div>
                </div>

                <div className="flex shrink-0 items-center justify-end border-t border-gray-100 bg-gray-50/60 px-5 py-3.5 sm:px-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl px-4 py-2 text-sm font-semibold text-gray-500 transition hover:text-gray-700"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

/**
 * Wide hero banner across the top of the Courses dashboard.
 *
 * Sourced from the admin's Brand settings ("Dashboard Banner"): the owner's
 * uploaded artwork when they set one (`dashboard_banner`), otherwise the
 * platform default — so a hero always shows above the courses. `overlay` is the
 * admin's rgba tint, drawn over the image so the membership name stays readable.
 *
 * Also hosts the brand intro video (Brand → Options → "Intro video"): when set,
 * a "Watch intro" button sits on the banner, and the video auto-plays once on
 * the learner's first visit (remembered per membership in localStorage).
 */
export function DashboardBanner({ community, token, memberId, primary = "#0274F7" }) {
    const introUrl = Number(community?.intro_video) === 1
        ? String(community?.intro_video_url || "").trim()
        : "";
    const introSeenKey = `introSeen:${token}:${memberId || "guest"}`;
    const [introOpen, setIntroOpen] = useState(false);
    // Auto-open once, on first visit only.
    useEffect(() => {
        if (!introUrl) return;
        let seen = false;
        try { seen = localStorage.getItem(introSeenKey) === "1"; } catch { /* ignore */ }
        if (!seen) setIntroOpen(true);
    }, [introUrl, introSeenKey]);
    const closeIntro = () => {
        setIntroOpen(false);
        try { localStorage.setItem(introSeenKey, "1"); } catch { /* ignore */ }
    };

    if (!community) return null;
    const image = community.dashboard_banner || DEFAULT_DASHBOARD_BANNER;
    const overlay = community.dashboard_banner_overlay || "rgba(0,0,0,0.5)";
    const name = community.membership_name || community.name || "";
    return (
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-ink shadow-sm">
            <img
                src={image}
                alt=""
                className="h-36 w-full object-cover sm:h-48 lg:h-56"
                onError={(e) => { e.currentTarget.src = DEFAULT_DASHBOARD_BANNER; }}
            />
            {/* Admin-configured tint, plus a bottom gradient so the title reads
                cleanly regardless of the artwork. */}
            <div className="pointer-events-none absolute inset-0" style={{ background: overlay }} />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            {name && (
                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                    <Tooltip label={name}>
                        <h2 className="line-clamp-1 text-xl font-bold text-white drop-shadow sm:text-2xl">{name}</h2>
                    </Tooltip>
                </div>
            )}
            {/* Watch-intro replay button — only when a video is configured. */}
            {introUrl && (
                <button
                    type="button"
                    onClick={() => setIntroOpen(true)}
                    className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-surface/95 px-3.5 py-1.5 text-xs font-bold shadow-sm backdrop-blur transition hover:bg-surface"
                    style={{ color: primary }}
                >
                    <Icon d="M8 5v14l11-7z" className="h-3.5 w-3.5" /> Watch intro
                </button>
            )}
            {introOpen && introUrl && (
                <IntroVideoModal
                    url={introUrl}
                    title={name}
                    subtitle="Welcome video"
                    primary={primary}
                    onClose={closeIntro}
                />
            )}
        </div>
    );
}

// Confetti colors — brand primary first, then a festive spread.
const CELEBRATION_COLORS = ["#f59e0b", "#22c55e", "#3b82f6", "#ec4899", "#8b5cf6", "#ef4444", "#14b8a6"];

/**
 * Full-screen celebration shown when a learner finishes a course, if the owner
 * enabled it (Brand → Options → "Celebration popup on completion"). Pure CSS
 * confetti — no library. Rendered through a portal so no overflow-hidden
 * ancestor clips it. Auto-dismisses; also closable.
 */
function CelebrationOverlay({ primary, title, onClose }) {
    useEffect(() => {
        const timer = setTimeout(onClose, 6000);
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => { clearTimeout(timer); window.removeEventListener("keydown", onKey); };
    }, [onClose]);

    // Deterministic-enough spread; index-varied so pieces don't stack.
    const pieces = Array.from({ length: 46 }, (_, i) => ({
        left: (i * 2.17 + (i % 5) * 3.3) % 100,
        delay: (i % 11) * 0.18,
        duration: 2.6 + (i % 7) * 0.35,
        color: CELEBRATION_COLORS[i % CELEBRATION_COLORS.length],
        size: 7 + (i % 4) * 2,
        rotate: (i * 47) % 360,
    }));

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <style>{`
                @keyframes cm-confetti-fall {
                    0% { transform: translateY(-12vh) rotate(0deg); opacity: 0; }
                    8% { opacity: 1; }
                    100% { transform: translateY(102vh) rotate(720deg); opacity: 1; }
                }
                @keyframes cm-pop-in {
                    0% { transform: scale(.86); opacity: 0; }
                    60% { transform: scale(1.03); }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>

            {/* backdrop */}
            <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} />

            {/* confetti layer */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                {pieces.map((p, i) => (
                    <span
                        key={i}
                        style={{
                            position: "absolute",
                            top: 0,
                            left: `${p.left}%`,
                            width: p.size,
                            height: p.size * 1.6,
                            background: p.color,
                            borderRadius: 2,
                            transform: `rotate(${p.rotate}deg)`,
                            animation: `cm-confetti-fall ${p.duration}s linear ${p.delay}s infinite`,
                        }}
                    />
                ))}
            </div>

            {/* card */}
            <div
                className="relative w-full max-w-sm overflow-hidden rounded-3xl bg-surface p-8 text-center shadow-2xl ring-1 ring-black/5"
                style={{ animation: "cm-pop-in 380ms cubic-bezier(.16,1,.3,1)" }}
            >
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full" style={{ background: `${primary}14` }}>
                    <span className="flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg" style={{ background: primary }}>
                        {/* trophy */}
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7">
                            <path d="M8 21h8m-4-4v4M7 4h10v4a5 5 0 01-10 0V4zM7 6H5a1 1 0 00-1 1 3 3 0 003 3M17 6h2a1 1 0 011 1 3 3 0 01-3 3" />
                        </svg>
                    </span>
                </div>
                <h3 className="mt-5 text-2xl font-extrabold text-gray-900">Congratulations! 🎉</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-500">
                    You completed{title ? " " : ""}
                    {title && <span className="font-semibold text-gray-700">{title}</span>}. Fantastic work — keep the momentum going!
                </p>
                <button
                    onClick={onClose}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:brightness-110"
                    style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                >
                    Awesome!
                </button>
            </div>
        </div>,
        document.body
    );
}

/* ---------- FlexiFunnels layout: underline tabs (About Lesson · Discussions ·
   Notes · Resources · Goals). Reuses the same tab panels as the Classic
   LessonExtras; only the chrome differs (matches the legacy course player). */
/* ---------- Scroll-aware floating mini-player ---------- */
/* When the lesson video scrolls out of view, the player detaches into a premium
   draggable floating card in the corner (keep watching while reading / taking
   notes) and re-docks when scrolled back — a nicer take on the old app's
   floating video. The media element is NEVER remounted (only the wrapper's
   position toggles), so playback never restarts. Works for every template and
   every provider (file / HLS / YouTube / Vimeo / Gumlet / DRM).
   `enabled` gates it to actual video lessons (audio / text / locked don't float). */
function FloatingVideo({ enabled, lessonToken, primary = "#16a34a", children }) {
    const dockRef = useRef(null); // in-flow placeholder we observe for visibility
    const cardRef = useRef(null); // the floating card itself (measured for clamping)
    const [floating, setFloating] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [boxH, setBoxH] = useState(0);
    const [dragging, setDragging] = useState(false);
    const debounce = useRef(null);
    const drag = useRef(null); // {id, dx, dy} while a pointer is held on the handle

    const defaultPos = () => {
        if (typeof window === "undefined") return { x: 24, y: 24 };
        const w = window.innerWidth;
        const h = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
        const cardW = Math.min(360, w * 0.88);
        return { x: Math.max(12, w - cardW - 24), y: Math.max(12, h - (cardW * 9) / 16 - 100) };
    };
    const [pos, setPos] = useState(defaultPos);

    // Reset when the lesson changes (or floating is turned off).
    useEffect(() => { setFloating(false); setDismissed(false); }, [lessonToken, enabled]);

    // Observe the dock: float when it scrolls out of view, re-dock when back.
    useEffect(() => {
        const node = dockRef.current;
        if (!enabled || !node || typeof IntersectionObserver === "undefined") return undefined;
        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver((es) => { const h = es[0]?.contentRect?.height ?? 0; if (h > 0) setBoxH(h); });
            ro.observe(node);
        } else {
            setBoxH(node.getBoundingClientRect().height);
        }
        const minVisible = window.innerWidth <= 1000 ? 0.15 : 0.45;
        const io = new IntersectionObserver((entries) => {
            const e = entries[0]; if (!e) return;
            clearTimeout(debounce.current);
            debounce.current = setTimeout(() => setFloating(e.intersectionRatio < minVisible), 350);
        }, { threshold: [0, minVisible, 0.6, 1] });
        io.observe(node);
        return () => { io.disconnect(); if (ro) ro.disconnect(); clearTimeout(debounce.current); };
    }, [enabled, lessonToken]);

    // Re-enable floating for the next scroll-away once the video re-docks.
    useEffect(() => { if (!floating) setDismissed(false); }, [floating]);

    const isFloat = !!enabled && floating && !dismissed;

    // Keep the whole card on screen — the old clamp used fixed 120/80px margins,
    // so most of it could be dragged off the right/bottom edge and the controls
    // with it.
    const clamp = useCallback((x, y) => {
        const r = cardRef.current?.getBoundingClientRect();
        const w = r?.width || 320;
        const h = r?.height || 180;
        const vw = window.innerWidth;
        const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
        return {
            x: Math.min(Math.max(8, x), Math.max(8, vw - w - 8)),
            y: Math.min(Math.max(8, y), Math.max(8, vh - h - 8)),
        };
    }, []);

    /* Drag. Pointer events (not mouse+touch pairs) with setPointerCapture, which
       is what makes this work at all: the card is mostly a cross-origin IFRAME,
       and once the cursor crosses into it the iframe swallows every mousemove —
       the old document-level listeners simply stopped firing a few pixels in, so
       the card froze. Capture re-routes the stream to the handle regardless of
       what's underneath, and the shield below covers the case where a browser
       refuses the capture. */
    const onHandleDown = (e) => {
        if (!isFloat || (e.button != null && e.button !== 0)) return;
        e.preventDefault();
        drag.current = { id: e.pointerId, dx: e.clientX - pos.x, dy: e.clientY - pos.y };
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
        setDragging(true);
    };
    const onHandleMove = (e) => {
        const d = drag.current;
        if (!d || d.id !== e.pointerId) return;
        if (e.cancelable) e.preventDefault();
        setPos(clamp(e.clientX - d.dx, e.clientY - d.dy));
    };
    const endDrag = (e) => {
        const d = drag.current;
        if (!d || d.id !== e.pointerId) return;
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        drag.current = null;
        setDragging(false);
    };

    // Re-seat the card whenever it starts floating or the viewport changes
    // (rotation, mobile URL-bar collapse) so it can never end up off screen.
    useEffect(() => {
        if (!isFloat) return undefined;
        const reseat = () => setPos((p) => clamp(p.x, p.y));
        reseat();
        window.addEventListener("resize", reseat);
        window.addEventListener("orientationchange", reseat);
        return () => {
            window.removeEventListener("resize", reseat);
            window.removeEventListener("orientationchange", reseat);
        };
    }, [isFloat, clamp]);

    // The media (children) always lives inside ONE wrapper node whose class/style
    // merely toggles — never conditionally re-parented — so it isn't remounted and
    // playback continues seamlessly while it floats.
    return (
        <div ref={dockRef} style={isFloat && boxH > 0 ? { height: boxH } : undefined}>
            {/* Belt-and-braces for the iframe problem: while a drag is live this
                covers the page so no frame can steal the pointer stream. */}
            {dragging ? <div className="fixed inset-0 z-[69] cursor-grabbing" style={{ touchAction: "none" }} /> : null}
            <div
                ref={cardRef}
                className={
                    isFloat
                        ? "group/float fixed z-[70] w-[min(360px,88vw)] overflow-hidden rounded-2xl bg-black shadow-[0_24px_70px_-15px_rgba(0,0,0,0.65)] ring-1 ring-white/10 transition-shadow duration-200"
                        : "relative"
                }
                style={isFloat ? { left: pos.x, top: pos.y } : undefined}
            >
                {/* Drag handle + controls — always rendered (hidden when docked) so
                    toggling never shifts the media's position and remounts it.
                    z-[60] because the player it sits over puts its own click-catcher
                    at z-10 and its control bar at z-20 in THIS stacking context (the
                    player's wrapper is position:relative with z-index:auto, so it
                    doesn't contain them). At the old z-10 the click-catcher tied and
                    won on DOM order, leaving the handle and both buttons dead.
                    Under lg the bar stays visible: touch screens have no hover, so a
                    reveal-on-hover close button is unreachable on a phone. */}
                <div
                    className={
                        isFloat
                            ? "absolute inset-x-0 top-0 z-[60] flex cursor-grab touch-none select-none items-center justify-between gap-2 bg-gradient-to-b from-black/75 via-black/40 to-transparent px-3 py-2 opacity-100 transition-opacity duration-150 active:cursor-grabbing lg:opacity-0 lg:group-hover/float:opacity-100 lg:group-focus-within/float:opacity-100"
                            : "hidden"
                    }
                    onPointerDown={isFloat ? onHandleDown : undefined}
                    onPointerMove={isFloat ? onHandleMove : undefined}
                    onPointerUp={isFloat ? endDrag : undefined}
                    onPointerCancel={isFloat ? endDrag : undefined}
                >
                    <span className="flex items-center gap-1.5 text-[11px] font-bold text-white/90">
                        <span className="flex h-2 w-2 rounded-full" style={{ backgroundColor: primary, boxShadow: `0 0 8px ${primary}` }} />
                        Now playing
                    </span>
                    <span className="flex items-center gap-1">
                        <button
                            type="button"
                            aria-label="Back to lesson"
                            title="Back to lesson"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => dockRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
                            className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/30"
                        >
                            <Icon d="M4 14h6m0 0v6m0-6l-7 7M20 10h-6m0 0V4m0 6l7-7" className="h-3.5 w-3.5" />
                        </button>
                        <button
                            type="button"
                            aria-label="Close mini player"
                            title="Close"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => setDismissed(true)}
                            className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/30"
                        >
                            <Icon d="M6 18L18 6M6 6l12 12" className="h-3.5 w-3.5" />
                        </button>
                    </span>
                </div>
                {children}
            </div>
        </div>
    );
}

function FlexiLessonTabs({ active, ctx, primary, member, labels, discussionOn = true, reviews = null, goal = null, goalForm, setGoalForm, goalSubmitting, onSubmitGoal, onPointsChange, getTime = null, onSeek = null }) {
    const L = labels || ((k, fb) => fb);
    const tabs = [
        { key: "about", label: L("about_lesson_txt", "About Lesson") },
        ...(member && discussionOn ? [{ key: "discussion", label: L("discussion_txt", "Discussions") }] : []),
        ...(member ? [{ key: "notes", label: L("notes_txt", "Notes") }] : []),
        ...(member ? [{ key: "downloads", label: L("resources_txt", "Resources") }] : []),
        // Same rule as the other layout: the creator's switch, and at least one
        // approved review to put in it.
        ...(member && reviews?.enabled && reviews.reviews.length ? [{ key: "reviews", label: "Reviews" }] : []),
        ...(member && goal ? [{ key: "goals", label: L("goals_txt", "Goals") }] : []),
    ];
    // A notification / email link (&tab=discussion) opens straight on Discussion.
    const [tabSp] = useSearchParams();
    const [tab, setTab] = useState(() =>
        tabSp.get("tab") === "discussion" && tabs.some((t) => t.key === "discussion") ? "discussion" : goal ? "goals" : "about");
    // A notification clicked while this lesson is already open only changes the
    // URL, so follow it here too: switch to Discussion and remount it (linkKey)
    // so it reloads and opens the linked post.
    const linkKey = tabSp.get("tab") === "discussion" ? `${tabSp.get("post") || ""}:${tabSp.get("comment") || ""}` : "";
    useEffect(() => {
        if (linkKey && tabs.some((t) => t.key === "discussion")) setTab("discussion");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkKey]);
    return (
        <div className="mt-5">
            <div className="flex items-center gap-6 overflow-x-auto border-b border-gray-200 scrollbar-hide">
                {tabs.map((t) => {
                    const on = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`relative -mb-px whitespace-nowrap pb-2.5 text-sm font-bold transition ${on ? "" : "text-gray-400 hover:text-gray-600"}`}
                            style={on ? { color: primary } : undefined}
                        >
                            {t.label}
                            {on && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full" style={{ backgroundColor: primary }} />}
                        </button>
                    );
                })}
            </div>
            <div className="pt-4">
                {tab === "about" && active.detailed_description ? (
                    // Clamped: a text lesson is often a whole article, and at full
                    // height it buried the tab row above it under a page of scroll.
                    <ClampedRichText
                        key={active.lesson_token}
                        html={active.detailed_description}
                        primary={primary}
                        className="text-[15px] leading-relaxed text-gray-700"
                    />
                ) : null}
                {tab === "discussion" && <DiscussionTab key={linkKey} ctx={ctx} primary={primary} member={member} onPointsChange={onPointsChange} />}
                {tab === "notes" && <NotesTab ctx={ctx} primary={primary} getTime={getTime} onSeek={onSeek} />}
                {tab === "downloads" && <DownloadsTab ctx={ctx} primary={primary} />}
                {tab === "reviews" && <CourseReviewsTab data={reviews} primary={primary} />}
                {tab === "goals" && goal && (
                    <GoalPanel goal={goal} primary={primary} form={goalForm} setForm={setGoalForm} submitting={goalSubmitting} onSubmit={onSubmitGoal} />
                )}
            </div>
        </div>
    );
}

/**
 * Full-screen lock overlay for a course held behind one of the Course Access &
 * Visibility time gates.
 *
 * A toast on the card was easy to miss and left no way to read the creator's
 * upgrade copy. Members can now open the course; this covers the player with the
 * reason, the relevant date and the creator's CTA. It sits ABOVE the player
 * rather than replacing it, so the course identity (name, cover) stays visible
 * behind the blur and the page still feels like the course they tapped.
 */

function CourseLockOverlay({ course, primary, onBack }) {
    const reason = course?.lock_reason;
    const comingSoon = reason === "not_released";

    // The message IS the content here — the legacy member app showed a lock
    // illustration and one centred line ("You can access this course in 8 HOURS"),
    // and that reads better than a titled dialog. No heading competing with it.
    const message = lockMsg(reason, {
        release_at: course?.release_at,
        release_message: course?.release_message,
        access_expires_at: course?.access_expires_at,
        // Only the ended state carries the creator's copy — a countdown needs no
        // upsell attached to it.
        upgrade_text: comingSoon ? "" : course?.upgrade_text,
    });

    // Which state this is, in two words, for the status pill. `course_restricted`
    // is the same shape of problem as an ended window but arrives from a different
    // endpoint, and calling it "ended" would claim a window that may never have
    // existed — the API only tells us access is currently withheld.
    const statusLabel = comingSoon
        ? "Starts soon"
        : reason === "course_restricted"
          ? "Access restricted"
          : "Access ended";

    // Did the creator write the line above, or is it our generated fallback?
    // Matters because the fallbacks already state the date inline; repeating it
    // in the detail row below would read as a stutter.
    const creatorCopy = comingSoon ? course?.release_message : course?.upgrade_text;
    const gateDate = comingSoon ? course?.release_at : course?.access_expires_at;
    // Creator copy almost never names the date ("Upgrade to keep learning"), so
    // this row is where a member finds out WHEN — the single most useful fact on
    // the screen, and the one the previous layout dropped entirely.
    const showDate = !!gateDate && !!creatorCopy;

    // The creator's CTA. Its LABEL is what decides whether it renders — the
    // reference app showed the button on `buttontext` alone and sent a
    // link-less one back to the course list, so a half-configured CTA still
    // gives the member somewhere to go rather than silently disappearing.
    const ctaText = !comingSoon ? (course?.upgrade_button_text || "").trim() : "";
    const ctaLink = !comingSoon ? (course?.upgrade_button_link || "").trim() : "";
    const ctaStyle = {
        background: `linear-gradient(140deg, ${primary}, ${primary}c4)`,
        color: "var(--brand-btn-text, #fff)",
        boxShadow: `0 14px 28px -14px ${primary}`,
    };
    const ctaClass =
        "inline-flex w-full items-center justify-center gap-1.5 rounded-xl px-7 py-3.5 text-[14.5px] font-bold transition-all duration-200 hover:brightness-[1.06] active:scale-[.98] sm:w-auto sm:min-w-[168px]";
    const ctaArrow = (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h11" />
        </svg>
    );
    // Always the membership's brand colour. A scheduled "opens later" lock used to
    // get a fixed amber so it read as "come back" rather than "pay up", but a
    // hard-coded hue is off-theme on every membership that isn't orange — and the
    // "Starts soon" pill plus the "Opens on" date already carry that distinction
    // in words, which survives a brand palette in a way a colour cannot.
    const accent = primary;

    return (
        // Inline, not `fixed inset-0`: this fills the page's CONTENT area so the
        // app header and footer stay in place. A full-screen cover took the member
        // out of the app entirely — they lost the nav they needed to go anywhere
        // else, and the reference screen keeps its chrome too.
        //
        // The wrapper is deliberately unstyled now. It used to BE the card, which
        // meant one pale box stretched to the full content width with a narrow
        // column of text floating in the middle of it — the emptiness read as a
        // broken page rather than a designed one. The card below is capped instead,
        // and the page background frames it.
        <div className="flex min-h-[60vh] items-center justify-center px-4 py-10 sm:py-14">
            <div className="anim-modal relative w-full max-w-4xl overflow-hidden rounded-[26px] bg-surface shadow-[0_30px_70px_-38px_rgba(15,23,42,0.5)] ring-1 ring-black/5">
                {/* Accent rail. One brand-coloured line across the top is what
                    stops the card reading as a generic empty-state panel. */}
                <div
                    className="h-1.5 w-full"
                    style={{ background: `linear-gradient(90deg, ${accent}, ${accent}55)` }}
                />

                {/* Split layout: the illustration earns a stage of its own instead
                    of sitting on the same white as the text. Stacks on mobile. */}
                <div className="grid md:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
                    <div
                        className="relative flex items-center justify-center overflow-hidden px-8 py-9 md:py-12"
                        style={{ background: `linear-gradient(155deg, ${accent}16, ${accent}06 55%, rgb(var(--c-surface)))` }}
                    >
                        {/* Soft off-canvas glows — depth behind the padlock without
                            competing with the illustration's own aura. */}
                        <span
                            aria-hidden="true"
                            className="pointer-events-none absolute -left-12 -top-12 h-40 w-40 rounded-full"
                            style={{ background: `radial-gradient(circle, ${accent}2e, transparent 70%)` }}
                        />
                        <span
                            aria-hidden="true"
                            className="pointer-events-none absolute -bottom-16 -right-10 h-44 w-44 rounded-full"
                            style={{ background: `radial-gradient(circle, ${accent}24, transparent 70%)` }}
                        />
                        {/* Shared locked-content illustration, tinted by `accent` so the
                            padlock picks up the membership's brand colour. */}
                        <LockIllustration accent={accent} className="relative h-36 w-full max-w-[230px] sm:h-44" />
                    </div>

                    <div className="flex flex-col justify-center gap-5 border-t border-gray-100 px-6 py-8 sm:px-9 sm:py-10 md:border-l md:border-t-0">
                        <span
                            className="inline-flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em]"
                            style={{ background: `${accent}16`, color: accent }}
                        >
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
                            {statusLabel}
                        </span>

                        {/* Course identity. The cover is what makes this feel like
                            THEIR course rather than a system error page — the old
                            layout reduced it to a grey uppercase caption. */}
                        {course?.name ? (
                            <div className="flex items-center gap-3">
                                <img
                                    src={course.image || DEFAULT_COURSE_IMAGE}
                                    onError={(e) => { e.currentTarget.src = DEFAULT_COURSE_IMAGE; }}
                                    alt=""
                                    className="h-11 w-11 shrink-0 rounded-xl object-cover ring-1 ring-black/5"
                                />
                                <p className="min-w-0 truncate text-[15px] font-bold text-gray-900">{course.name}</p>
                            </div>
                        ) : null}

                        {/* The one line that matters, sized to be read from across the page. */}
                        <p className="text-balance text-[22px] font-bold leading-snug tracking-[-0.015em] text-gray-900 sm:text-[26px]">
                            {message}
                        </p>

                        {showDate ? (
                            <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50/80 px-4 py-3">
                                <span
                                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                                    style={{ background: `${accent}16`, color: accent }}
                                >
                                    <Icon d={CALENDAR_ICON} className="h-[18px] w-[18px]" />
                                </span>
                                <div className="min-w-0">
                                    <p className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-gray-400">
                                        {comingSoon ? "Opens on" : "Access ended on"}
                                    </p>
                                    <p className="truncate text-[14px] font-bold text-gray-800">{fmtDripDate(gateDate)}</p>
                                </div>
                            </div>
                        ) : null}

                        <div className="flex w-full flex-col gap-2.5 pt-1 sm:flex-row sm:items-center">
                            {ctaText && ctaLink ? (
                                <a href={ctaLink} target="_blank" rel="noopener noreferrer" className={ctaClass} style={ctaStyle}>
                                    {ctaText}
                                    {ctaArrow}
                                </a>
                            ) : ctaText ? (
                                <button type="button" onClick={onBack} className={ctaClass} style={ctaStyle}>
                                    {ctaText}
                                    {ctaArrow}
                                </button>
                            ) : null}

                            <button
                                type="button"
                                onClick={onBack}
                                className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-surface px-7 py-3.5 text-[14.5px] font-semibold text-gray-600 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 sm:w-auto"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5 5-5m7 10l-5-5 5-5" />
                                </svg>
                                Back to courses
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// `gradeOn` — Membership Grade feature flag, passed down from the catalogue so
// the course header hides its grade pill with the cards. Defaults true for any
// caller that doesn't pass it, matching the flag's default-on rule everywhere.
function CoursePlayer({ token, courseToken, primary, member, community, totalMembers, courseMeta, onBack, initialLesson, onLessonChange, onPointsChange, template = "classic", onInstructor = null, gradeOn = true, ratingOn = true }) {
    // Per-membership wording overrides (Brand → Localization).
    const labels = makeLabels(community?.labels);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null); // lesson object
    // Whole-course restriction, discovered from api/lesson-details (restrictCourse):
    // the purchase-relative access window has run out, or the release date hasn't
    // arrived. api/lessonlist doesn't apply either gate — its lessons still come
    // back show=1 — so the ONLY place this surfaces is the first lesson-details
    // call, exactly as in the reference member app. Holds that lock result.
    const [courseRestriction, setCourseRestriction] = useState(null);
    // Drop it when moving to another course; the next lesson-details call re-answers.
    useEffect(() => { setCourseRestriction(null); }, [courseToken]);

    /*
     * Per-course retargeting code — the highest-priority script source.
     *
     * A course that has its own code replaces the project's Advanced/General
     * script on its lesson pages; one that does not leaves the project script
     * running. The cleanup hands control back when the learner leaves the
     * course, which matters here because this is a single page — the project
     * script was injected once at login and stays in the DOM otherwise.
     */
    useEffect(() => {
        // Claim FIRST and synchronously: this holds the project script back
        // before either request starts. Without it, /customscript can resolve
        // first, inject the project pixel, and the pixel has then already fired
        // — removing the node afterwards does not un-fire it.
        claimCourseScript(courseToken);
        injectCourseScript({ courseToken, funnelId: member?.funnel_id });
        return () => injectCourseScript({ courseToken: null });
    }, [courseToken, member?.funnel_id]);
    // Keep the latest onLessonChange without re-triggering the effect below.
    const onLessonChangeRef = useRef(onLessonChange);
    onLessonChangeRef.current = onLessonChange;
    // Reflect the active lesson in the URL (?lesson=) so it survives refresh.
    useEffect(() => {
        if (active?.lesson_token) onLessonChangeRef.current?.(active.lesson_token);
    }, [active?.lesson_token]);
    const [collapsed, setCollapsed] = useState({}); // accordion: { [moduleIndex]: true }
    // The module whose intro video is playing, if any. One piece of state for both
    // curriculum layouts below — they render the same modules, just differently.
    const [introModule, setIntroModule] = useState(null);
    /*
     * One module open at a time.
     *
     * Expanding was independent per module, so a member working down a course
     * left every module they had touched standing open and the rail turned into
     * one long list where the module boundaries stopped meaning anything —
     * reaching module 4 meant scrolling past every lesson of 1, 2 and 3.
     * Opening one now shuts the rest; collapsing the open one leaves them all
     * shut, so "no module expanded" is still reachable.
     */
    const shutAllBut = (mi) => {
        const shut = {};
        (data?.modules || []).forEach((_, i) => { if (i !== mi) shut[i] = true; });
        return shut;
    };
    const openModule = (mi) => setCollapsed(shutAllBut(mi));
    const toggleModule = (mi) =>
        setCollapsed((c) => (c[mi] ? shutAllBut(mi) : { ...shutAllBut(mi), [mi]: true }));
    // Quiz modal: opens from the lesson's "Take Quiz" button. Passing the quiz does
    // NOT complete the lesson — it's an independent task; the member finishes the
    // lesson via Mark Complete (gated on quiz+goal).
    const [quizOpen, setQuizOpen] = useState(false);
    // Server-driven per-lesson quiz status so it SURVIVES a page refresh:
    // { [lesson_token]: { attempted, passed, locked, lockMsg } }. Sourced from the
    // quiz-list endpoint (attempted / quiz_passed / retake_attempt_status / attempt_msg).
    const [quizStatus, setQuizStatus] = useState({});
    const setLessonQuiz = (token, patch) =>
        setQuizStatus((s) => ({ ...s, [token]: { ...(s[token] || {}), ...patch } }));
    // On first load, only the first module is expanded; the rest start collapsed.
    // After that it follows the member: the chevron toggles, and tapping a
    // module's name opens it as well as loading its first lesson.
    const collapseInitRef = useRef(false);
    useEffect(() => {
        const mods = data?.modules;
        if (collapseInitRef.current || !mods?.length) return;
        collapseInitRef.current = true;
        const init = {};
        mods.forEach((_, i) => { if (i !== 0) init[i] = true; });
        setCollapsed(init);
    }, [data]);

    // lesson_token -> content from api/lesson-details. Only the lesson on screen
    // needs it, so it's fetched per lesson and kept for the life of the player
    // (revisiting a lesson, or a reload() after Mark complete, costs nothing).
    const detailCache = useRef(new Map());
    // The active file <video> element (direct-file lessons only) — powers native
    // Picture-in-Picture and timestamped notes in the FlexiFunnels layout. Stays
    // null for embed/iframe media (YouTube/Vimeo can't expose currentTime).
    const videoRef = useRef(null);
    // The active embed <iframe> (YouTube/Vimeo/Gumlet) + its player-API tracker,
    // so notes can capture the current video position for cross-origin players.
    // embedHtmlRef wraps a raw embed_html block (DRM VdoCipher / Gumlet), whose
    // inner iframe is queried and tracked the same way.
    const embedIframeRef = useRef(null);
    const embedHtmlRef = useRef(null);
    const embedTrackerRef = useRef(null);
    /* The saved positions, mirrored into a ref so the VdoCipher player can read
       them WHENEVER it is ready to seek. They arrive from their own request,
       which routinely finishes after a DRM player has started (licence + first
       segment), and a value captured at attach time would be an empty {}. */
    const positionsRef = useRef({});
    /* Embed health. A FlexiFunnels-hosted video plays inside a cross-origin
       iframe, so whether it WORKS is decided entirely inside that frame — and
       there are browsers where it does not: third-party storage partitioned
       away (Safari ITP, Firefox Total Cookie Protection), EME unavailable, or
       the embed blocked by an extension. The frame still loads; the player just
       never starts, and the member stares at a black rectangle with no idea
       what to do. `embedStalled` flips on when the provider's API has not
       answered within EMBED_HEALTH_MS, which is the only evidence obtainable
       from outside the frame, and puts a way out on screen.
       `embedReload` remounts the iframe for the Reload action. */
    const [embedStalled, setEmbedStalled] = useState(false);
    const [embedReload, setEmbedReload] = useState(0);
    // (Re)attach the embed time-tracker whenever the active lesson's iframe mounts
    // (YouTube via its IFrame API, Vimeo/Gumlet via PlayerJs — see videoTime.js).
    // Rebuilt per lesson; destroyed on change/unmount. Files use videoRef instead.
    useEffect(() => {
        embedTrackerRef.current?.destroy?.();
        embedTrackerRef.current = null;
        setEmbedStalled(false);
        let healthTimer = null;
        const a = active;
        if (a && !a.locked && a.hydrated && a.lesson_type === 3) {
            // A DRM/custom embed_html block: track the iframe inside it (VdoCipher
            // or Gumlet). Otherwise a plain provider iframe (YouTube/Vimeo/Gumlet),
            // but not a file/HLS <video> which exposes currentTime via videoRef.
            let iframeEl = null;
            if (a.embed_html && embedHtmlRef.current) {
                iframeEl = embedHtmlRef.current.querySelector("iframe");
            } else if (!a.embed_html && a.media_url && !isFile(a.media_url) && !isHls(a.media_url)) {
                iframeEl = embedIframeRef.current;
            }
            if (iframeEl) {
                // "judged" covers BOTH verdicts: a live player, and an SDK that
                // never loaded. Only the second kind of silence — SDK present,
                // frame not answering — is evidence the embed is broken.
                let judged = false;
                const verdicts = {
                    onReady: () => { judged = true; setEmbedStalled(false); },
                    onUnknown: () => { judged = true; },
                };
                /*
                 * VdoCipher (`flexi`) gets its own owner. It is the one provider
                 * whose API exposes a real media element, so it can do what the
                 * generic time-tracker cannot: RESUME from the member's saved
                 * position and write it back. Both talk to the same v2 API and
                 * VdoPlayer.getInstance() is per-iframe, so exactly one of them
                 * may attach — and the handles are the same shape, which keeps
                 * note timestamps and seek-from-a-note working either way.
                 */
                embedTrackerRef.current = isVdoCipherSrc(iframeEl.getAttribute("src"))
                    ? attachVdoCipher(iframeEl, {
                        ...verdicts,
                        startAt: () => positionsRef.current[String(a.lesson_token)] || 0,
                        onSave: (pos, dur) => persistPosition(a, pos, dur),
                    })
                    : makeEmbedTimeTracker(iframeEl, verdicts);
                if (WATCHABLE_EMBED.test(iframeEl.getAttribute("src") || "")) {
                    healthTimer = setTimeout(() => { if (!judged) setEmbedStalled(true); }, EMBED_HEALTH_MS);
                }
            }
        }
        return () => {
            if (healthTimer) clearTimeout(healthTimer);
            embedTrackerRef.current?.destroy?.();
            embedTrackerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active?.lesson_token, active?.hydrated, embedReload]);
    const hydrate = (lesson) => {
        const det = lesson && detailCache.current.get(lesson.lesson_token);
        return det ? { ...lesson, ...det, hydrated: true } : lesson;
    };

    /* Below lg the curriculum and the viewer are STACKED, not side by side — so a
       tap in the lesson list changed a pane that was entirely off screen. On a
       phone or tablet the page just sat there and the member had to work out for
       themselves that the answer was further down. Every route into a lesson now
       brings the viewer to the top of the screen.

       The sticky header's height is measured, never assumed: it differs per
       template and grows by a whole row where the section nav sits under the bar,
       and getting it wrong hides the lesson title behind the bar — the one line
       that tells the member the tap worked. */
    const viewerRef = useRef(null);
    const revealViewer = useCallback(() => {
        if (typeof window === "undefined") return;
        // From lg up the two columns are side by side and already both visible;
        // scrolling there would move the page for no reason.
        if (window.matchMedia("(min-width: 1024px)").matches) return;
        // Two frames, because the viewer may be swapping the "Select a lesson"
        // placeholder for the real panel — measure only once React has committed.
        requestAnimationFrame(() => requestAnimationFrame(() => {
            const el = viewerRef.current;
            if (!el) return;
            const header = document.querySelector("header");
            const stuck = header && window.getComputedStyle(header).position === "sticky"
                ? header.offsetHeight
                : 0;
            const top = el.getBoundingClientRect().top + window.scrollY - stuck - 8;
            window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        }));
    }, []);

    /* The single way a lesson is opened: the curriculum list, a module header's
       "start here", and the prev/next pair at the foot of the viewer all come
       through here, so none of them can forget the scroll. */
    const openLesson = (lesson) => {
        setActive(hydrate(lesson));
        revealViewer();
    };

    // `silent` refetches in the background: no full-page loader, no error toast.
    // Used by the visibility effect below, where the member is returning to a tab
    // they left open and blanking the player to a spinner would read as a fault.
    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = { data: await fetchCourseDetail({
                funnelId: member?.funnel_id,
                memberId: member?.community_member_id,
                jwt: member?.member_token,
                courseToken,
            }) };
            if (res.data?.error === 0) {
                setData(res.data);
                const all = res.data.modules.flatMap((m) => m.lessons);
                // Keep the same lesson selected; on first load honour the lesson
                // from the URL (?lesson=), else default to the first lesson.
                // Re-apply cached content — reload() returns bare stubs, and
                // without this the open lesson would blank out and refetch.
                setActive((prev) => {
                    const want = prev?.lesson_token || initialLesson;
                    return hydrate((want && all.find((l) => l.lesson_token === want)) || all[0] || null);
                });
            }
        } catch {
            if (!silent) toast.error("Could not load course.");
        } finally {
            if (!silent) setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, courseToken, member?.auth_token, member?.community_member_id]);
    useEffect(() => { load(); }, [load]);

    /* Returning from an upgrade purchase.
       The lock screen's CTA opens the creator's sales page in a NEW TAB. The
       member buys there and switches back to this one — but load() is keyed on
       the member and the course, neither of which changed, so the curriculum on
       screen is still the pre-purchase one and the module they just paid for is
       still padlocked. Same symptom as the server-side bug, a different cause,
       and it would survive that fix.

       So refetch when the tab becomes visible again — but ONLY while something
       here is upgrade-locked, since that is the only state a purchase can
       change. Every other course keeps exactly the load-once behaviour it has
       today, and the refetch is silent, so nothing flashes. */
    const hasUpgradeLock = (data?.modules || []).some(
        (m) => m.lock_reason === "upgrade_module"
            || (m.lessons || []).some((l) => l.lock_reason === "upgrade")
    );
    useEffect(() => {
        if (!hasUpgradeLock || typeof document === "undefined") return undefined;
        const onVisible = () => {
            if (document.visibilityState === "visible") load(true);
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [hasUpgradeLock, load]);

    // Load the active lesson's content on demand.
    useEffect(() => {
        const l = active;
        if (!l || l.locked || l.hydrated) return undefined;
        let on = true;
        (async () => {
            try {
                const det = await fetchLessonDetail({
                    funnelId: member?.funnel_id,
                    memberId: member?.community_member_id,
                    jwt: member?.member_token,
                    courseToken,
                    lessonToken: l.lesson_token,
                    title: l.title,
                });
                if (!on) return;
                // The course itself is shut — swap the whole player for the
                // upgrade screen rather than locking this one lesson.
                if (det?.course_locked) setCourseRestriction(det);
                // Cache even an empty result, so a lesson with no content doesn't
                // refetch every time it's opened.
                detailCache.current.set(l.lesson_token, det || {});
                setActive((a) => (a && a.lesson_token === l.lesson_token ? { ...a, ...(det || {}), hydrated: true } : a));
            } catch {
                if (on) setActive((a) => (a && a.lesson_token === l.lesson_token ? { ...a, hydrated: true } : a));
            }
        })();
        return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active?.lesson_token, active?.hydrated, courseToken]);

    // Prefetch the next unlocked lesson so stepping forward is instant. Delayed
    // so it never competes with the lesson the member is actually waiting on.
    useEffect(() => {
        if (!data || !active?.hydrated) return undefined;
        const all = data.modules.flatMap((m) => m.lessons);
        const i = all.findIndex((l) => l.lesson_token === active.lesson_token);
        const next = all.slice(i + 1).find((l) => !l.locked);
        if (!next || detailCache.current.has(next.lesson_token)) return undefined;
        let on = true;
        const t = setTimeout(() => {
            fetchLessonDetail({
                funnelId: member?.funnel_id,
                memberId: member?.community_member_id,
                jwt: member?.member_token,
                courseToken,
                lessonToken: next.lesson_token,
                title: next.title,
            })
                .then((det) => { if (on) detailCache.current.set(next.lesson_token, det || {}); })
                .catch(() => { /* prefetch is best-effort */ });
        }, 500);
        return () => { on = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active?.lesson_token, active?.hydrated, data, courseToken]);

    const toggleComplete = async (lesson) => {
        if (!member) return toast.info("Sign in to track your progress.");
        const next = lesson.completed ? 0 : 1;
        try {
            const res = await markLessonComplete({
                funnelId: member?.funnel_id,
                memberId: member?.community_member_id,
                jwt: member?.member_token,
                courseToken,
                lessonToken: lesson.lesson_token,
                completed: next,
            });
            // Gated: the lesson has a quiz/goal that isn't done yet. Tell the member
            // what's left and leave the lesson incomplete.
            if (res && res.ok === false) {
                toast.info(res.message || "Finish this lesson's quiz and goal first.");
                return;
            }
            await load();
            setActive((a) => (a && a.lesson_token === lesson.lesson_token ? { ...a, completed: !!next } : a));
            // Only a completion earns points; un-completing silently removes them.
            if (next) toastPoints(res?.points_awarded, "Lesson complete.");
            // Completing/undoing a lesson changes gamification points — refresh the
            // standing so the points card is current when the learner returns.
            onPointsChange?.();
        } catch {
            toast.error("Could not update progress.");
        }
    };

    // Goal submission (text / link / action) — gates lesson completion.
    const [goalForm, setGoalForm] = useState({ text: "", url: "" });
    const [submitting, setSubmitting] = useState(false);
    const submitGoal = async (lesson) => {
        if (!member) return toast.info("Sign in to submit your goal.");
        setSubmitting(true);
        try {
            const res = await submitLessonGoal({
                funnelId: member?.funnel_id,
                memberId: member?.community_member_id,
                jwt: member?.member_token,
                courseToken,
                lessonToken: lesson.lesson_token,
                text: goalForm.text,
                url: goalForm.url,
            });
            if (res?.error === 0) {
                toast.success(res.status === 1 ? "Goal approved — lesson complete! 🎉" : "Submitted for review.");
                const nextGoal = {
                    my_status: Number(res.status) === 1 ? 1 : 0,
                    my_text: goalForm.text || null,
                    my_url: goalForm.url || null,
                };

                // Flip the panel to its submitted state NOW. load() below refetches
                // the course, but the lesson DETAIL — which carries the goal — is
                // served from detailCache, so without the two updates here the
                // panel kept showing the submit button until a hard refresh
                // cleared that cache.
                setActive((a) =>
                    a && a.lesson_token === lesson.lesson_token && a.goal
                        ? { ...a, goal: { ...a.goal, ...nextGoal } }
                        : a,
                );
                // hydrate() spreads the cached detail OVER the freshly loaded row,
                // so the cache has to carry the new status too — otherwise the
                // next load() would put the stale goal straight back.
                const cached = detailCache.current.get(lesson.lesson_token);
                if (cached?.goal) {
                    detailCache.current.set(lesson.lesson_token, {
                        ...cached,
                        goal: { ...cached.goal, ...nextGoal },
                    });
                }

                setGoalForm({ text: "", url: "" });
                await load();
                // An auto-approved goal completes the lesson and awards points.
                if (res.status === 1) onPointsChange?.();
            } else {
                toast.error(res?.msg || "Could not submit your goal.");
            }
        } catch (e) {
            toast.error(e.response?.data?.msg || "Could not submit your goal.");
        } finally {
            setSubmitting(false);
        }
    };

    // Per-lesson request context shared by the notes / quiz / discussion / etc. tabs.
    const lessonCtx = (lesson) => ({
        funnelId: member?.funnel_id,
        memberId: member?.community_member_id,
        jwt: member?.member_token,
        courseToken,
        lessonToken: lesson?.lesson_token,
    });

    /*
     * Approved reviews for this course — the Reviews tab in LessonExtras.
     *
     * Fetched once per course, not per lesson: the reviews are the course's, and
     * the tab strip remounts on every lesson change (its key is the lesson), so
     * fetching there would repeat the same request all the way down a module.
     */
    const [courseReviews, setCourseReviews] = useState({ enabled: false, reviews: [], count: 0, average: 0 });
    useEffect(() => {
        if (!member?.funnel_id || !courseToken) return undefined;
        let on = true;
        fetchCourseReviews({ funnelId: member.funnel_id, courseToken })
            .then((r) => { if (on) setCourseReviews(r); })
            .catch(() => {});
        return () => { on = false; };
    }, [member?.funnel_id, courseToken]);

    // Bookmarked / saved lessons (set of lesson_token strings).
    const [savedSet, setSavedSet] = useState(() => new Set());
    useEffect(() => {
        if (!member) return undefined;
        let on = true;
        fetchSavedLessons({ funnelId: member.funnel_id, memberId: member.community_member_id, jwt: member.member_token })
            .then((r) => { if (on) setSavedSet(new Set(r.lessons.map((l) => String(l.lesson_id)))); })
            .catch(() => { /* ignore */ });
        return () => { on = false; };
    }, [member?.community_member_id]);

    const toggleSave = async (lesson) => {
        if (!member) return toast.info("Sign in to bookmark lessons.");
        const key = String(lesson.lesson_token);
        const was = savedSet.has(key);
        setSavedSet((s) => { const n = new Set(s); if (was) n.delete(key); else n.add(key); return n; });
        try {
            const r = await toggleSavedLesson(lessonCtx(lesson));
            if (typeof r.saved === "boolean") {
                setSavedSet((s) => { const n = new Set(s); if (r.saved) n.add(key); else n.delete(key); return n; });
            }
            toast.success(was ? "Removed bookmark" : "Lesson bookmarked");
        } catch {
            setSavedSet((s) => { const n = new Set(s); if (was) n.add(key); else n.delete(key); return n; });
            toast.error("Could not update bookmark.");
        }
        return undefined;
    };

    // Right-click is off on the course page: the context menu is the one-click
    // route to "Save video as…", "Copy video address" and "Copy image" on the
    // lesson media. Bound to the document (not a wrapper) so it also covers the
    // portalled bits — quiz modal, floating mini-player — and removed on unmount,
    // so the rest of the app keeps its normal menu.
    // Caveat: a cross-origin player iframe (YouTube/Vimeo/Gumlet) shows ITS menu
    // for clicks inside the frame; no page script can reach into it.
    useEffect(() => {
        const block = (e) => e.preventDefault();
        document.addEventListener("contextmenu", block);
        return () => document.removeEventListener("contextmenu", block);
    }, []);

    // Server-authoritative completion % — refreshes whenever the course reloads
    // (e.g. after marking a lesson complete).
    const [completion, setCompletion] = useState(null);
    const refreshCompletion = useCallback(async () => {
        if (!member) return;
        try {
            setCompletion(await fetchCompletion({ funnelId: member.funnel_id, memberId: member.community_member_id, jwt: member.member_token, courseToken }));
        } catch { /* fall back to local count */ }
    }, [member?.community_member_id, courseToken]);
    useEffect(() => { if (data) refreshCompletion(); }, [data, refreshCompletion]);

    // Load a quiz lesson's PERSISTED status (attempted / passed / retake-lock) from
    // the server so the launcher button reads "Take Quiz" vs "Retake Quiz" vs "Quiz
    // completed" — and shows the retake-lock message — correctly even after a page
    // refresh (not from ephemeral session state). Reused after each attempt too.
    const loadQuizStatus = useCallback(async (lesson) => {
        if (!member || !lesson || Number(lesson.quiz_status) !== 1) return;
        try {
            const d = await fetchQuiz({
                funnelId: member.funnel_id,
                memberId: member.community_member_id,
                jwt: member.member_token,
                courseToken,
                lessonToken: lesson.lesson_token,
            });
            setLessonQuiz(lesson.lesson_token, {
                attempted: !!d.attempted,
                passed: !!d.quiz_passed,
                locked: Number(d.retake_attempt_status) === 0,
                lockMsg: d.attempt_msg || "",
            });
        } catch { /* ignore */ }
    }, [member?.community_member_id, member?.funnel_id, member?.member_token, courseToken]);
    useEffect(() => { if (active) loadQuizStatus(active); }, [active?.lesson_token, active?.quiz_status, loadQuizStatus]);

    // Celebration on course completion (Brand → Options → "Celebration popup").
    // Fires once, only on a genuine transition to 100% during this session — not
    // when re-opening an already-finished course — and only when enabled.
    const [celebrate, setCelebrate] = useState(false);
    const prevPctRef = useRef(null);
    useEffect(() => { prevPctRef.current = null; setCelebrate(false); }, [courseToken]);
    useEffect(() => {
        const p = completion?.percentage;
        if (typeof p !== "number") return;
        const prev = prevPctRef.current;
        prevPctRef.current = p;
        if (prev != null && prev < 100 && p >= 100 && Number(community?.celebration_popup) === 1) {
            setCelebrate(true);
        }
    }, [completion, community, courseToken]);

    // Saved video positions ({ [lesson_token]: seconds }) for resume-on-play.
    const [positions, setPositions] = useState({});
    // Mirror for readers that are not React — see positionsRef's declaration.
    positionsRef.current = positions;
    useEffect(() => {
        if (!member) return undefined;
        let on = true;
        fetchVideoPositions({ funnelId: member.funnel_id, memberId: member.community_member_id, jwt: member.member_token })
            .then((p) => { if (on) setPositions(p); })
            .catch(() => { /* ignore */ });
        return () => { on = false; };
    }, [member?.community_member_id]);
    const persistPosition = (lesson, positionSeconds, durationSeconds) => {
        setPositions((p) => ({ ...p, [String(lesson.lesson_token)]: Math.round(positionSeconds) }));
        saveVideoPosition(lessonCtx(lesson), { positionSeconds, durationSeconds }).catch(() => { /* best effort */ });
    };

    if (loading) return <LessonSkeleton template={template} />;
    /*
     * The lessonlist call failed (timeout, a 401 on a stale JWT, the bridge
     * down). This used to `return null`, which left the member on an empty
     * page — no header, no rail, no lesson and no way back — with nothing to
     * re-fire load(), since it is keyed on the member and the course and
     * neither changes. Only another browser refresh cleared it, so the same
     * page "sometimes" came up blank. Offer the retry and the way out instead.
     */
    if (!data) {
        return (
            <div>
                <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900">
                    <Icon d="M15 19l-7-7 7-7" /> Back to courses
                </button>
                <div className="mt-4 flex flex-col items-center gap-3 rounded-3xl bg-surface px-6 py-14 text-center shadow-[0_10px_40px_-16px_rgba(15,23,42,0.28)] ring-1 ring-gray-900/[0.06]">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                        <Icon d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.85-2.74l-7-12a2 2 0 00-3.7 0l-7 12A2 2 0 005 19z" className="h-5 w-5" />
                    </span>
                    <p className="text-[15px] font-bold text-gray-900">This course didn't load</p>
                    <p className="max-w-sm text-[13px] text-gray-500">Check your connection and try again — nothing about your progress has changed.</p>
                    <button
                        onClick={() => load()}
                        className="mt-1 rounded-xl px-5 py-2.5 text-[13px] font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
                        style={{ backgroundColor: primary }}
                    >
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    const { can_view, lock_reason, unlock_level, unlock_level_name, unlock_points, modules, sales_url } = data;
    // The detail endpoint returns a sparse course (name / image / counts); the
    // list row carries rating, access type, price and description. Merge so the
    // banner shows them, preferring any non-empty value the detail did provide.
    const course = {
        ...(courseMeta || {}),
        ...data.course,
        star_rating: data.course.star_rating || courseMeta?.star_rating || 0,
        access_type: data.course.access_type ?? courseMeta?.access_type ?? 1,
        price: data.course.price ?? courseMeta?.price ?? 0,
        short_description: data.course.short_description || courseMeta?.short_description || "",
        image: data.course.image || courseMeta?.image || "",
        instructor_name: data.course.instructor_name || courseMeta?.instructor_name || "",
        instructor_image: data.course.instructor_image || courseMeta?.instructor_image || "",
        // The ordered list, preferring whichever source actually has one.
        instructors: (data.course.instructors?.length ? data.course.instructors : courseMeta?.instructors) || [],
        // Spelled out rather than left to the spread above: on an API that
        // predates courseHeader this key is undefined on data.course, and a
        // spread copies undefined over the catalogue row's answer.
        purchased: data.course.purchased ?? courseMeta?.purchased ?? false,
    };
    const lockCtx = { unlock_level, unlock_level_name: unlock_level_name || courseMeta?.unlock_level_name || "", unlock_points: unlock_points || courseMeta?.unlock_points || 0 };
    // Level-gated course: show the level's access badge (amber) in the header.
    const isLevelGated = Number(unlock_level) > 0 || Number(course.unlock_level) > 0;
    const levelBadgeLabel = lockCtx.unlock_level_name
        || `Level ${Number(unlock_level) || Number(course.unlock_level) || 0}`;
    const allLessons = modules.flatMap((m) => m.lessons);
    const localDone = allLessons.filter((l) => l.completed).length;
    // Prefer the server's completion count; fall back to the locally-derived one.
    const done = completion ? completion.completed : localDone;
    const totalLessons = completion?.total || allLessons.length;
    const pct = completion ? completion.percentage : (allLessons.length ? Math.round((localDone / allLessons.length) * 100) : 0);
    // Same rule as the course card's badge: a level gate is themed (it names
    // something earned in this membership), paid keeps amber, free is green.
    const accessColor = isLevelGated ? primary : course.access_type === 2 ? "#d97706" : "#16a34a";
    // A paid course the member already owns. `purchased` reaches here through the
    // courseMeta spread above — the course-detail endpoint doesn't return it.
    const isPurchasedCourse = Number(course.access_type) === 2 && !!course.purchased;
    /*
     * The catalogue row is the ONLY source of rating, access type and price —
     * api/lessonlist returns none of them (see the merge above). A member who
     * refreshes straight into the player has no row yet, and a course past the
     * first catalogue page never gets one, so the fallbacks in that merge were
     * being read as facts: access_type 1 prints "Free", star_rating 0 prints
     * "0.0". A paid, well-rated course announced itself as free and unrated
     * for as long as courselist took, and sometimes for good.
     *
     * Both pills wait to be told by SOMEONE. api/lessonlist now answers for
     * itself (FunnelPage::courseHeader -> data.course.header_known), so on a
     * current bridge they render straight away and the catalogue row is only a
     * fallback for an older one. The LEVEL gate never waited — unlock_level has
     * always come from the detail response, so a level-gated course names its
     * level immediately.
     */
    const metaKnown = !!courseMeta || !!data.course.header_known;

    // Where the active lesson sits (module name + index) and flat prev/next.
    const flatIdx = active ? allLessons.findIndex((l) => l.lesson_token === active.lesson_token) : -1;
    const nextLesson = flatIdx >= 0 ? allLessons[flatIdx + 1] : null;
    const prevLesson = flatIdx > 0 ? allLessons[flatIdx - 1] : null;
    let activePos = null;
    if (active) {
        for (let mi = 0; mi < modules.length; mi++) {
            const li = modules[mi].lessons.findIndex((l) => l.lesson_token === active.lesson_token);
            if (li >= 0) { activePos = { moduleName: modules[mi].name, lessonIndex: li }; break; }
        }
    }
    const T = active ? lessonTypeMeta(active.lesson_type) : null;

    // Derived quiz-launcher state for the active lesson (server-driven; survives
    // refresh). Used by both template layouts.
    const activeQuiz = active ? quizStatus[active.lesson_token] : null;
    const quizIsPassed = !!activeQuiz?.passed;
    const quizAttempted = !!activeQuiz?.attempted;
    const quizBtnLabel = quizIsPassed ? "Quiz completed" : quizAttempted ? "Retake Quiz" : "Take Quiz";

    // A course held by one of the time gates. courseMeta (the list row) carries
    // the reason; the detail endpoint doesn't return it, and the merge above keeps
    // the list value because data.course has no such key to overwrite it with.
    const courseTimeLocked =
        course?.lock_reason === "not_released" || course?.lock_reason === "window_ended";

    // A time-gated course renders ONLY the lock overlay, before any layout branch.
    //
    // This was previously folded into the shared `overlays` fragment, which turns
    // out to be rendered by the `flexifunnels` template branch alone — so on every
    // other template the overlay never mounted and the member fell through to the
    // player's own per-lesson lock ("Access Forbidden!"). Returning here makes it
    // template-independent, and there is nothing worth rendering behind a course
    // the member cannot open anyway.
    if (courseTimeLocked) {
        return <CourseLockOverlay course={course} primary={primary} onBack={onBack} />;
    }

    // Same treatment for the restriction api/lesson-details reports (restrictCourse):
    // the reference app covered the entire screen with the padlock, the creator's
    // upgrade line and their button, and no lesson content behind it.
    if (courseRestriction) {
        return (
            <CourseLockOverlay
                course={{
                    ...course,
                    lock_reason: "course_restricted",
                    upgrade_text: courseRestriction.lock_message,
                    upgrade_button_text: courseRestriction.upgrade_button,
                    upgrade_button_link: courseRestriction.upgrade_link,
                }}
                primary={primary}
                onBack={onBack}
            />
        );
    }

    // Shared overlays (celebration + quiz).
    const overlays = (
        <>
            {celebrate && (
                <CelebrationOverlay primary={primary} title={course.name} onClose={() => setCelebrate(false)} />
            )}
            {introModule?.intro_video && (
                <IntroVideoModal
                    url={introModule.intro_video.url}
                    embedHtml={introModule.intro_video.embed_html}
                    title={introModule.intro_video.title || introModule.name}
                    subtitle="Module intro"
                    primary={primary}
                    onClose={() => setIntroModule(null)}
                />
            )}
            {quizOpen && active && (
                <QuizModal
                    ctx={lessonCtx(active)}
                    lessonName={active.title || active.name}
                    primary={primary}
                    onClose={() => setQuizOpen(false)}
                    onFailed={() => {
                        // Optimistic, then re-sync from server (gets retake-lock + msg).
                        setLessonQuiz(active.lesson_token, { attempted: true, passed: false });
                        loadQuizStatus(active);
                    }}
                    onPassed={() => {
                        setLessonQuiz(active.lesson_token, { attempted: true, passed: true, locked: false });
                        loadQuizStatus(active);
                    }}
                />
            )}
        </>
    );

    /* The same video, opened directly rather than framed. When a browser is the
       reason an embed will not run, a first-party tab is the one place it still
       works: the restrictions that break these players — partitioned storage,
       EME withheld from third-party frames, embeds blocked by an extension —
       are all restrictions on being IN A FRAME. */
    const embedDirectUrl = !active || active.locked || active.lesson_type !== 3
        ? null
        : active.embed_html
            ? firstIframeSrc(normalizeEmbed(active.embed_html))
            : (active.media_url && !isFile(active.media_url) && !isHls(active.media_url)
                ? embedUrl(active.media_url)
                : null);

    /* Shown only once the watchdog has decided the embed is not running (see
       embedStalled). Deliberately a bar ABOVE the player rather than a
       replacement for it: the player may still be merely slow, and swapping it
       out would kill a video that was about to start. */
    const embedRecoveryBar = !embedStalled || !embedDirectUrl ? null : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 px-4 py-2.5 text-[12.5px] text-amber-900 dark:text-amber-300">
            <span className="flex items-center gap-1.5 font-semibold">
                <Icon d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.74-3l-6.93-12a2 2 0 00-3.48 0l-6.93 12a2 2 0 001.74 3z" className="w-4 h-4 shrink-0" />
                This video isn&rsquo;t playing in this browser.
            </span>
            <button
                type="button"
                onClick={() => { setEmbedStalled(false); setEmbedReload((n) => n + 1); }}
                className="rounded-full bg-amber-100 dark:bg-amber-500/15 px-3 py-1 font-bold text-amber-900 dark:text-amber-300 ring-1 ring-amber-300 dark:ring-amber-500/30 transition hover:bg-amber-200"
            >
                Reload player
            </button>
            <a
                href={embedDirectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-surface px-3 py-1 font-bold text-amber-900 dark:text-amber-300 ring-1 ring-amber-300 dark:ring-amber-500/30 transition hover:bg-amber-100 dark:hover:bg-amber-500/15"
            >
                Open in a new tab
            </a>
        </div>
    );

    // Media node (video / embed / audio) for the active lesson — shared shape.
    const mediaNode = !active ? null : (
        // Keyed by lesson so a lesson change fully REMOUNTS the media subtree
        // instead of reconciling the same <div> in place. Third-party players
        // (YouTube's YT.Player, VdoCipher.getInstance, some embed scripts) can
        // replace or relocate the <iframe> node they were handed; reusing the
        // node would make React removeChild a displaced iframe → "not a child of
        // this node". Remounting removes the outer wrapper React fully owns. The
        // key is constant while a single lesson floats/scrolls, so the mini-player
        // keeps playing without a remount.
        <Fragment key={active.lesson_token}>
        {active.locked ? (
        // Fills the whole player area — the reference shows the lock where the
        // video would be, with the module list still beside it, so the member can
        // see which modules ARE open. A small inline notice read as a hiccup;
        // this reads as the state of the course.
        (() => {
            // A drip/scheduled lock is a "come back later", not an upsell — it gets
            // the amber treatment and a different chip, so the screen never implies
            // money will fix something only time will.
            const isScheduled = String(active.lock_reason || lock_reason || "").startsWith("drip")
                || (active.lock_reason || lock_reason) === "not_released";
            // A subscription that lapsed, as opposed to access never held. The
            // bridge names it since the curriculum learned to tell the two apart;
            // access_expired is the older per-lesson flag, kept as a fallback.
            const isExpired = (active.lock_reason || lock_reason) === "access_expired"
                || !!active.access_expired;
            const accent = isScheduled ? "#f59e0b" : primary;
            const ctaClass =
                "inline-flex items-center justify-center gap-1.5 rounded-xl px-6 py-3 text-[14px] font-bold transition-all duration-200 hover:brightness-[1.06] active:scale-[.98]";
            const ctaStyle = {
                background: `linear-gradient(140deg, ${primary}, ${primary}c4)`,
                color: "var(--brand-btn-text, #fff)",
                boxShadow: `0 14px 28px -14px ${primary}`,
            };
            const ctaArrow = (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h11" />
                </svg>
            );

            return (
                <div className="relative flex min-h-[24rem] flex-col items-center justify-center overflow-hidden px-6 py-14 text-center sm:min-h-[28rem]">
                    {/* Layered backdrop: base wash, a brand glow bleeding from the top,
                        and a faint grid that gives the empty area some structure
                        instead of reading as a blank panel. */}
                    <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-b from-surface via-gray-50/80 to-surface" />
                    <div
                        aria-hidden
                        className="pointer-events-none absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 -translate-y-1/3 rounded-full blur-3xl"
                        style={{ background: `radial-gradient(closest-side, ${accent}2e, transparent)` }}
                    />
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 opacity-[0.55]"
                        style={{
                            backgroundImage:
                                "linear-gradient(to right, rgba(15,23,42,.045) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,.045) 1px, transparent 1px)",
                            backgroundSize: "34px 34px",
                            maskImage: "radial-gradient(ellipse at center, #000 15%, transparent 72%)",
                            WebkitMaskImage: "radial-gradient(ellipse at center, #000 15%, transparent 72%)",
                        }}
                    />

                    <div className="relative flex w-full max-w-md flex-col items-center">
                        <LockIllustration accent={accent} className="h-40 w-full max-w-[240px] sm:h-44" />

                        <span
                            className="mt-5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.09em]"
                            style={{ backgroundColor: `${accent}1f`, color: accent }}
                        >
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
                                {isScheduled ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M6 11h12v9H6z" />
                                )}
                            </svg>
                            {isScheduled ? "Scheduled" : "Locked"}
                        </span>

                        {/* Prefer the server-formatted message (module drip countdown /
                            upgrade copy / drip-date text / "Access Forbidden!"); fall
                            back to a locally-composed one. */}
                        <p className="mt-3 text-balance text-[19px] font-bold leading-snug tracking-[-0.015em] text-gray-900 sm:text-[22px]">
                            {active.lock_message || lockMsg(active.lock_reason || lock_reason, { ...lockCtx, unlock_on: active.unlock_on })}
                        </p>

                        {/* When the lock is a subscription that RAN OUT, say when. The
                            message above is the creator's wording (bundles.
                            expired_access_message); the date is what turns it from a
                            refusal into something the member can act on. */}
                        {isExpired && active.expired_at ? (
                            <p className="mt-2 text-[13px] font-medium text-gray-500">
                                Expired on {fmtDripDate(active.expired_at)}
                            </p>
                        ) : null}

                        {/* Buy Now for an expired subscription. Rendered only when the
                            course actually has somewhere to buy — api/lessonlist sends
                            sales_url:'' for a course with no sales or checkout page, and
                            a button that goes nowhere is worse than no button.
                            Takes precedence over the upgrade CTA below: a lapsed
                            entitlement is a renewal, not an upsell. */}
                        {isExpired && sales_url ? (
                            <a
                                href={withReferral(sales_url)}
                                target="_blank"
                                rel="noreferrer"
                                className={`mt-6 ${ctaClass}`}
                                style={ctaStyle}
                            >
                                Buy Now
                                {ctaArrow}
                            </a>
                        ) : null}

                        {/* Upgrade CTA for an upgrade-locked lesson (lessons.upgrade_data).
                            The label alone is enough to render it — same as the reference,
                            which sent a link-less button back to the course list. */}
                        {isExpired && sales_url ? null : active.upgrade_link ? (
                            <a
                                href={active.upgrade_link}
                                target="_blank"
                                rel="noreferrer"
                                className={`mt-6 ${ctaClass}`}
                                style={ctaStyle}
                            >
                                {active.upgrade_button || "Upgrade"}
                                {ctaArrow}
                            </a>
                        ) : active.upgrade_button ? (
                            <button type="button" onClick={onBack} className={`mt-6 ${ctaClass}`} style={ctaStyle}>
                                {active.upgrade_button}
                                {ctaArrow}
                            </button>
                        ) : null}
                    </div>
                </div>
            );
        })()
    ) : !active.hydrated && active.lesson_type !== 1 ? (
        <div className={`animate-pulse bg-gray-100 ${active.lesson_type === 3 ? "aspect-video" : "h-24"}`} />
    ) : active.lesson_type === 3 && (active.embed_html || active.media_url) ? (
        <>
        {embedRecoveryBar}
        {active.embed_html ? (
            <div key={embedReload} className="yt-wrap relative aspect-video overflow-hidden bg-black">
                <div ref={embedHtmlRef} className="absolute inset-0" dangerouslySetInnerHTML={{ __html: normalizeEmbed(active.embed_html) }} />
                {/* An author-pasted embed is often a YouTube iframe too. */}
                {/youtube(-nocookie)?\.com\/embed\//i.test(active.embed_html || "") ? (
                    <YouTubeGuard getFrame={() => embedHtmlRef.current?.querySelector("iframe") || null} />
                ) : null}
            </div>
        ) : isFile(active.media_url) ? (
            <ResumableVideo
                src={active.media_url}
                startAt={positions[String(active.lesson_token)] || 0}
                onSave={(pos, dur) => persistPosition(active, pos, dur)}
                videoRef={videoRef}
                primary={primary}
            />
        ) : isHls(active.media_url) ? (
            <HlsVideo
                src={active.media_url}
                startAt={positions[String(active.lesson_token)] || 0}
                onSave={(pos, dur) => persistPosition(active, pos, dur)}
                videoRef={videoRef}
                primary={primary}
            />
        ) : (
            /youtube(-nocookie)?\.com\/embed\//i.test(embedUrl(active.media_url)) ? (
                <YouTubePlayer
                    src={embedUrl(active.media_url)}
                    title={active.title}
                    frameRef={embedIframeRef}
                />
            ) : (
                <div key={embedReload} className="aspect-video bg-black">
                    {/* allow: see EMBED_ALLOW — every one of those features is off
                        by default in a cross-origin frame, and `encrypted-media`
                        in particular is what a Gumlet/VdoCipher player needs
                        before it can start a protected stream at all.
                        referrerPolicy: providers gate embeds on the referring
                        domain, so the origin has to survive a stricter global
                        policy — but only the origin, never the lesson path.
                        sandbox: see PLAYER_SANDBOX — withholding allow-popups and
                        allow-top-navigation is what makes the Vimeo and Gumlet
                        logos in the control bar inert, fullscreen included. It
                        grants allow-same-origin, so it takes nothing away from
                        the two lines above: the player still sends its referrer
                        and still gets every feature EMBED_ALLOW hands it. */}
                    <iframe
                        ref={embedIframeRef}
                        title={active.title}
                        src={embedUrl(active.media_url)}
                        className="w-full h-full"
                        style={{ border: 0 }}
                        sandbox={PLAYER_SANDBOX}
                        allow={EMBED_ALLOW}
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                    />
                </div>
            )
        )}
        </>
    ) : active.lesson_type === 3 && active.hydrated && !!active.video_type && !active.body_has_video ? (
        /* A lesson that HAS a video provider but resolved to no source — say so,
           the way the reference player does, instead of leaving an empty gap.
           Two exclusions, both of which would otherwise be false accusations:

             video_type    must be set. A third of lessons are lesson_type = 1
                           with no video ever attached — a creator made a video
                           lesson and has not filled it in yet. There is nothing
                           unavailable about those, and the reference agrees:
                           its "currently unavailable" copy lives INSIDE the
                           per-provider branches, never in the fall-through.
             body_has_video  a legacy lesson can carry its player inside its html
                           body, so it has no media_url and no embed_html and
                           still shows its video. */
        <VideoUnavailable videoType={active.video_type} videoStatus={active.video_status} />
    ) : active.lesson_type === 2 && active.media_url ? (
        <div className="flex items-center gap-4 border-b border-gray-100 px-5 py-5" style={{ background: `linear-gradient(135deg, ${T.color}12, rgb(var(--c-surface)) 70%)` }}>
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white shadow-sm" style={{ backgroundColor: T.color }}>
                <Icon d={T.icon} className="w-6 h-6" />
            </span>
            <audio src={active.media_url} controls className="w-full" />
        </div>
    ) : null}
        </Fragment>
    );

    // Complete / quiz action buttons for the active lesson — shared. A locked
    // lesson shows none of them (not even the "Completed" chip): the reference
    // hid the whole action row behind a restriction.
    const actionButtons = !active || active.locked ? null : active.completed ? (
        <span className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/15 ring-1 ring-green-200 dark:ring-green-500/30">
            <Icon d="M5 13l4 4L19 7" className="w-3.5 h-3.5" /> {labels("lesson_complete_txt", "Completed")}
        </span>
    ) : (!active.locked && active.hydrated) ? (
        <div className="flex flex-wrap items-center gap-2">
            {Number(active.quiz_status) === 1 && (
                quizIsPassed ? (
                    // Passed → read-only status (no retake). The retake/lock message
                    // lives only inside the quiz popup, never inline here.
                    <span className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/15 ring-1 ring-green-200 dark:ring-green-500/30">
                        <Icon d="M5 13l4 4L19 7" className="w-3.5 h-3.5" /> Quiz completed
                    </span>
                ) : (
                    <button onClick={() => setQuizOpen(true)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold text-white shadow-sm transition hover:brightness-110" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>
                        <Icon d="M9 12l2 2 4-4m5 2a9 9 0 11-18 0 9 9 0 0118 0z" className="w-3.5 h-3.5" />
                        {quizBtnLabel}
                    </button>
                )
            )}
            <button onClick={() => toggleComplete(active)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold text-white shadow-sm transition hover:brightness-110" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>
                <Icon d="M5 13l4 4L19 7" className="w-3.5 h-3.5" /> {labels("mark_complete_txt", "Mark as Complete")}
            </button>
        </div>
    ) : null;

    // Right-hand modules rail (shared by the FlexiFunnels layout). Reuses the
    // same accordion state (collapsed / toggleModule) as the Classic curriculum.
    const modulesRail = (
        <div className="overflow-hidden rounded-2xl bg-surface shadow-[0_6px_24px_-12px_rgba(15,23,42,0.18)] ring-1 ring-gray-900/[0.06]">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                <h3 className="text-sm font-extrabold tracking-tight text-gray-900">Total {modules.length} {modules.length === 1 ? "Module" : "Modules"}</h3>
            </div>
            {/* Progress: count · track · percent. This rail showed the count and
                the percent with nothing between them — the Classic layout's
                progress panel has a ring + bar, so the SHIPPED DEFAULT was the
                one layout with no visual progress at all. Track styling is
                lifted from the course cards so the two read as the same
                component across the app. */}
            <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-2.5 text-[12px] font-semibold">
                <span className="shrink-0 text-gray-500">{done}/{totalLessons} {labels("lesson_txt", "Lessons")}</span>
                {totalLessons > 0 && (
                    <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100 ring-1 ring-inset ring-gray-200/70">
                        <div
                            className="h-full rounded-full transition-[width] duration-500 ease-out"
                            style={{
                                // Floor a started course at 6% so the first completed
                                // lesson is actually visible on a narrow rail.
                                width: `${pct >= 100 ? 100 : pct > 0 ? Math.max(pct, 6) : 0}%`,
                                background:
                                    pct >= 100
                                        ? "linear-gradient(90deg, #16a34a 0%, #22c55e 100%)"
                                        : `linear-gradient(90deg, ${primary} 0%, var(--brand-secondary, ${primary}cc) 100%)`,
                                boxShadow: pct > 0 ? `0 1px 5px ${primary}66` : "none",
                            }}
                        />
                    </div>
                )}
                <span className="shrink-0" style={{ color: pct >= 100 ? "#16a34a" : primary }}>{pct}%</span>
            </div>
            {/* Course certificate. This layout is the SHIPPED DEFAULT (the column
                default moved from 'classic' to 'flexifunnels'), but the button only
                existed in the other branch's progress panel — so the default layout
                completed a course and offered nothing. The API only returns
                certificate_url at 100% and when the creator enabled certificates,
                so the flag alone is the right condition here too. */}
            {pct === 100 && completion?.certificate_url && (
                <div className="border-b border-gray-100 p-2.5">
                    <a
                        href={completion.certificate_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl px-4 py-2.5 text-[12.5px] font-bold text-white transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
                        style={{
                            background: "linear-gradient(135deg, #22c55e 0%, #16a34a 55%, #15803d 100%)",
                            boxShadow: "0 10px 22px -10px rgba(22,163,74,0.75)",
                        }}
                    >
                        <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
                        <svg className="relative h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="8" r="6" />
                            <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
                        </svg>
                        <span className="relative">Download certificate</span>
                    </a>
                </div>
            )}
            <div className="space-y-2 p-2.5">
                {modules.map((m, mi) => {
                    const mDone = m.lessons.filter((l) => l.completed).length;
                    const mAll = m.lessons.length;
                    const mComplete = mAll > 0 && mDone === mAll;
                    const isOpen = !collapsed[mi];
                    // Module drip (drip_feeds): a padlock replaces the progress mark
                    // and the row still opens, so the player can explain WHEN it
                    // unlocks rather than the module silently doing nothing.
                    const mLocked = !!m.locked;
                    return (
                        <div key={mi} className="overflow-hidden rounded-xl ring-1 ring-gray-900/[0.06]">
                            <ModuleHeader
                                module={m}
                                index={mi}
                                compact
                                isOpen={isOpen}
                                mDone={mDone}
                                mAll={mAll}
                                mComplete={mComplete}
                                mLocked={mLocked}
                                primary={primary}
                                onOpenFirst={() => {
                                    // The row loads the module's first lesson AND expands it.
                                    // Loading a lesson into the viewer while the module stayed shut
                                    // left the member with no sign of where in the course they had
                                    // just landed.
                                    openModule(mi);
                                    const first = m.lessons[0];
                                    if (first) openLesson(first);
                                }}
                                onToggle={() => toggleModule(mi)}
                                onPlayIntro={() => setIntroModule(m)}
                            />
                            {isOpen && (
                                <div className="space-y-0.5 border-t border-gray-100 p-1.5">
                                    <ModuleIntroCard module={m} primary={primary} onPlay={() => setIntroModule(m)} />
                                    {mAll === 0 ? (
                                        <p className="px-3 py-2 text-[12px] text-gray-400">No lessons.</p>
                                    ) : (
                                        m.lessons.map((l) => {
                                            const LT = lessonTypeMeta(l.lesson_type);
                                            const isActive = active?.lesson_token === l.lesson_token;
                                            const locked = !!l.locked;
                                            return (
                                                <button
                                                    key={l.lesson_token}
                                                    onClick={() => openLesson(l)}
                                                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${isActive ? "" : "hover:bg-gray-50"}`}
                                                    style={isActive ? { backgroundColor: `${primary}12`, boxShadow: `inset 0 0 0 1px ${primary}29` } : {}}
                                                >
                                                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg"
                                                        style={locked ? { backgroundColor: "rgb(var(--c-gray-100))", color: "rgb(var(--c-gray-400))" } : { backgroundColor: `${LT.color}14`, color: LT.color }}>
                                                        {locked
                                                            ? <Icon d="M12 11V7a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6a1 1 0 011-1z" className="h-3.5 w-3.5" />
                                                            : <Icon d={LT.icon} className="h-3.5 w-3.5" />}
                                                    </span>
                                                    <Tooltip label={l.title}>
                                                        <span className={`min-w-0 flex-1 line-clamp-2 text-[12.5px] leading-snug ${locked ? "text-gray-400" : isActive ? "font-semibold text-gray-900" : "text-gray-700"}`}>{l.title}</span>
                                                    </Tooltip>
                                                    {l.completed ? (
                                                        <Icon d="M5 13l4 4L19 7" className="h-4 w-4 shrink-0 text-green-500" />
                                                    ) : locked ? (
                                                        <Icon d="M12 11V7a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6a1 1 0 011-1z" className="h-4 w-4 shrink-0 text-gray-300 dark:text-gray-500" />
                                                    ) : null}
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    // ------------------------------------------------------------------ //
    // FlexiFunnels layout (default): breadcrumb, large video on the left with //
    // "Mark as Complete" + About/Discussions/Notes tabs, modules rail right.   //
    // ------------------------------------------------------------------ //
    // ── Picture-in-Picture + timestamped-notes helpers (FlexiFunnels layout) ──
    // Reads the current position from a direct-file <video> (videoRef) OR, for a
    // cross-origin embed (YouTube/Vimeo/Gumlet), from its player API via
    // embedTrackerRef (see videoTime.js) — mirroring how the old app read each
    // provider's clock, so notes can capture the video time everywhere.
    const fmtVideoTime = (s) => {
        const t = Math.max(0, Math.floor(Number(s) || 0));
        return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
    };
    const getVideoTime = () => {
        if (videoRef.current) return fmtVideoTime(videoRef.current.currentTime);
        const s = embedTrackerRef.current?.getSeconds?.();
        return typeof s === "number" ? fmtVideoTime(s) : "";
    };
    const seekVideo = (timeStr) => {
        if (!timeStr) return;
        const parts = String(timeStr).split(":").map(Number);
        const secs = parts.length === 2 ? parts[0] * 60 + parts[1] : Number(parts[0]) || 0;
        const v = videoRef.current;
        if (v) {
            try { v.currentTime = secs; v.play?.(); v.scrollIntoView?.({ behavior: "smooth", block: "center" }); } catch { /* ignore */ }
            return;
        }
        embedTrackerRef.current?.seekTo?.(secs);
    };
    // A native <video> is on screen — a direct file OR an HLS stream. Both expose
    // currentTime via videoRef.
    const hasFileVideo = !!active && !active.locked && active.hydrated
        && active.lesson_type === 3 && !active.embed_html
        && (isFile(active.media_url) || isHls(active.media_url));
    // A tracked cross-origin embed is on screen: a provider iframe
    // (YouTube/Vimeo/Gumlet) OR a DRM/custom embed_html block — its time is read
    // through the provider's player API (embedTrackerRef).
    const hasTrackedEmbed = !!active && !active.locked && active.hydrated
        && active.lesson_type === 3
        && (!!active.embed_html || (!!active.media_url && !isFile(active.media_url) && !isHls(active.media_url)));
    // Notes can stamp the video time when a native video or tracked embed is on screen.
    const canCaptureTime = hasFileVideo || hasTrackedEmbed;
    // Any playable video on screen — enables the floating mini-player (FloatingVideo).
    const hasAnyVideo = !!active && !active.locked && active.hydrated
        && active.lesson_type === 3 && (active.embed_html || active.media_url);

    if (template === "flexifunnels") {
        return (
            <div>
                {overlays}
                {/* breadcrumb */}
                <nav className="flex items-center gap-2 text-[13px]">
                    <button onClick={onBack} className="font-semibold text-gray-500 transition-colors hover:text-gray-800">Home</button>
                    <span className="text-gray-300 dark:text-gray-500">/</span>
                    <span className="min-w-0 truncate font-semibold text-gray-800">{course.name}</span>
                </nav>

                {/* Events for this course PLUS the general "courses" placement — the
                    same event learners see on the course list, so it carries into the
                    player too. The API ORs the contexts (course_page_id | placement). */}
                <UpcomingEventBanner token={token} coursePageId={courseToken} placement="courses" primary={primary} className="mt-4" />

                {!can_view && (
                    <div className="mt-4 flex items-center gap-2 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
                        <Icon d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.85-2.74l-7-12a2 2 0 00-3.7 0l-7 12A2 2 0 005 19z" />
                        {lockMsg(lock_reason, lockCtx)}
                    </div>
                )}

                <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-12">
                    {/* ---------- player (left) ---------- */}
                    <div ref={viewerRef} className="lg:col-span-8">
                        {!active ? (
                            <div className="rounded-2xl bg-surface p-10 text-center shadow-[0_6px_24px_-12px_rgba(15,23,42,0.18)] ring-1 ring-gray-900/[0.06]">
                                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: `${primary}14`, color: primary }}>
                                    <Icon d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" className="w-6 h-6" />
                                </span>
                                <p className="mt-3 text-sm font-medium text-gray-400">Select a lesson to begin.</p>
                            </div>
                        ) : (
                            <div className="overflow-hidden rounded-2xl bg-surface shadow-[0_10px_40px_-16px_rgba(15,23,42,0.18)] ring-1 ring-gray-900/[0.06]">
                                <FloatingVideo enabled={hasAnyVideo} lessonToken={active?.lesson_token} primary={primary}>{mediaNode}</FloatingVideo>
                                <div className="p-5 sm:p-7">
                                    {/* Stacks below sm — see the Classic header. */}
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                        <div className="min-w-0 sm:flex-1">
                                            <h1 className="truncate text-xl font-extrabold tracking-tight text-gray-900 sm:text-2xl">{course.name}</h1>
                                            <p className="mt-1.5 text-[13px] leading-relaxed text-gray-500 [overflow-wrap:anywhere]">
                                                {activePos && (
                                                    <><span className="font-semibold text-gray-700">Module:</span> {activePos.moduleName}{"  "}<span className="mx-1 text-gray-300 dark:text-gray-500">·</span></>
                                                )}
                                                <span className="font-semibold text-gray-700">{labels("lesson_txt", "Lesson")}:</span> {active.title}
                                            </p>
                                        </div>
                                        <div className="flex shrink-0 flex-wrap items-center gap-2">
                                            {actionButtons}
                                        </div>
                                    </div>

                                    {active.locked ? (
                                        <p className="mt-4 text-sm text-gray-400">{active.lock_message || lockMsg(active.lock_reason || lock_reason, { ...lockCtx, unlock_on: active.unlock_on })}</p>
                                    ) : !active.hydrated ? (
                                        <div className="mt-5 animate-pulse space-y-2">
                                            <div className="h-3 w-full rounded bg-gray-100" />
                                            <div className="h-3 w-11/12 rounded bg-gray-100" />
                                            <div className="h-3 w-4/6 rounded bg-gray-100" />
                                        </div>
                                    ) : (
                                        <FlexiLessonTabs
                                            key={active.lesson_token}
                                            active={active}
                                            ctx={lessonCtx(active)}
                                            primary={primary}
                                            member={member}
                                            labels={labels}
                                            discussionOn={community?.discussion ?? true}
                                            reviews={courseReviews}
                                            goal={active.goal || null}
                                            goalForm={goalForm}
                                            setGoalForm={setGoalForm}
                                            goalSubmitting={submitting}
                                            onSubmitGoal={() => submitGoal(active)}
                                            onPointsChange={onPointsChange}
                                            getTime={canCaptureTime ? getVideoTime : null}
                                            onSeek={canCaptureTime ? seekVideo : null}
                                        />
                                    )}

                                    {(prevLesson || nextLesson) && (
                                        <div className="mt-6 flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
                                            {prevLesson ? (
                                                <button onClick={() => openLesson(prevLesson)} className="group inline-flex max-w-[46%] items-center gap-2 rounded-xl border border-gray-200 bg-surface/70 px-3 py-1.5 text-left backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-lg sm:px-3.5">
                                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-all duration-300 group-hover:-translate-x-0.5 group-hover:bg-ink group-hover:text-white">
                                                        <Icon d="M15 19l-7-7 7-7" className="h-3.5 w-3.5" />
                                                    </span>
                                                    <span className="min-w-0"><span className="block text-[9px] font-semibold uppercase tracking-[0.16em] text-gray-400">Previous</span><Tooltip label={prevLesson.title}><span className="hidden truncate text-[13px] font-semibold text-gray-700 group-hover:text-gray-900 sm:block">{prevLesson.title}</span></Tooltip></span>
                                                </button>
                                            ) : <span />}
                                            {nextLesson ? (
                                                <button onClick={() => openLesson(nextLesson)} className="group inline-flex max-w-[46%] items-center gap-2 rounded-xl px-3 py-1.5 text-right transition-all duration-300 hover:-translate-y-0.5 hover:brightness-95 sm:px-4" style={{ backgroundColor: primary, color: "var(--brand-btn-text, rgb(var(--c-surface)))", boxShadow: `0 6px 16px -8px ${primary}59` }}>
                                                    <span className="min-w-0"><span className="block text-[9px] font-semibold uppercase tracking-[0.16em] text-white/70">Next</span><Tooltip label={nextLesson.title}><span className="hidden truncate text-[13px] font-bold sm:block">{nextLesson.title}</span></Tooltip></span>
                                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/20 ring-1 ring-inset ring-white/30 transition-transform duration-300 group-hover:translate-x-0.5">
                                                        <Icon d="M9 5l7 7-7 7" className="h-3.5 w-3.5" />
                                                    </span>
                                                </button>
                                            ) : <span />}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ---------- modules rail (right) ---------- */}
                    <div className="lg:col-span-4">{modulesRail}</div>
                </div>
            </div>
        );
    }

    return (
        <div>
            {celebrate && (
                <CelebrationOverlay
                    primary={primary}
                    title={course.name}
                    onClose={() => setCelebrate(false)}
                />
            )}
            {introModule?.intro_video && (
                <IntroVideoModal
                    url={introModule.intro_video.url}
                    embedHtml={introModule.intro_video.embed_html}
                    title={introModule.intro_video.title || introModule.name}
                    subtitle="Module intro"
                    primary={primary}
                    onClose={() => setIntroModule(null)}
                />
            )}
            {quizOpen && active && (
                <QuizModal
                    ctx={lessonCtx(active)}
                    lessonName={active.title || active.name}
                    primary={primary}
                    onClose={() => setQuizOpen(false)}
                    onFailed={() => {
                        // Passing/failing the quiz is an independent task — it does NOT
                        // complete the lesson; the member clicks Mark Complete for that.
                        setLessonQuiz(active.lesson_token, { attempted: true, passed: false });
                        loadQuizStatus(active);
                    }}
                    onPassed={() => {
                        setLessonQuiz(active.lesson_token, { attempted: true, passed: true, locked: false });
                        loadQuizStatus(active);
                    }}
                />
            )}
            <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900">
                <Icon d="M15 19l-7-7 7-7" /> Back to courses
            </button>

            {/* ---------- Course header (full width) ---------- */}
            <div className="mt-4">
                {/* Events targeted to this course. placement="courses" matches
                    the sibling banner above the course list — a banner only ever
                    shows on a page the creator ticked. */}
                <UpcomingEventBanner token={token} coursePageId={courseToken} placement="courses" primary={primary} className="mb-4" />

                {/* Full-width course header: clear cover image · info · a dedicated
                    premium progress panel. Compact height, three columns on desktop. */}
                <div className="group relative flex flex-col overflow-hidden rounded-3xl bg-surface shadow-[0_10px_40px_-16px_rgba(15,23,42,0.28)] ring-1 ring-gray-900/[0.06] lg:flex-row lg:min-h-[6.5rem]">
                    {/* Cover image — cinematic, brand-tinted */}
                    <div className="relative w-full shrink-0 overflow-hidden bg-ink lg:w-64 xl:w-72">
                        <img
                            src={course.image || DEFAULT_COURSE_IMAGE}
                            onError={(e) => { e.currentTarget.src = DEFAULT_COURSE_IMAGE; }}
                            alt=""
                            className="h-40 w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05] lg:absolute lg:inset-0 lg:h-full"
                        />
                    </div>

                    {/* Info */}
                    <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 px-6 py-4">
                        <Tooltip label={course.name}>
                            <h1 className="truncate text-xl font-extrabold tracking-tight text-gray-900 sm:text-[1.6rem]">{course.name}</h1>
                        </Tooltip>
                        {course.short_description && (
                            <Tooltip label={course.short_description}>
                                <p className="line-clamp-1 text-[13px] leading-relaxed text-gray-500">{course.short_description}</p>
                            </Tooltip>
                        )}
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200/70 bg-surface px-3 py-1 text-[11px] font-semibold text-gray-600 shadow-sm">
                                <Icon d="M4 6h16M4 10h16M4 14h10M4 18h10" className="w-3.5 h-3.5 text-gray-400" /> {modules.length} modules
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200/70 bg-surface px-3 py-1 text-[11px] font-semibold text-gray-600 shadow-sm">
                                <Icon d="M12 6.5C10.5 5.5 8.3 5 6 5a8 8 0 00-2 .26v12A8 8 0 016 17c2.3 0 4.5.5 6 1.5m0-12c1.5-1 3.7-1.5 6-1.5a8 8 0 012 .26v12A8 8 0 0018 17c-2.3 0-4.5.5-6 1.5m0-12V18.5" className="w-3.5 h-3.5 text-gray-400" /> {allLessons.length} lessons
                            </span>
                            {/* Access pill. Hidden with the catalogue badges when
                                Membership Grade is off, but only while it is showing
                                the Free/Paid grade — a level name, the locked state
                                (access_type 3) and "Purchased" all stay.

                                This header names the grade only — "Free", "Paid",
                                "Purchased", a level — never the amount. The price is
                                the sales page's job, where it appears beside what the
                                money buys; on a card it is a bare number with no
                                currency or offer attached to it. */}
                            {(isLevelGated || (metaKnown && (gradeOn || Number(course.access_type) === 3 || isPurchasedCourse))) && (
                                <span className="inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-bold" style={{ backgroundColor: `${accessColor}12`, color: accessColor, borderColor: `${accessColor}33` }}>
                                    {isLevelGated
                                        ? levelBadgeLabel
                                        : isPurchasedCourse
                                            ? "Purchased"
                                            : ACCESS[course.access_type]?.label}
                                </span>
                            )}
                            {/* Course Rating toggle — hidden here too, so turning it
                                off cannot be defeated by opening the course. */}
                            {metaKnown && ratingOn && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200/70 bg-amber-50 dark:bg-amber-500/15 px-3 py-1 text-[11px] font-bold text-amber-600 dark:text-amber-300">
                                    <svg className="h-3 w-3 fill-amber-400" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                                    {Number(course.star_rating || 0).toFixed(1)}
                                </span>
                            )}
                            <InstructorChips
                                list={instructorsOf(course)}
                                onInstructor={onInstructor}
                                primary={primary}
                                prefixInLabel={false}
                                sizeClass="h-5 w-5 shrink-0"
                                textClass="text-[9px]"
                                imgClass="ring-2 ring-surface"
                                initialClass="ring-2 ring-surface text-white"
                                initialStyle={{ backgroundColor: primary }}
                                labelClass=""
                                // Several instructors show avatars only, so the
                                // chip drops the padding the label needed.
                                className={`inline-flex items-center gap-1.5 rounded-full border border-gray-200/70 bg-surface py-0.5 pl-0.5 text-[11px] font-semibold text-gray-700 shadow-sm ${
                                    instructorsOf(course).length > 1 ? "pr-1" : "pr-3"
                                }`}
                            />
                        </div>
                    </div>

                    {/* Premium progress panel */}
                    <div className="relative flex shrink-0 flex-col items-center justify-center gap-2.5 border-t border-gray-100 px-7 py-4 lg:w-64 lg:border-l lg:border-t-0" style={{ background: `linear-gradient(160deg, ${primary}12, rgb(var(--c-surface)) 72%)` }}>
                        <div className="flex items-center gap-3">
                            <ProgressRing pct={pct} primary={primary} size={54} />
                            <div className="text-left leading-none">
                                <p className="text-xl font-extrabold text-gray-900">{done}<span className="text-sm font-semibold text-gray-400">/{totalLessons}</span></p>
                                <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400">lessons done</p>
                            </div>
                        </div>
                        <p className="text-center text-[11px] font-medium leading-snug text-gray-500">{pct === 100 ? "Course complete 🎉" : pct > 0 ? "Keep going — you're making progress" : "Not started yet"}</p>
                        <div className="h-2 w-full max-w-[12rem] overflow-hidden rounded-full bg-gray-200/70 shadow-inner">
                            <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${pct}%`, background: pct >= 100 ? "#16a34a" : `linear-gradient(90deg, ${primary}, color-mix(in srgb, ${primary} 65%, rgb(var(--c-surface))))`, boxShadow: `0 0 10px ${primary}66` }} />
                        </div>
                        {pct === 100 && completion?.certificate_url && (
                            <a
                                href={completion.certificate_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative mt-1 flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl px-4 py-2.5 text-[12.5px] font-bold text-white transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
                                style={{ background: "linear-gradient(135deg, #22c55e 0%, #16a34a 55%, #15803d 100%)", boxShadow: "0 10px 22px -10px rgba(22,163,74,0.75)" }}
                            >
                                {/* premium sheen that sweeps across on hover */}
                                <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
                                {/* award / medal icon */}
                                <svg className="relative h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="8" r="6" />
                                    <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
                                </svg>
                                <span className="relative">Download certificate</span>
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Membership banner — hidden per request.
            {community && (
              <div className="mt-4 lg:hidden">
                <CommunityMiniBanner community={community} totalMembers={totalMembers} className="min-h-[180px]" />
              </div>
            )}
            */}

            {!can_view && (
                <div className="mt-4 flex items-center gap-2 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
                    <Icon d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.85-2.74l-7-12a2 2 0 00-3.7 0l-7 12A2 2 0 005 19z" />
                    {lockMsg(lock_reason, lockCtx)}
                </div>
            )}

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-5">
                {/* ---------- curriculum ---------- */}
                {/* 4 of 12, matching the other template's rail — at 3 columns the
                    module names wrapped to four lines and the progress bar had no
                    room to read as a bar. */}
                <div className="lg:col-span-4 space-y-4">
                    {modules.map((m, mi) => {
                        const mDone = m.lessons.filter((l) => l.completed).length;
                        const mAll = m.lessons.length;
                        const mComplete = mAll > 0 && mDone === mAll;
                        const isOpen = !collapsed[mi];
                        return (
                            <div key={mi} className="overflow-hidden rounded-2xl bg-surface shadow-[0_6px_24px_-12px_rgba(15,23,42,0.18)] ring-1 ring-gray-900/[0.06]">
                                <ModuleHeader
                                    module={m}
                                    index={mi}
                                    isOpen={isOpen}
                                    mDone={mDone}
                                    mAll={mAll}
                                    mComplete={mComplete}
                                    primary={primary}
                                    onOpenFirst={() => {
                                        // The row loads the module's first lesson AND expands it.
                                        // Loading a lesson into the viewer while the module stayed shut
                                        // left the member with no sign of where in the course they had
                                        // just landed.
                                        openModule(mi);
                                        const first = m.lessons[0];
                                        if (first) openLesson(first);
                                    }}
                                    onToggle={() => toggleModule(mi)}
                                    onPlayIntro={() => setIntroModule(m)}
                                />
                                {isOpen && (
                                    <div className="space-y-0.5 border-t border-gray-100 p-1.5">
                                        <ModuleIntroCard module={m} primary={primary} onPlay={() => setIntroModule(m)} />
                                        {m.lessons.length === 0 ? (
                                            <p className="px-4 py-3 text-sm text-gray-400">No lessons.</p>
                                        ) : (
                                            m.lessons.map((l) => {
                                                const LT = lessonTypeMeta(l.lesson_type);
                                                const isActive = active?.lesson_token === l.lesson_token;
                                                const isDrip = l.locked && String(l.lock_reason || "").startsWith("drip");
                                                const locked = !!l.locked;
                                                return (
                                                    <button
                                                        key={l.lesson_token}
                                                        onClick={() => openLesson(l)}
                                                        className={`relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${isActive ? "" : locked ? "bg-gray-50/70 hover:bg-gray-100/70" : "hover:bg-gray-50"}`}
                                                        style={isActive ? { backgroundColor: `${primary}12`, boxShadow: `inset 0 0 0 1px ${primary}29` } : {}}
                                                    >
                                                        {/* Leading badge shows the lesson TYPE (video / content / …),
                                                            not completion — the themed tick on the right marks that. */}
                                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl"
                                                            style={locked
                                                                ? { backgroundColor: "rgb(var(--c-gray-100))", color: "rgb(var(--c-gray-400))" }
                                                                : { backgroundColor: `${LT.color}14`, color: LT.color, boxShadow: `inset 0 0 0 1px ${LT.color}26` }}>
                                                            {locked
                                                                ? <Icon d="M12 11V7a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6a1 1 0 011-1z" className="w-4 h-4" />
                                                                : <Icon d={LT.icon} className="w-4 h-4" />}
                                                        </span>
                                                        <span className="min-w-0 flex-1">
                                                            <Tooltip label={l.title}>
                                                                {/* `block` dropped for the same reason as the module rows above: it overrode line-clamp's display and killed both the clamp and the tooltip. */}
                                                                <span className={`line-clamp-2 break-words text-[13px] leading-snug ${locked ? "text-gray-400" : isActive ? "text-gray-900" : "text-gray-700"}`}>{l.title}</span>
                                                            </Tooltip>
                                                            {isDrip ? (
                                                                <span className="text-[11px] font-medium" style={{ color: primary }}>
                                                                    {l.lock_reason === "drip_prev"
                                                                        ? "Complete previous to unlock"
                                                                        : l.unlock_on ? `Unlocks ${fmtDripDate(l.unlock_on)}` : "Scheduled"}
                                                                </span>
                                                            ) : locked ? (
                                                                <span className="text-[11px] font-medium text-gray-400">Locked</span>
                                                            ) : null}
                                                            {/* Small feature icons under the lesson name: quiz / goal / drip. */}
                                                            {(Number(l.quiz_status) === 1 || l.has_goal || l.drip_type > 0) && (
                                                                <span className="mt-1 flex items-center gap-1">
                                                                    {Number(l.quiz_status) === 1 && (
                                                                        <Tooltip label="Has a quiz" always>
                                                                            <span className="flex h-5 w-5 items-center justify-center rounded-md" style={{ backgroundColor: "#7c3aed1f", color: "#7c3aed" }}>
                                                                                <Icon d="M9 12l2 2 4-4m5 2a9 9 0 11-18 0 9 9 0 0118 0z" className="h-3 w-3" />
                                                                            </span>
                                                                        </Tooltip>
                                                                    )}
                                                                    {l.has_goal && (
                                                                        <Tooltip label="Has a goal" always>
                                                                            <span className="flex h-5 w-5 items-center justify-center rounded-md" style={{ backgroundColor: "#f59e0b1f", color: "#d97706" }}>
                                                                                <Icon d="M3 3v18M3 4.5h12l-2 3.5 2 3.5H3" className="h-3 w-3" />
                                                                            </span>
                                                                        </Tooltip>
                                                                    )}
                                                                    {l.drip_type > 0 && (
                                                                        <Tooltip label="Drip-scheduled" always>
                                                                            <span className="flex h-5 w-5 items-center justify-center rounded-md" style={{ backgroundColor: "#0ea5e91f", color: "#0284c7" }}>
                                                                                <Icon d="M12 3C9 7 6.5 9.8 6.5 13a5.5 5.5 0 0011 0C17.5 9.8 15 7 12 3z" className="h-3 w-3" />
                                                                            </span>
                                                                        </Tooltip>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </span>

                                                        {/* Trailing status only (completed / locked). The quiz · goal ·
                                                            drip markers now render as chips under the lesson name. */}
                                                        <span className="flex shrink-0 items-center gap-1.5">
                                                            {l.completed ? (
                                                                <Tooltip label="Completed" always>
                                                                    <span className="flex items-center"><Icon d="M5 13l4 4L19 7" className="w-4 h-4 text-green-500" /></span>
                                                                </Tooltip>
                                                            ) : locked ? (
                                                                <Tooltip label={isDrip ? (l.lock_reason === "drip_prev" ? "Complete the previous lesson to unlock" : l.unlock_on ? `Unlocks ${fmtDripDate(l.unlock_on)}` : "Unlocks later") : (l.lock_message || "Locked")} always>
                                                                    <span className="flex items-center">
                                                                        {isDrip
                                                                            ? <Icon d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" className="w-4 h-4 text-gray-400" />
                                                                            : <Icon d="M12 11V7a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6a1 1 0 011-1z" className="w-4 h-4 text-gray-400" />}
                                                                    </span>
                                                                </Tooltip>
                                                            ) : null}
                                                        </span>
                                                    </button>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* ---------- viewer ---------- */}
                <div ref={viewerRef} className="lg:col-span-8">
                    {!active ? (
                        <div className="rounded-2xl bg-surface p-10 text-center shadow-[0_6px_24px_-12px_rgba(15,23,42,0.18)] ring-1 ring-gray-900/[0.06]">
                            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: `${primary}14`, color: primary }}>
                                <Icon d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" className="w-6 h-6" />
                            </span>
                            <p className="mt-3 text-sm font-medium text-gray-400">Select a lesson to begin.</p>
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-3xl bg-surface shadow-[0_10px_40px_-16px_rgba(15,23,42,0.18)] ring-1 ring-gray-900/[0.06]">
                            {/* media — shared with the FlexiFunnels layout (mediaNode:
                                file/HLS/embed/DRM), wrapped so it floats into a draggable
                                mini-player when scrolled off screen. */}
                            <FloatingVideo enabled={hasAnyVideo} lessonToken={active?.lesson_token} primary={primary}>{mediaNode}</FloatingVideo>

                            <div className="p-6 sm:p-8">
                                {/* Title and actions share a row only where there's room
                                    for both. Side by side on a phone the shrink-0 action
                                    group kept its full width and the title was squeezed
                                    into a 2-word-wide column, so below sm they stack. */}
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <h2 className="min-w-0 text-xl font-extrabold leading-snug tracking-tight text-gray-900 [overflow-wrap:anywhere] sm:flex-1 sm:text-2xl">{active.title}</h2>
                                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                                        {/* Save / Saved bookmark button — hidden per request.
                                        {!active.locked && (() => {
                                            const isSaved = savedSet.has(String(active.lesson_token));
                                            return (
                                                <Tooltip label={isSaved ? "Saved — click to remove" : "Save this lesson for later"} always>
                                                    <button
                                                        onClick={() => toggleSave(active)}
                                                        aria-label={isSaved ? "Remove bookmark" : "Bookmark this lesson"}
                                                        aria-pressed={isSaved}
                                                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition hover:brightness-105"
                                                        style={isSaved
                                                            ? { backgroundColor: `${primary}14`, color: primary, boxShadow: `inset 0 0 0 1px ${primary}55` }
                                                            : { color: "rgb(var(--c-gray-500))", boxShadow: "inset 0 0 0 1px rgb(var(--c-gray-200))" }}
                                                    >
                                                        <Icon d={isSaved ? "M5 5v14l7-5 7 5V5a2 2 0 00-2-2H7a2 2 0 00-2 2z" : "M5 5a2 2 0 012-2h10a2 2 0 012 2v14l-7-5-7 5V5z"} className="w-3.5 h-3.5" />
                                                        {isSaved ? "Saved" : "Save"}
                                                    </button>
                                                </Tooltip>
                                            );
                                        })()}
                                        */}
                                        {active.locked ? null : active.completed ? (
                                            <span className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/15 ring-1 ring-green-200 dark:ring-green-500/30">
                                                <Icon d="M5 13l4 4L19 7" className="w-3.5 h-3.5" /> {labels("lesson_complete_txt", "Completed")}
                                            </span>
                                        ) : active.hydrated ? (
                                            // Quiz and goal are INDEPENDENT tasks — neither completes the
                                            // lesson. Show "Take Quiz" (if any) for that task, and always a
                                            // "Mark complete" button; the server gates it until the quiz is
                                            // passed and the goal (in its tab) is done.
                                            <div className="flex flex-wrap items-center gap-2">
                                                {Number(active.quiz_status) === 1 && (
                                                    quizIsPassed ? (
                                                        // Passed → read-only status; retake/lock message is popup-only.
                                                        <span
                                                            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/15 ring-1 ring-green-200 dark:ring-green-500/30"
                                                           
                                                        >
                                                            <Icon d="M5 13l4 4L19 7" className="w-3.5 h-3.5" /> Quiz completed
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => setQuizOpen(true)}
                                                            className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold text-white shadow-sm transition hover:brightness-110"
                                                            style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                                                        >
                                                            <Icon d="M9 12l2 2 4-4m5 2a9 9 0 11-18 0 9 9 0 0118 0z" className="w-3.5 h-3.5" />
                                                            {quizBtnLabel}
                                                        </button>
                                                    )
                                                )}
                                                <button
                                                    onClick={() => toggleComplete(active)}
                                                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold text-white shadow-sm transition hover:brightness-110"
                                                    style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                                                >
                                                    <Icon d="M5 13l4 4L19 7" className="w-3.5 h-3.5" /> {labels("mark_complete_txt", "Mark complete")}
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                {active.short_description && <p className="mt-1.5 text-sm text-gray-500">{active.short_description}</p>}

                                {active.locked ? (
                                    // Same precedence as the FlexiFunnels layout: the
                                    // server's own copy (upgrade text / drip countdown)
                                    // wins over the locally-composed fallback.
                                    <p className="mt-4 text-sm text-gray-400">{active.lock_message || lockMsg(active.lock_reason || lock_reason, { ...lockCtx, unlock_on: active.unlock_on })}</p>
                                ) : !active.hydrated ? (
                                    <div className="mt-4 animate-pulse space-y-2">
                                        <div className="h-3 w-full rounded bg-gray-100" />
                                        <div className="h-3 w-11/12 rounded bg-gray-100" />
                                        <div className="h-3 w-4/6 rounded bg-gray-100" />
                                    </div>
                                ) : active.detailed_description ? (
                                    // Clamped, so Notes / Discussion / Resources below
                                    // stay reachable on a long lesson body.
                                    //
                                    // The key is PREFIXED, not the bare lesson token. This
                                    // block and <LessonExtras> below are siblings, and both
                                    // remount per lesson — when both carried the same raw
                                    // token, React saw two children with one key in the same
                                    // list. Its keyed pass maps remaining old children by key,
                                    // so the second entry overwrote the first: the previous
                                    // lesson's body was never scheduled for deletion and its
                                    // DOM node stayed on the page while the new one was
                                    // inserted beside it. Switching lessons stacked every body
                                    // read so far, one under the other.
                                    <ClampedRichText
                                        key={`lesson-body-${active.lesson_token}`}
                                        html={active.detailed_description}
                                        primary={primary}
                                        className="mt-4 text-[15px] leading-relaxed text-gray-700"
                                    />
                                ) : null}

                                {/* notes · discussion · resources · goals — the goal (when the
                                    lesson has one) is now a tab here, next to Resources. */}
                                {!active.locked && member && (
                                    <LessonExtras
                                        key={`lesson-extras-${active.lesson_token}`}
                                        ctx={lessonCtx(active)}
                                        primary={primary}
                                        member={member}
                                        labels={labels}
                                        discussionOn={community?.discussion ?? true}
                                        reviews={courseReviews}
                                        goal={active.goal || null}
                                        goalForm={goalForm}
                                        setGoalForm={setGoalForm}
                                        goalSubmitting={submitting}
                                        onSubmitGoal={() => submitGoal(active)}
                                        onPointsChange={onPointsChange}
                                    />
                                )}

                                {/* prev / next navigation */}
                                {(prevLesson || nextLesson) && (
                                    <div className="mt-6 flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
                                        {prevLesson ? (
                                            <button onClick={() => openLesson(prevLesson)} className="group inline-flex max-w-[46%] items-center gap-2 rounded-xl border border-gray-200 bg-surface/70 px-3 py-1.5 text-left backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-lg sm:px-3.5">
                                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-all duration-300 group-hover:-translate-x-0.5 group-hover:bg-ink group-hover:text-white">
                                                    <Icon d="M15 19l-7-7 7-7" className="h-3.5 w-3.5" />
                                                </span>
                                                <span className="min-w-0"><span className="block text-[9px] font-semibold uppercase tracking-[0.16em] text-gray-400">Previous</span><Tooltip label={prevLesson.title}><span className="hidden truncate text-[13px] font-semibold text-gray-700 group-hover:text-gray-900 sm:block">{prevLesson.title}</span></Tooltip></span>
                                            </button>
                                        ) : <span />}
                                        {nextLesson ? (
                                            <button onClick={() => openLesson(nextLesson)} className="group inline-flex max-w-[46%] items-center gap-2 rounded-xl px-3 py-1.5 text-right transition-all duration-300 hover:-translate-y-0.5 hover:brightness-95 sm:px-4" style={{ backgroundColor: primary, color: "var(--brand-btn-text, rgb(var(--c-surface)))", boxShadow: `0 6px 16px -8px ${primary}59` }}>
                                                <span className="min-w-0"><span className="block text-[9px] font-semibold uppercase tracking-[0.16em] text-white/70">Next</span><Tooltip label={nextLesson.title}><span className="hidden truncate text-[13px] font-bold sm:block">{nextLesson.title}</span></Tooltip></span>
                                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/20 ring-1 ring-inset ring-white/30 transition-transform duration-300 group-hover:translate-x-0.5">
                                                    <Icon d="M9 5l7 7-7 7" className="h-3.5 w-3.5" />
                                                </span>
                                            </button>
                                        ) : <span />}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


/* ================================================================== */
/* Courses list                                                        */
/* ================================================================== */
/**
 * The access badge a course card shows, and its clipped name — shared by every
 * card layout (the grid, Campus's rows, Spotlight's artwork cards) so they can
 * never disagree about what a course's badge says.
 */
export function courseBadge(c, primary, gradeOn) {
    // Level-gated courses show the required gamification level
    // ("Level N", amber) — matching the admin course grid badge.
    const isLevelGated = Number(c.unlock_level) > 0;
    const accessLabel =
        isLevelGated
            ? (c.unlock_level_name || `Level ${c.unlock_level}`)
            : c.lock_reason === "not_released"
                ? "Coming soon"
                : c.lock_reason === "window_ended"
                    ? "Access ended"
                    : c.access_type === 3
                        ? (c.locked ? "Locked" : "Unlocked")
                : c.access_type === 2
                    // A paid course the member already owns should
                    // say so — showing "Paid" back to someone who
                    // has paid it reads as if they still need to buy.
                    //
                    // The badge names the grade, never the amount:
                    // the price belongs on the sales page, where it
                    // sits next to what the money buys. A bare
                    // number on a card is a figure without a
                    // currency, a plan or an offer attached to it.
                    ? (c.purchased ? "Purchased" : "Paid")
                    : ACCESS[c.access_type]?.label;
    // A LEVEL badge takes the membership's brand colour: it names
    // something the learner earns inside this membership, so it
    // belongs to the theme rather than standing outside it — and
    // it matches the gate panel the badge leads to.
    //
    // "Coming soon" stays amber (it will open), "Access ended" is
    // grey (it will not) — a learner should be able to tell the
    // two apart without reading the message. Amber now means
    // "wait" alone, which is one fewer thing the colour has to say.
    const accessColor = isLevelGated
        ? primary
        : c.lock_reason === "not_released"
            ? "#d97706"
            : c.lock_reason === "window_ended"
                ? "#6b7280"
                : c.access_type === 2
                    // Owned reads as a positive state, so it takes the
                    // same green as Free rather than the red price tag.
                    ? (c.purchased ? "#16a34a" : "#dc2626")
                    : (c.access_type === 3 && c.locked) ? "#6b7280" : "#16a34a";
    /*
     * Membership Grade toggle (admin: Settings → Features).
     *
     * This one pill says several different things. Only the
     * Free / Paid grade is a "grade" and only that is hidden:
     * a level name, "Coming soon", "Access ended" and the
     * locked/unlocked state all describe whether the course
     * can be opened at all, so hiding them would strip the
     * only on-card explanation of why a course won't open.
     */
    //
    // "Purchased" is likewise kept: it states what the member
    // owns, not what the course costs. Hiding the grade should
    // not take away the confirmation that they already bought
    // this one.
    const isPurchasedBadge = Number(c.access_type) === 2 && !!c.purchased;
    const isGradeBadge =
        !isLevelGated &&
        c.lock_reason !== "not_released" &&
        c.lock_reason !== "window_ended" &&
        c.access_type !== 3 &&
        !isPurchasedBadge;
    const showAccessBadge = gradeOn || !isGradeBadge;
    // Course name: single line, capped at 40 chars with an ellipsis.
    // The full name stays reachable via the tooltip when it's cut.
    const nameClipped = (c.name || "").length > 40;
    const nameDisplay = nameClipped ? `${c.name.slice(0, 40).trimEnd()}…` : c.name;
    return { isLevelGated, accessLabel, accessColor, showAccessBadge, nameClipped, nameDisplay };
}

export default function CoursesView({ token, groupToken, primary, member, group, community, totalMembers, template = "classic", headerSearchSlot = null, onInstructor = null, pointsCard = null, onPointsChange = null,
}) {
  // Studio → roomier "library" grid (wider cards, taller covers, larger titles);
  const isLibrary = template === "studio";
  // Catalog previously used a denser "compact" grid; it now shares the Classic
  // course-card design (2-up, taller covers, with description) per request.
  const isCompact = false;
  // Campus → progress-forward horizontal "row" cards (thumbnail left, content right).
  const isRow = template === "campus";
  // Creator → big single-column "feature" cards (large cover, bold title).
  const isFeature = template === "creator";
  // Spotlight → the standard card in a wide three-up grid, no rail.
  const isSpotlight = template === "spotlight";
    const [courses, setCourses] = useState(null);
    // The course whose offer chooser is open, when a locked course turns out to be
    // sold by more than one product. Null the rest of the time.
    const [offerCourse, setOfferCourse] = useState(null);
    // Course grid pagination — COURSES_PER_PAGE cards per page, fetched a page at
    // a time so a large catalogue is never shipped whole.
    const [coursePage, setCoursePage] = useState(1);
    // From the API. Undefined against an API that does not page, in which case
    // the count below falls back to what actually arrived.
    const [courseTotalPages, setCourseTotalPages] = useState(undefined);
    // The FULL count, for the "N courses" header. Since the response is now one
    // page, courses.length is the page size — using it there would tell a member
    // with 40 courses that they have 6.
    const [courseTotal, setCourseTotal] = useState(undefined);
    // Membership Grade feature flag, from the courselist response. Starts ON so
    // the badge never flickers off during the first load of a membership that
    // has it enabled.
    const [gradeOn, setGradeOn] = useState(true);
    // Course Rating feature flag, from the same response. Starts ON for the same
    // reason: the rating must not flicker off during the first load of a
    // membership that shows it.
    const [ratingOn, setRatingOn] = useState(true);
    const [loading, setLoading] = useState(true);
    // Right-rail standing comes from the PARENT, which already holds it.
    //
    // Every caller of this view (the Shell, CommunityFeed, MembershipHome,
    // GroupCollection) mounts its own useMyPoints, and this mounted a second
    // one — two membership-leaderboard requests per page for identical data.
    // The parent passes the rendered card down instead, so the standing is
    // fetched once and both surfaces show the same numbers.
    // Deep-link: ?course=<token> opens that course's lessons straight away (used by
    // the admin "Visit Page" button). Course / lesson live in the URL so the page is
    // shareable + survives refresh:
    //   ?course=<token>[&view=lessons][&lesson=…] -> inside the lessons (player)
    const [sp, setSp] = useSearchParams();
    const open = sp.get("course") || null; // course_token
    const lessonToken = sp.get("lesson") || null;

    // Update course/lesson params while preserving gid / page / ref etc.
    const setCourseParams = (changes, opts) => {
        setSp((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(changes).forEach(([k, v]) => {
                if (v == null || v === "") next.delete(k);
                else next.set(k, v);
            });
            return next;
        }, opts);
    };

    const load = useCallback(async () => {
        // Wait for the fields this actually SENDS.
        //
        // Coming from login the member object arrives in pieces, and firing early
        // posted courselist with funnel_id/jwt undefined — a request that can only
        // fail. Worse, fetchCourses caches by URL+body+token for 15s, so that bad
        // {funnel_id: undefined} answer was then served to every retry inside the
        // window. The member saw a spinner that never resolved, and only a hard
        // refresh (which reads a complete member out of localStorage before the
        // first render) fixed it.
        // No member at all — a guest browsing the catalogue. There is nothing to
        // fetch and nothing to wait for, so settle into the empty state rather
        // than spinning forever (`loading` starts true).
        if (!member) {
            setCourses([]);
            setLoading(false);
            return;
        }

        // A member exists but the fields above have not landed yet. This one IS
        // transient, so hold the spinner and let the re-run (see the deps) do the
        // fetch once they arrive.
        if (!member.funnel_id || !member.member_token) {
            return;
        }

        setLoading(true);
        try {
            const res = { data: await fetchCourses({
                funnelId: member?.funnel_id,
                memberId: member?.community_member_id,
                jwt: member?.member_token,
                page: coursePage,
                perPage: COURSES_PER_PAGE,
            }) };
            // A page past the end answers empty — which happens when the
            // catalogue shrinks under a member sitting on a later page (access
            // revoked, a course unpublished). Snap back to the last real page;
            // coursePage is in this effect's deps, so that refetches. Without
            // it the grid reads "No courses" while the control still highlights
            // a page that no longer exists.
            const pages = res.data?.totalPages;
            if (pages !== undefined && coursePage > pages) {
                setCoursePage(Math.max(1, pages));
                return;
            }
            setCourses(res.data?.error === 0 ? res.data.courses : []);
            setCourseTotalPages(pages);
            setCourseTotal(res.data?.total);
            // Membership Grade toggle (admin: Settings → Features). Only an
            // explicit 0 hides the badge — see fetchCourses for why a missing
            // flag has to mean "shown".
            setGradeOn(res.data?.grade_mode !== 0);
            // Course Rating toggle (admin: Settings → Features). Same rule as
            // the badge above — only an explicit 0 hides it.
            setRatingOn(res.data?.rating_mode !== 0);
        } catch {
            setCourses([]);
        } finally {
            setLoading(false);
        }
    // Depend on what the call USES. `auth_token` was listed while the request
    // sends `member_token`, and `funnel_id` was not listed at all — so when those
    // two arrived after login this never re-ran, and the guard above would have
    // left the page blank forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // coursePage belongs here: the response IS the page, so changing it has to
    // refetch. Without it the click would move the control and leave the grid
    // showing the previous page's courses.
    }, [token, groupToken, member?.funnel_id, member?.member_token, member?.community_member_id, coursePage]);
    useEffect(() => { load(); }, [load]);

    const closeCourse = () => { setCourseParams({ course: null, view: null, lesson: null }); load(); };

    // Open a course card. A course the member doesn't hold yet AND that has a
    // published sales page (resolved by the bridge from the course's product)
    // sends them there to get it; every other case — level-gated and time-gated
    // included — opens the course, which shows its own lock screen when it has to.
    //
    // That covers a PAID course they haven't bought, and now a FREE course the
    // creator put behind a product: a free course grants no access row of its own,
    // so the $0 order on that checkout is the only thing that can enrol them.
    // Without this they landed on the player and got "Access Forbidden!" with
    // nowhere to go. A free course with no such product still has no sales_url and
    // opens for everyone exactly as before.
    //
    // access_type 3 is excluded on purpose: that is the expired / level-gated
    // badge state, and those courses open so CourseLockOverlay can explain
    // themselves (expiry date, level name, the creator's own upgrade CTA).
    const openCourse = (c, view = null) => {
        if (c.access_type !== 3 && !c.purchased) {
            // Sold by more than one product — a Starter, a Pro and an All Access can
            // all carry the same course in their bundle. `sales_url` names only the
            // first of them, so sending every buyer there hid the other offers
            // completely. Ask which one they want instead.
            if ((c.offers?.length || 0) > 1) {
                setOfferCourse(c);
                return;
            }
            if (c.sales_url) {
                window.open(withReferral(c.sales_url), "_blank", "noopener,noreferrer");
                return;
            }
        }
        // Nothing is being chosen any more — drop the chooser's course so it cannot
        // reappear over the grid when the member comes back from the player.
        setOfferCourse(null);
        // A LEVEL-gated course the member hasn't reached DOES open, for the same
        // reason time-gated ones do: the course page covers itself with the lock
        // screen, which names the level, its point cost and how far off they are.
        // This used to stop at a toast, which said the same thing in a message
        // that disappears — and left the member on the grid with nothing to read
        // twice. The lessons stay locked server-side either way.
        //
        // Time-gated courses (not_released / window_ended) deliberately DO open:
        // CoursePlayer covers itself with CourseLockOverlay, which has room for
        // the date and the creator's upgrade CTA. A toast here could be missed and
        // gave the creator's copy nowhere to appear.
        setCourseParams({ course: c.course_token, view, lesson: null });
    };

    // The course + lesson search. Rendered inline in the card header on most
    // templates; on Catalog it's portalled up into the page header's slot — and
    // kept mounted on the course landing / lesson-player pages too, so the header
    // search stays available while a course is open.
    // The response IS the page — no slicing here. The API reports how many pages
    // there are; without that (an API that does not page) fall back to what
    // arrived, which yields a single page and hides the control.
    const totalCoursePages = Math.max(
        1,
        courseTotalPages ?? Math.ceil((courses?.length || 0) / COURSES_PER_PAGE)
    );
    const safeCoursePage = Math.min(coursePage, totalCoursePages);
    const pagedCourses = courses || [];

    const courseSearchEl = courses && courses.length > 0 ? (
        <CourseSearch
            courses={courses}
            primary={primary}
            member={member}
            token={token}
            variant={headerSearchSlot ? "header" : "card"}
            // A course result opens its lessons directly (no landing screen) —
            // routed through openCourse so a paid, unbought course still goes to
            // its sales page rather than a locked player.
            onPickCourse={(ct) => { const c = courses.find((x) => x.course_token === ct); if (c) openCourse(c, "lessons"); else setCourseParams({ course: ct, view: "lessons", lesson: null }); }}
            // A lesson result deep-links into the player at that lesson.
            onPickLesson={(ct, lt) => setCourseParams({ course: ct, view: "lessons", lesson: lt })}
        />
    ) : null;
    const headerPortal = headerSearchSlot && courseSearchEl ? createPortal(courseSearchEl, headerSearchSlot) : null;

    // "Choose your plan" for a locked course several products sell. Rendered
    // alongside both grid layouts (Spotlight and the rail templates) so the
    // chooser is the same wherever the card was clicked.
    const offerModal = offerCourse ? (
        <CourseOfferModal
            courseName={offerCourse.title}
            offers={offerCourse.offers}
            primary={primary}
            onClose={() => setOfferCourse(null)}
        />
    ) : null;

    if (open) {
        // Clicking a course opens the lesson player directly — the intermediate
        // "Start course" landing page was removed. Back returns to the course list.
        return (
            <>
            {headerPortal}
            {/* Course-scoped Live Mock Tests (admin "Show on Courses → this course"). */}
            {member ? (
              <div className="mx-auto w-full max-w-5xl px-4 pt-6">
                <MockTestLauncher
                  page="courses"
                  courseId={parseInt(open, 10) || null}
                  funnelId={member.funnel_id}
                  memberId={member.community_member_id}
                  jwt={member.member_token}
                  primary={primary}
                />
              </div>
            ) : null}
            <CoursePlayer
                token={token}
                courseToken={open}
                primary={primary}
                member={member}
                community={community}
                totalMembers={totalMembers}
                // The course-detail endpoint doesn't return rating / access /
                // price / description — the list does. Hand the list row over so
                // the banner can show them.
                courseMeta={courses?.find((x) => x.course_token === open) || null}
                // Same flag as the catalogue badges: without it the grade would
                // still be one click away on the course header, so turning the
                // feature off would only half-work.
                gradeOn={gradeOn}
                ratingOn={ratingOn}
                initialLesson={lessonToken}
                onLessonChange={(lt) => setCourseParams({ lesson: lt }, { replace: true })}
                onPointsChange={onPointsChange}
                onBack={closeCourse}
                template={template}
                onInstructor={onInstructor}
            />
            </>
        );
    }

    // The course cards, built once and shared by the standard layout below and
    // Spotlight's page — the same card (cover or the default course image,
    // badges, meta row, progress), whichever frame it sits in.
    // Named, because the loading skeleton lays itself out with the SAME class.
    // Each template catalogues differently (two-up, three-up, one wide column,
    // a horizontal stack), and a placeholder grid in other columns re-flows the
    // page the moment the real cards arrive — the jump it exists to prevent.
    const courseGridClass = isRow ? "flex flex-col gap-4" : isSpotlight ? SPOTLIGHT_GRID : isFeature ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" : isLibrary ? "grid grid-cols-1 md:grid-cols-2 gap-6" : isCompact ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" : "grid grid-cols-1 sm:grid-cols-2 gap-5";

    const courseGrid = (
        <div className={courseGridClass}>
            {pagedCourses.map((c) => {
                const { isLevelGated, accessLabel, accessColor, showAccessBadge, nameClipped, nameDisplay } = courseBadge(c, primary, gradeOn);

                // Campus "row" card: a clean cover (no stacked badges) with the
                // price, rating and instructor moved into the content for a calmer,
                // more premium list card.
                if (isRow) {
                    return (
                        <button
                            key={c.course_token}
                            onClick={() => openCourse(c, null)}
                            // Vertical card on phones (full-width cover on top) → horizontal
                            // row from sm up, so mobile content never gets cramped.
                            className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-surface text-left shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-lg sm:h-48 sm:flex-row"
                        >
                            {/* 16:9 like every other template's cover on desktop (192px tall row →
                                340px wide), not the old square w-48 that cropped it. */}
                            <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-ink sm:h-full sm:w-64 lg:w-[21.25rem]">
                                <CourseCover src={c.image} />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                            </div>

                            <div className="flex min-w-0 flex-1 flex-col p-4">
                                {/* Title + access badge. Hidden only when it is
                                    showing a Free/Paid grade and Membership
                                    Grade is off — same rule as the grid card. */}
                                <div className="flex items-start gap-2">
                                    <Tooltip label={c.name} always={nameClipped}>
                                        <h3 className="min-w-0 flex-1 truncate text-[15px] font-bold text-gray-900">{nameDisplay}</h3>
                                    </Tooltip>
                                    {showAccessBadge && (
                                        <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" style={{ backgroundColor: accessColor }}>
                                            {accessLabel}
                                        </span>
                                    )}
                                </div>

                                {/* Instructor + rating */}
                                <div className="mt-1.5 flex items-center gap-2 text-[12px]">
                                    {instructorsOf(c).length ? (
                                        <InstructorChips
                                            list={instructorsOf(c)}
                                            onInstructor={onInstructor}
                                            insideButton
                                            primary={primary}
                                            prefixInLabel={false}
                                            sizeClass="h-[18px] w-[18px] shrink-0"
                                            textClass="text-[9px]"
                                            initialClass="text-white"
                                            initialStyle={{ backgroundColor: primary }}
                                            labelClass="truncate font-medium"
                                            className="inline-flex min-w-0 items-center gap-1.5 text-gray-600"
                                        />
                                    ) : <span />}
                                    {ratingOn && (
                                    <span className="ml-auto inline-flex shrink-0 items-center gap-1 font-bold text-amber-500">
                                        <svg className="h-3.5 w-3.5 fill-amber-400" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                                        {Number(c.star_rating || 0).toFixed(1)}
                                    </span>
                                    )}
                                </div>

                                {/* Description — two lines then ellipsis (tooltip on hover) */}
                                {c.short_description && (
                                    <Tooltip label={c.short_description}>
                                        <p className="mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-gray-500">{c.short_description}</p>
                                    </Tooltip>
                                )}

                                {/* Bottom — modules + lessons + created date, then the progress bar
                                    (always shown once the course has lessons; hidden for a
                                    0-lesson course). */}
                                <div className="mt-auto pt-2.5">
                                    <div className="flex min-w-0 items-center gap-x-3 text-[12px] font-medium text-gray-500">
                                        {c.total_modules > 0 && (
                                            <span className="inline-flex shrink-0 items-center gap-1.5">
                                                <Icon d={MODULE_ICON} className="h-4 w-4 text-gray-400" />
                                                {c.total_modules} {c.total_modules === 1 ? "Module" : "Modules"}
                                            </span>
                                        )}
                                        <span className="inline-flex shrink-0 items-center gap-1.5">
                                            <Icon d={LESSON_ICON} className="h-4 w-4 text-gray-400" />
                                            {c.total_lessons} {c.total_lessons === 1 ? "Lesson" : "Lessons"}
                                        </span>
                                        {formatCourseDate(c.created_at) && (
                                            <span className="inline-flex min-w-0 items-center gap-1.5">
                                                <Icon d={CALENDAR_ICON} className="h-4 w-4 shrink-0 text-gray-400" />
                                                <span className="truncate">{formatCourseDate(c.created_at)}</span>
                                            </span>
                                        )}
                                    </div>
                                    {c.total_lessons > 0 && (
                                        <div className="mt-2">
                                            <div className="mb-1 flex items-center justify-between text-[11px] font-bold">
                                                <span style={{ color: c.completed ? "#16a34a" : primary }}>
                                                    {c.completed ? "Completed ✓" : c.progress_pct > 0 ? `${c.progress_pct}% complete` : "Not started"}
                                                </span>
                                                <span className="text-gray-400">{c.completed_lessons || 0}/{c.total_lessons}</span>
                                            </div>
                                            {/* Premium track: inset ring for depth, gradient fill with a soft glow. */}
                                            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 ring-1 ring-inset ring-gray-200/70">
                                                <div
                                                    className="h-full rounded-full transition-[width] duration-500 ease-out"
                                                    style={{
                                                        width: `${c.completed ? 100 : c.progress_pct > 0 ? Math.max(c.progress_pct, 6) : 0}%`,
                                                        background: c.completed
                                                            ? "linear-gradient(90deg, #16a34a 0%, #22c55e 100%)"
                                                            : `linear-gradient(90deg, ${primary} 0%, var(--brand-secondary, ${primary}cc) 100%)`,
                                                        boxShadow: c.completed || c.progress_pct > 0 ? `0 1px 5px ${primary}66` : "none",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </button>
                    );
                }

                // Image + its overlays, shared by the grid/feature/library layouts.
                const cardImage = (
                    <>
                        <CourseCover src={c.image} />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent" />

                        {/* Access badge — solid pill matching the group card's
                            FREE badge (white text on a colour). Hidden only
                            when it is showing a Free/Paid grade and the creator
                            turned off Membership Grade; a level, drip or locked
                            state still shows. */}
                        {showAccessBadge && (
                        <span className="absolute left-2.5 top-2.5 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm" style={{ backgroundColor: accessColor }}>
                            {/* A level course the member hasn't reached can't be
                                opened at all, so it gets a padlock instead of the
                                gift icon — the badge already names the level.
                                Once they HAVE reached it the padlock swings open,
                                so the badge reads as earned access rather than a
                                gift, which is what a free course means. */}
                            {c.level_locked ? (
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 11V7a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6a1 1 0 011-1z" transform="translate(4)" />
                                </svg>
                            ) : isLevelGated ? (
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 019 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                </svg>
                            ) : c.access_type !== 2 && !(c.access_type === 3 && c.locked) && (
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M3 8h18v4H3V8zm9 0S10 2 7 4s5 4 5 4zm0 0s2-6 5-4-5 4-5 4z" />
                                </svg>
                            )}
                            {accessLabel}
                        </span>
                        )}

                        {/* Rating + lessons chips — shown for every course. We
                            don't overlay a lock icon for paid / level-gated
                            courses; the access badge above + the course landing
                            page already explain how to get access. */}
                        {ratingOn && (
                        <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 rounded-full bg-surface/90 px-2 py-0.5 text-[11px] font-bold text-amber-500 shadow-sm backdrop-blur">
                            <svg className="h-3 w-3 fill-amber-400" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                            {Number(c.star_rating || 0).toFixed(1)}
                        </span>
                        )}

                        {/* Instructor chip — the course's assigned instructor(s). */}
                        <InstructorChips
                            list={instructorsOf(c)}
                            onInstructor={onInstructor}
                            insideButton
                            primary={primary}
                            prefixInLabel={false}
                            sizeClass="h-5 w-5 shrink-0"
                            textClass="text-[10px]"
                            initialClass="text-white"
                            initialStyle={{ backgroundColor: primary }}
                            className={`absolute bottom-2.5 right-2.5 inline-flex max-w-[60%] items-center gap-1.5 rounded-full bg-black/45 py-0.5 pl-0.5 text-[11px] font-semibold text-white backdrop-blur-sm ${
                                instructorsOf(c).length > 1 ? "pr-1" : "pr-2.5"
                            }`}
                        />
                    </>
                );
                return (
                <button
                    key={c.course_token}
                    // Clicking a course opens its lessons directly (the
                    // intermediate landing / "Start course" screen was removed).
                    onClick={() => openCourse(c, "lessons")}
                    className={`group overflow-hidden border border-gray-200 bg-surface text-left shadow-sm transition duration-200 hover:border-gray-300 ${isRow ? "flex h-40 rounded-2xl hover:shadow-md sm:h-44" : "flex flex-col rounded-xl hover:-translate-y-1 hover:shadow-xl"}`}
                >
                    {isRow ? (
                        // Fixed-height image column fills the card so every row is the
                        // same height regardless of description length / progress.
                        <div className="relative h-full w-28 shrink-0 overflow-hidden bg-ink sm:w-48">
                            {cardImage}
                        </div>
                    ) : (
                        // Inset the image with a small padded frame (rounded), like
                        // the reference course card, instead of a full-bleed top.
                        //
                        // The frame is 16:9 — the shape the admin asks covers to be
                        // (1280×720) — not a fixed height. A fixed h-40/h-48 turned
                        // into a thin strip on the wide two-up cards (Classic,
                        // Studio, Catalog) and cropped the top and bottom off the
                        // cover; at 16:9 the whole image shows at every width.
                        <div className="p-2 pb-0">
                            <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-ink">
                                {cardImage}
                            </div>
                        </div>
                    )}

                    <div className={`flex min-w-0 flex-1 flex-col ${isLibrary ? "p-5" : isRow ? "p-3 sm:p-4" : "p-4"}`}>
                        {/* The card clamps both of these; the tooltip is how the
                            full name / description stay reachable. It self-suppresses
                            when the text already fits. */}
                        <Tooltip label={c.name} always={nameClipped}>
                            <h3 className={`truncate font-bold text-gray-900 ${isLibrary ? "text-lg" : "text-[15px]"}`}>{nameDisplay}</h3>
                        </Tooltip>
                        {c.short_description && !isCompact && (
                            <Tooltip label={c.short_description}>
                                {/* Two lines then ellipsis; the Tooltip reveals the full
                                    text on hover/tap (it self-suppresses when it fits). */}
                                <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-gray-500">{c.short_description}</p>
                            </Tooltip>
                        )}
                        <div className={`mt-auto ${isRow ? "pt-2.5" : "pt-4"}`}>
                            {/* Module + lesson count and the created date — meta row.
                                Row cards keep it to one line (the counts are shrink-0, the
                                date truncates) so it never wraps out of the fixed height. */}
                            <div className={`flex items-center gap-x-4 text-[12px] font-medium text-gray-500 ${isRow ? "min-w-0 flex-nowrap gap-x-3" : "flex-wrap gap-y-1.5"}`}>
                                {c.total_modules > 0 && (
                                    <span className="inline-flex shrink-0 items-center gap-1.5">
                                        <Icon d={MODULE_ICON} className="h-4 w-4 text-gray-400" />
                                        {c.total_modules} {c.total_modules === 1 ? "Module" : "Modules"}
                                    </span>
                                )}
                                <span className="inline-flex shrink-0 items-center gap-1.5">
                                    <Icon d={LESSON_ICON} className="h-4 w-4 text-gray-400" />
                                    {c.total_lessons} {c.total_lessons === 1 ? "Lesson" : "Lessons"}
                                </span>
                                {formatCourseDate(c.created_at) && (
                                    <span className="inline-flex min-w-0 items-center gap-1.5">
                                        <Icon d={CALENDAR_ICON} className="h-4 w-4 shrink-0 text-gray-400" />
                                        <span className="truncate">{formatCourseDate(c.created_at)}</span>
                                    </span>
                                )}
                            </div>
                            {/* Progress — shown whenever the course has lessons (hidden for a
                                0-lesson course); "Not started" at 0%. */}
                            {c.total_lessons > 0 && (
                                <div className="mt-3">
                                    <div className="mb-1 flex items-center justify-between text-[11px] font-bold">
                                        <span style={{ color: c.completed ? "#16a34a" : primary }}>
                                            {c.completed ? "Completed ✓" : c.progress_pct > 0 ? `${c.progress_pct}% complete` : "Not started"}
                                        </span>
                                        <span className="text-gray-400">{c.completed_lessons || 0}/{c.total_lessons} lessons</span>
                                    </div>
                                    {/* Premium track: inset ring for depth, gradient fill with a soft glow. */}
                                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 ring-1 ring-inset ring-gray-200/70">
                                        <div
                                            className="h-full rounded-full transition-[width] duration-500 ease-out"
                                            style={{
                                                width: `${c.completed ? 100 : c.progress_pct > 0 ? Math.max(c.progress_pct, 6) : 0}%`,
                                                background: c.completed
                                                    ? "linear-gradient(90deg, #16a34a 0%, #22c55e 100%)"
                                                    : `linear-gradient(90deg, ${primary} 0%, var(--brand-secondary, ${primary}cc) 100%)`,
                                                boxShadow: c.completed || c.progress_pct > 0 ? `0 1px 5px ${primary}66` : "none",
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </button>
                );
            })}
        </div>
    );

    // Spotlight: its own catalogue page (title + search, then the Explore grid,
    // no rail) around the same cards as below.
    if (template === "spotlight") {
        return (
            <>
            {headerPortal}
            {offerModal}
            <SpotlightCourses
                courses={pagedCourses}
                loading={loading}
                total={courseTotal}
                // Placeholder count tracks the real page size, so the grid does
                // not reflow when the cards land.
                skeletonCount={COURSES_PER_PAGE}
                communityName={community?.name}
                search={courseSearchEl}
                grid={courseGrid}
                topSlot={
                    <>
                        <UpcomingEventBanner token={token} placement="courses" primary={primary} className="mb-6" />
                        {member ? (
                            <MockTestLauncher page="courses" funnelId={member.funnel_id} memberId={member.community_member_id} jwt={member.member_token} primary={primary} />
                        ) : null}
                    </>
                }
                // The same membership rail the other templates put beside the
                // catalogue: community card, My Points / levels, banner ads.
                rail={
                    <>
                        {community && (
                            <CommunityMiniBanner
                                community={community}
                                totalMembers={totalMembers}
                                totalCourses={courseTotal ?? (courses ? courses.length : undefined)}
                                className="h-44"
                            />
                        )}
                        {pointsCard}
                        <InlineBanner token={token} groupToken={groupToken} placement="courses" wrapperClass="space-y-3" />
                    </>
                }
                empty={<EmptyState art="courses" accent={primary} title="No courses yet" message="Courses are the lessons and modules inside this membership. As soon as the first one is published it appears here, with your progress alongside it." />}
                pagination={
                    <Pagination
                        page={safeCoursePage}
                        totalPages={totalCoursePages}
                        primary={primary}
                        onChange={(p) => {
                            setCoursePage(p);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                    />
                }
            />
            </>
        );
    }

    return (
        // Dashboard hero banner across the top (Brand settings → Dashboard
        // Banner), then the catalogue beneath.
        // Same shape as Events: content left, membership rail right (sticky on
        // desktop, stacked first on mobile).
        <div className="space-y-6">
        {headerPortal}
        {offerModal}
        {/* <DashboardBanner community={community} token={token} memberId={member?.community_member_id} primary={primary} /> */}
        <div className="tpl-rail-grid">
          <aside className="space-y-4 order-2 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1 scrollbar-hide">
            {/* Rendered here rather than passed in: the banner's course count is
                only known once this view has loaded the list. */}
            {community && (
              <CommunityMiniBanner
                community={community}
                totalMembers={totalMembers}
                totalCourses={courseTotal ?? (courses ? courses.length : undefined)}
                className="h-44"
              />
            )}
            {pointsCard}
            {/* Banner ads for the Courses page — under the points card. */}
            <InlineBanner token={token} groupToken={groupToken} placement="courses" wrapperClass="space-y-3" />
          </aside>
          <div className="order-1 min-w-0">
            {/* Events targeted to the Courses page. */}
            <UpcomingEventBanner token={token} placement="courses" primary={primary} className="mb-6" />
            {/* Live Mock Tests — shows only if a published test targets this page. */}
            {member ? (
              <MockTestLauncher
                page="courses"
                funnelId={member.funnel_id}
                memberId={member.community_member_id}
                jwt={member.member_token}
                primary={primary}
              />
            ) : null}
            <div className="bg-surface rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <SectionHeader
                primary={primary}
                iconPath="M12 6.5C10.5 5.5 8.3 5 6 5a8 8 0 00-2 .26v12A8 8 0 016 17c2.3 0 4.5.5 6 1.5m0-12c1.5-1 3.7-1.5 6-1.5a8 8 0 012 .26v12A8 8 0 0018 17c-2.3 0-4.5.5-6 1.5m0-12V18.5"
                title="Courses"
                // Always the membership: courses are funnel-scoped (fetchCourses
                // filters on funnel_id alone), so the list is the same whichever
                // group happens to be in context. "In this group" was inherited
                // from the group-first app and promised a filter that never existed.
                subtitle="Courses in this membership"
                // Catalog moves the search up into the header bar (headerSearchSlot);
                // every other template keeps it inline in this card header.
                action={headerSearchSlot ? null : courseSearchEl}
            />
            <div className="p-5 sm:p-6">
            {loading ? (
                <CardGridSkeleton count={COURSES_PER_PAGE} grid={courseGridClass} row={isRow} />
            ) : !courses || courses.length === 0 ? (
                <EmptyState art="courses" accent={primary} title="No courses yet" message="Courses are the lessons and modules inside this membership. As soon as the first one is published it appears here, with your progress alongside it." />
            ) : (
                courseGrid
            )}
            {/* Only renders when there is more than one page (see Pagination). */}
            <Pagination
                page={safeCoursePage}
                totalPages={totalCoursePages}
                primary={primary}
                onChange={(p) => {
                    setCoursePage(p);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                }}
            />
            </div>
            </div>
          </div>
        </div>
        </div>
    );
}

/* ================================================================== */
/* Lesson extras — Notes · Discussion · Downloads · Quiz              */
/* Tabbed panel under the lesson body. Remounted per lesson (key) so  */
/* each tab fetches fresh data on mount.                              */
/* ================================================================== */
function Avatar({ src, name, size = 32 }) {
    const [err, setErr] = useState(false);
    const initial = (String(name || "?").trim().charAt(0) || "?").toUpperCase();
    const s = { width: size, height: size };
    if (src && !err) {
        return <img src={src} alt="" style={s} className="shrink-0 rounded-full object-cover" onError={() => setErr(true)} />;
    }
    return (
        <span style={s} className="shrink-0 inline-flex items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-500">{initial}</span>
    );
}

function LessonExtras({ ctx, primary, member, labels, discussionOn = true, reviews = null, goal = null, goalForm, setGoalForm, goalSubmitting, onSubmitGoal, onPointsChange }) {
    const L = labels || ((k, fb) => fb);
    // NOTE: the Quiz is no longer an inline tab — it opens in a dedicated modal
    // from the lesson's "Take Quiz" button (see QuizModal). The Goal, when the
    // lesson has one, IS a tab here, sitting right after Resources.
    const tabs = [
        { key: "notes", label: L("notes_txt", "Notes"), icon: "M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" },
        // Lesson discussion is gated by the admin's "Membership Discussion"
        // feature toggle; when it's off the tab disappears entirely.
        ...(discussionOn ? [{ key: "discussion", label: L("discussion_txt", "Discussion"), icon: "M8 10h8M8 14h5m7-2a8 8 0 01-11.6 7.13L3 20l1.87-5.4A8 8 0 1120 12z" }] : []),
        { key: "downloads", label: L("resources_txt", "Resources"), icon: "M12 3v10m0 0l3.5-3.5M12 13l-3.5-3.5M5 17v2a2 2 0 002 2h10a2 2 0 002-2v-2" },
        // Reviews: the creator's switch AND something approved to show. An empty
        // Reviews tab on a paid course reads as neglect, so the tab arrives with
        // the first published review rather than before it.
        ...(reviews?.enabled && reviews.reviews.length
            ? [{ key: "reviews", label: "Reviews", icon: "M11.48 3.5a.56.56 0 011.04 0l2.13 5.11c.08.2.27.34.49.36l5.51.44c.52.04.73.7.34 1.04l-4.2 3.6a.56.56 0 00-.19.57l1.28 5.37c.12.51-.43.91-.88.64l-4.72-2.88a.56.56 0 00-.58 0l-4.72 2.88c-.45.27-1-.13-.88-.64l1.28-5.37a.56.56 0 00-.19-.57l-4.2-3.6c-.39-.34-.18-1 .34-1.04l5.51-.44a.56.56 0 00.49-.36l2.13-5.11z" }]
            : []),
        // Goals tab appears ONLY when this lesson has a goal.
        ...(goal ? [{ key: "goals", label: "Goals", icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" }] : []),
    ];
    // If the lesson has a goal, open on it so the member sees what's required.
    // A notification / email link (&tab=discussion) opens straight on Discussion.
    const [tabSp] = useSearchParams();
    const [tab, setTab] = useState(() =>
        tabSp.get("tab") === "discussion" && tabs.some((t) => t.key === "discussion") ? "discussion" : goal ? "goals" : "notes");
    // A notification clicked while this lesson is already open only changes the
    // URL, so follow it here too: switch to Discussion and remount it (linkKey)
    // so it reloads and opens the linked post.
    const linkKey = tabSp.get("tab") === "discussion" ? `${tabSp.get("post") || ""}:${tabSp.get("comment") || ""}` : "";
    useEffect(() => {
        if (linkKey && tabs.some((t) => t.key === "discussion")) setTab("discussion");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkKey]);
    return (
        <div className="mt-6 border-t border-gray-100 pt-5 sm:pt-6">
            {/* Premium segmented control: soft container, active tab lifts to a
                white card in the brand color. */}
            <div className="flex max-w-full items-center gap-1 overflow-x-auto rounded-full bg-gray-100/80 p-1 ring-1 ring-gray-900/[0.04] scrollbar-hide">
                {tabs.map((t) => {
                    const on = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] font-bold transition ${on ? "bg-surface shadow-sm ring-1 ring-gray-900/[0.06]" : "text-gray-500 hover:text-gray-700"}`}
                            style={on ? { color: primary } : undefined}
                        >
                            <Icon d={t.icon} className="h-4 w-4 shrink-0" /> {t.label}
                        </button>
                    );
                })}
            </div>
            <div className="mt-4 sm:mt-5">
                {tab === "notes" && <NotesTab ctx={ctx} primary={primary} />}
                {tab === "discussion" && <DiscussionTab key={linkKey} ctx={ctx} primary={primary} member={member} onPointsChange={onPointsChange} />}
                {tab === "downloads" && <DownloadsTab ctx={ctx} primary={primary} />}
                {tab === "reviews" && <CourseReviewsTab data={reviews} primary={primary} />}
                {tab === "goals" && goal && (
                    <GoalPanel goal={goal} primary={primary} form={goalForm} setForm={setGoalForm} submitting={goalSubmitting} onSubmit={onSubmitGoal} />
                )}
                {/* {tab === "achievements" && <AchievementsTab ctx={ctx} primary={primary} />} */}
            </div>
        </div>
    );
}

/* Brand-colored focus ring for inputs — matches the search fields elsewhere. */
function brandFocus(primary) {
    return {
        onFocus: (e) => { e.target.style.borderColor = primary; e.target.style.boxShadow = `0 0 0 3px ${primary}22`; },
        onBlur: (e) => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; },
    };
}

/* Small premium empty-state used across the lesson tabs. */
function TabEmpty({ icon, title, hint, primary }) {
    return (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 px-6 py-10 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ backgroundColor: `${primary}14`, color: primary }}>
                <Icon d={icon} className="h-5 w-5" />
            </span>
            <p className="mt-3 text-sm font-semibold text-gray-700">{title}</p>
            {hint && <p className="mt-0.5 text-[13px] text-gray-400">{hint}</p>}
        </div>
    );
}

/* ---------- Notes ---------- */
function parseNote(n) {
    let note = "";
    let time = "";
    try {
        const p = JSON.parse(n.notes);
        if (p && typeof p === "object") { note = p.note || ""; time = p.time || ""; }
    } catch { /* notes not JSON */ }
    if (!note) note = n.note || n.notes || "";
    return { id: n.lesson_note_id || n.id, note, time, created_at: n.created_at || "" };
}

function NotesTab({ ctx, primary, getTime = null, onSeek = null }) {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { setNotes(await fetchLessonNotes(ctx)); } catch { /* ignore */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => { load(); }, [load]);

    const add = async () => {
        const text = draft.trim();
        if (!text) return;
        setBusy(true);
        // Stamp the note with the video's current position (FlexiFunnels layout,
        // direct-file videos only). getTime() returns "" when unavailable.
        const time = getTime ? getTime() : "";
        try { await saveLessonNote(ctx, { note: text, time }); setDraft(""); await load(); }
        catch { toast.error("Could not save note."); }
        finally { setBusy(false); }
    };
    const remove = async (id) => {
        try { await deleteLessonNote(ctx, id); setNotes((ns) => ns.filter((n) => parseNote(n).id !== id)); }
        catch { toast.error("Could not delete note."); }
    };

    return (
        <div>
            <div>
                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={2}
                    placeholder="Write a note for this lesson…"
                    className="w-full resize-none rounded-2xl border border-gray-200 bg-surface px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none transition-colors"
                    {...brandFocus(primary)}
                />
                <div className="mt-3 flex flex-col gap-2.5 px-1 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-400">
                            <Icon d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="h-3.5 w-3.5 shrink-0" />
                            Only you can see your notes
                        </span>
                        {getTime && (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ backgroundColor: `${primary}14`, color: primary }}>
                                <Icon d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" className="h-3 w-3" /> Saves the video time
                            </span>
                        )}
                    </div>
                    <button
                        onClick={add}
                        disabled={busy || !draft.trim()}
                        className="inline-flex w-full shrink-0 items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:py-2"
                        style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                    >
                        <Icon d="M12 5v14m-7-7h14" className="h-4 w-4" /> {busy ? "Saving…" : "Add note"}
                    </button>
                </div>
            </div>
            <div className="mt-4 space-y-2.5">
                {loading ? (
                    <InlineLoader label="Loading notes…" primary={primary} />
                ) : notes.length === 0 ? (
                    <TabEmpty icon="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" title="No notes yet" hint="Jot down your first one above." primary={primary} />
                ) : (
                    notes.map((n) => {
                        const p = parseNote(n);
                        return (
                            <div key={p.id} className="group flex items-start justify-between gap-3 rounded-2xl border border-gray-100 bg-surface px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition hover:border-gray-200 hover:shadow-sm">
                                <div className="flex min-w-0 gap-3">
                                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${primary}14`, color: primary }}>
                                        <Icon d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" className="h-3.5 w-3.5" />
                                    </span>
                                    <div className="min-w-0">
                                        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700">{p.note}</p>
                                        <div className="mt-1 flex flex-wrap items-center gap-2">
                                            {p.created_at && <span className="text-[11px] font-medium text-gray-400">{p.created_at}</span>}
                                            {p.time && (onSeek ? (
                                                <button
                                                    onClick={() => onSeek(p.time)}
                                                    title="Jump to this moment in the video"
                                                    className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-bold transition hover:brightness-105"
                                                    style={{ backgroundColor: `${primary}14`, color: primary }}
                                                >
                                                    <Icon d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" className="h-3 w-3" /> {p.time}
                                                </button>
                                            ) : (
                                                <span className="text-[11px] font-medium text-gray-400">· {p.time}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <button onClick={() => remove(p.id)} aria-label="Delete note" className="shrink-0 rounded-lg p-1.5 text-gray-300 dark:text-gray-500 opacity-0 transition hover:bg-red-50 dark:hover:bg-red-500/15 hover:text-red-500 group-hover:opacity-100">
                                    <Icon d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-7 0v12a1 1 0 001 1h6a1 1 0 001-1V7" className="h-4 w-4" />
                                </button>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

/* ---------- Downloads ---------- */
/**
 * The Reviews tab: what other customers said about this course, as the creator
 * approved it.
 *
 * Read-only by design — the place to WRITE one is the public form the creator
 * shares, not here. A member who wanted to add one would have to be identified,
 * moderated and de-duplicated, which is a different feature.
 */
function CourseReviewsTab({ data, primary }) {
    const items = data?.reviews || [];
    const average = Number(data?.average || 0);
    const rated = items.filter((r) => Number(r.rating) > 0).length;

    if (!items.length) {
        return <p className="py-6 text-center text-[13.5px] text-gray-500">No reviews yet.</p>;
    }

    const stars = (value, size = "h-4 w-4") => (
        <span className="inline-flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
                <svg
                    key={n}
                    className={size}
                    viewBox="0 0 24 24"
                    fill={n <= Math.round(value) ? "currentColor" : "none"}
                    stroke="currentColor"
                    strokeWidth="1.5"
                    style={{ color: n <= Math.round(value) ? primary : "rgb(var(--c-gray-300))" }}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.5a.56.56 0 011.04 0l2.13 5.11c.08.2.27.34.49.36l5.51.44c.52.04.73.7.34 1.04l-4.2 3.6a.56.56 0 00-.19.57l1.28 5.37c.12.51-.43.91-.88.64l-4.72-2.88a.56.56 0 00-.58 0l-4.72 2.88c-.45.27-1-.13-.88-.64l1.28-5.37a.56.56 0 00-.19-.57l-4.2-3.6c-.39-.34-.18-1 .34-1.04l5.51-.44a.56.56 0 00.49-.36l2.13-5.11z" />
                </svg>
            ))}
        </span>
    );

    return (
        <div>
            {/* The summary only claims what it can: the average is of the reviews
                that carry a rating, since the form makes that field optional. */}
            {rated > 0 ? (
                <div className="mb-4 flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50/60 px-4 py-3 dark:bg-surface">
                    <span className="text-[26px] font-extrabold leading-none tabular-nums" style={{ color: primary }}>
                        {average.toFixed(1)}
                    </span>
                    <div className="min-w-0">
                        {stars(average)}
                        <p className="mt-0.5 text-[12.5px] text-gray-500">
                            {rated} rating{rated === 1 ? "" : "s"} · {items.length} review{items.length === 1 ? "" : "s"}
                        </p>
                    </div>
                </div>
            ) : null}

            <ul className="space-y-3">
                {items.map((r) => (
                    <li key={r.id} className="flex gap-3 rounded-2xl border border-gray-200 bg-surface px-4 py-3.5">
                        {r.photo ? (
                            <img src={r.photo} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
                        ) : (
                            <span
                                className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-[14px] font-bold"
                                style={{ backgroundColor: `${primary}1f`, color: primary }}
                            >
                                {(r.name || "?").trim().charAt(0).toUpperCase()}
                            </span>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="text-[14px] font-bold text-gray-900">{r.name}</span>
                                {Number(r.rating) > 0 ? stars(r.rating, "h-3.5 w-3.5") : null}
                            </div>
                            {r.review ? (
                                <p className="mt-1 whitespace-pre-line text-[13.5px] leading-relaxed text-gray-600">{r.review}</p>
                            ) : null}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function DownloadsTab({ ctx, primary }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let on = true;
        fetchDownloads(ctx).then((d) => { if (on) setItems(d); }).catch(() => {}).finally(() => { if (on) setLoading(false); });
        return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) return <InlineLoader primary={primary} />;
    if (items.length === 0) return <TabEmpty icon="M12 3v10m0 0l3.5-3.5M12 13l-3.5-3.5M5 17v2a2 2 0 002 2h10a2 2 0 002-2v-2" title="No downloads" hint="This lesson has no resources to download." primary={primary} />;
    return (
        <div className="space-y-2.5">
            {items.map((it, i) => {
                const name = it.name || it.title || it.file_name || it.resource_name || it.label || `Resource ${i + 1}`;
                const url = it.url || it.file || it.file_url || it.link || it.resource || it.download_url || it.path || "";
                const ext = (name.split(".").pop() || "").toUpperCase().slice(0, 4);
                return (
                    <a
                        key={it.id || it.resource_id || i}
                        href={url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className={`group flex items-center gap-3 rounded-2xl border border-gray-100 bg-surface px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition hover:border-gray-200 hover:shadow-sm ${url ? "" : "pointer-events-none opacity-60"}`}
                    >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[10px] font-extrabold" style={{ backgroundColor: `${primary}14`, color: primary }}>
                            {ext && ext.length <= 4 ? ext : <Icon d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z" className="h-5 w-5" />}
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-gray-800">{name}</span>
                            <span className="text-[11px] font-medium text-gray-400">Tap to download</span>
                        </span>
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition group-hover:bg-gray-100" style={{ color: primary }}>
                            <Icon d="M12 3v10m0 0l3.5-3.5M12 13l-3.5-3.5M5 17v2a2 2 0 002 2h10a2 2 0 002-2v-2" className="h-4 w-4" />
                        </span>
                    </a>
                );
            })}
        </div>
    );
}

/* ---------- Quiz (modal) ----------
 * Opened from a lesson's "Take Quiz" button. Passing the quiz gates lesson
 * completion (enforced server-side); the parent marks the lesson complete via
 * onPassed(). onFailed() flips the launcher button to "Retake Quiz".
 */
function QuizModal({ ctx, lessonName, primary, onClose, onPassed, onFailed }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({}); // { question_id: choice_rand_id }
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setResult(null); setAnswers({});
        try { setData(await fetchQuiz(ctx)); } catch { /* ignore */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => { load(); }, [load]);

    // NOTE: no manual body-scroll lock here — the app-wide <ScrollLockManager>
    // handles it automatically for any `fixed inset-0` overlay (this modal is one).
    // A second manual lock stacks with the manager and leaves the page frozen on
    // close (the manager captures our "hidden" as the value to restore).

    const questions = data?.question_list || [];
    const ready = Number(data?.quiz_status) === 1 && questions.length > 0;
    const answeredCount = questions.filter((q) => answers[q.question_id]).length;
    const allAnswered = questions.length > 0 && answeredCount === questions.length;
    const pctAnswered = questions.length ? Math.round((answeredCount / questions.length) * 100) : 0;

    const submit = async () => {
        if (!allAnswered) return toast.info("Please answer all questions.");
        setBusy(true);
        try {
            const payload = questions.map((q) => ({ quiz_question_id: q.question_id, choice_rand_id: answers[q.question_id] || "" }));
            const r = await submitQuiz(ctx, payload);
            if (r?.quiz_result && (r.quiz_result.result_status != null || r.quiz_result.percentage != null)) {
                const res = { ...r.quiz_result, retake: Number(r.show_retake_quiz_btn) === 1 };
                setResult(res);
                if (String(res.result_status).toLowerCase() === "pass") onPassed?.();
                else onFailed?.();
            } else {
                toast.error(r?.message || "Could not submit the quiz.");
            }
        } catch { toast.error("Could not submit the quiz."); }
        finally { setBusy(false); }
    };

    const pass = result && String(result.result_status).toLowerCase() === "pass";
    // The bridge blocks a new attempt (retake_attempt_status === 0) when the member
    // is still inside the retake time-interval window OR has used all retakes;
    // attempt_msg explains which (it may contain a <b>…</b> time span).
    const attemptMsg = data?.attempt_msg || "";
    const blocked = ready && !result && Number(data?.retake_attempt_status) === 0;

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6">
            <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-surface shadow-2xl ring-1 ring-black/5">
                {/* header */}
                <div className="relative shrink-0 px-6 py-5 text-white" style={{ background: `linear-gradient(135deg, ${primary}, color-mix(in srgb, ${primary} 55%, #000))` }}>
                    <button onClick={onClose} aria-label="Close quiz" className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25">
                        <Icon d="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                    </button>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">Lesson quiz</p>
                    <h3 className="mt-1 pr-10 text-lg font-extrabold leading-snug">{lessonName || "Quiz"}</h3>
                    {ready && !result && !blocked && (
                        <div className="mt-3">
                            <div className="flex items-center justify-between text-[11px] font-semibold text-white/80">
                                <span>{answeredCount} of {questions.length} answered</span>
                                <span>{pctAnswered}%</span>
                            </div>
                            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/20">
                                <div className="h-full rounded-full bg-surface transition-all duration-500" style={{ width: `${pctAnswered}%` }} />
                            </div>
                        </div>
                    )}
                </div>

                {/* body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {loading ? (
                        <InlineLoader label="Loading quiz…" primary={primary} />
                    ) : !ready ? (
                        <p className="py-10 text-center text-sm text-gray-400">No quiz for this lesson.</p>
                    ) : result ? (
                        <div className="py-6 text-center">
                            <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full ${pass ? "bg-green-100 dark:bg-green-500/15" : "bg-red-100 dark:bg-red-500/15"}`}>
                                <span className={`flex h-14 w-14 items-center justify-center rounded-full text-white ${pass ? "bg-green-500" : "bg-red-500"}`}>
                                    <Icon d={pass ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"} className="h-7 w-7" />
                                </span>
                            </div>
                            <p className="mt-4 text-4xl font-black text-gray-900">{result.percentage}%</p>
                            <p className={`mt-1 text-base font-extrabold ${pass ? "text-green-600 dark:text-green-300" : "text-red-600 dark:text-red-300"}`}>{pass ? "You passed! 🎉" : "Not quite there"}</p>
                            {result.message && <p className="mx-auto mt-2 max-w-sm text-sm text-gray-500">{result.message}</p>}
                            {pass && <p className="mt-2 text-xs font-semibold text-green-600 dark:text-green-300">Quiz complete — finish any goal, then click “Mark complete”.</p>}
                        </div>
                    ) : blocked ? (
                        <div className="py-10 text-center">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/15">
                                <Icon d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" className="h-8 w-8 text-amber-500" />
                            </div>
                            <p className="mt-4 text-base font-extrabold text-gray-900">Quiz locked for now</p>
                            {attemptMsg ? (
                                <RichText html={attemptMsg} className="mx-auto mt-2 max-w-sm text-sm text-gray-500" />
                            ) : (
                                <p className="mx-auto mt-2 max-w-sm text-sm text-gray-500">You can’t retake this quiz right now.</p>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {data?.assesment_default_text && <p className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-500">{data.assesment_default_text}</p>}
                            {questions.map((q, qi) => (
                                <div key={q.question_id} className="rounded-2xl border border-gray-200 bg-surface p-4 shadow-sm">
                                    <p className="text-sm font-bold text-gray-900"><span style={{ color: primary }}>{qi + 1}.</span> {q.question}</p>
                                    <div className="mt-3 space-y-2">
                                        {(q.choices || []).map((c) => {
                                            const selected = answers[q.question_id] === c.choice_rand_id;
                                            return (
                                                <button
                                                    key={c.choice_rand_id}
                                                    onClick={() => setAnswers((a) => ({ ...a, [q.question_id]: c.choice_rand_id }))}
                                                    className="flex w-full items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left text-sm font-medium transition"
                                                    style={selected ? { borderColor: primary, backgroundColor: `${primary}0d`, color: "rgb(var(--c-gray-900))" } : { borderColor: "rgb(var(--c-gray-200))", color: "rgb(var(--c-gray-700))" }}
                                                >
                                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2" style={{ borderColor: selected ? primary : "rgb(var(--c-slate-300))" }}>
                                                        {selected && <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: primary }} />}
                                                    </span>
                                                    {c.choice_name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* footer */}
                {!loading && ready && (
                    <div className="shrink-0 border-t border-gray-100 bg-surface px-6 py-4">
                        {result ? (
                            pass ? (
                                <button onClick={onClose} className="w-full rounded-xl px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:brightness-110" style={{ backgroundColor: "#16a34a" }}>
                                    Continue
                                </button>
                            ) : result.retake ? (
                                <button onClick={load} className="w-full rounded-xl px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:brightness-110" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>
                                    Retake quiz
                                </button>
                            ) : (
                                <div className="text-center">
                                    <p className="text-xs font-semibold text-gray-500">You've used all your attempts for this quiz.</p>
                                    <button onClick={onClose} className="mt-2 text-sm font-bold text-gray-700 underline">Close</button>
                                </div>
                            )
                        ) : blocked ? (
                            <button onClick={onClose} className="w-full rounded-xl border border-gray-200 px-5 py-3 text-sm font-bold text-gray-600 transition hover:bg-gray-50">
                                Close
                            </button>
                        ) : (
                            <button
                                onClick={submit}
                                disabled={busy || !allAnswered}
                                className="w-full rounded-xl px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                                style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                            >
                                {busy ? "Submitting…" : allAnswered ? "Submit quiz" : `Answer all ${questions.length} question${questions.length === 1 ? "" : "s"}`}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ---------- Discussion (posts + comments + likes) ---------- */
function DiscussionTab({ ctx, primary, member, onPointsChange }) {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [title, setTitle] = useState("");
    const [gif, setGif] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { const r = await fetchLessonDiscussion(ctx); setPosts(r.posts); } catch { /* ignore */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => { load(); }, [load]);

    // Who can be @mentioned in comments/replies: members who can open this course.
    const [mentionMembers, setMentionMembers] = useState([]);
    useEffect(() => {
        let alive = true;
        fetchLessonMentionMembers(ctx).then((m) => alive && setMentionMembers(m)).catch(() => {});
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addPost = async () => {
        const t = title.trim();
        if (!t && !gif) return;
        setBusy(true);
        try {
            const r = await createLessonPost(ctx, { title: t, image: gif });
            setTitle(""); setGif(""); await load();
            if (toastEarned(r?.points_awarded, "Discussion posted.")) onPointsChange?.();
        }
        catch (err) { toast.error(err?.response?.data?.msg || "Could not post."); }
        finally { setBusy(false); }
    };

    return (
        <div>
            {/* Chat-style composer — avatar + pill input (emoji/GIF docked), send button. */}
            <div className="rounded-2xl border border-gray-200 bg-surface p-2 shadow-sm transition-shadow focus-within:shadow-md sm:p-2.5">
                <div className="flex items-center gap-2">
                    <Avatar src={member?.img} name={member?.name} size={36} />
                    <div className="relative min-w-0 flex-1">
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") addPost(); }}
                            placeholder="Share something…"
                            className="w-full rounded-full border border-gray-200 bg-gray-50/70 py-2 pl-4 pr-[4.25rem] text-sm text-gray-800 placeholder-gray-400 outline-none transition-colors"
                            {...brandFocus(primary)}
                        />
                        {/* Emoji + GIF pickers, docked to the right of the input. */}
                        <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                            <EmojiPicker onPick={(e) => setTitle((t) => t + e)} dropUp={false} className="shrink-0" />
                            <GifPicker onPick={(url) => setGif(url)} dropUp={false} className="shrink-0" />
                        </div>
                    </div>
                    <button
                        onClick={addPost}
                        disabled={busy || (!title.trim() && !gif)}
                        aria-label={busy ? "Posting" : "Post"}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 sm:h-9 sm:w-9"
                        style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                    >
                        <Icon d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </button>
                </div>

                {/* Selected GIF preview */}
                {gif && (
                    <div className="relative ml-[2.75rem] mt-2 inline-block overflow-hidden rounded-xl border border-gray-200">
                        <img src={gif} alt="Selected GIF" className="max-h-40 w-auto" />
                        <button
                            onClick={() => setGif("")}
                            aria-label="Remove GIF"
                            className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/80"
                        >
                            <Icon d="M6 18L18 6M6 6l12 12" className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}
            </div>

            {/* Thread */}
            <div className="mt-3 space-y-2.5">
                {loading ? (
                    <InlineLoader label="Loading discussion…" primary={primary} />
                ) : posts.length === 0 ? (
                    <TabEmpty icon="M8 10h8M8 14h5m7-2a8 8 0 01-11.6 7.13L3 20l1.87-5.4A8 8 0 1120 12z" title="No discussion yet" hint="Be the first to post." primary={primary} />
                ) : (
                    <>
                        <div className="flex items-center gap-2 px-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                            <Icon d="M8 10h8M8 14h5m7-2a8 8 0 01-11.6 7.13L3 20l1.87-5.4A8 8 0 1120 12z" className="h-3.5 w-3.5" />
                            {posts.length} {posts.length === 1 ? "discussion" : "discussions"}
                        </div>
                        {posts.map((p) => <PostItem key={p.post_id} ctx={ctx} post={p} primary={primary} member={member} mentionMembers={mentionMembers} onReload={load} onPointsChange={onPointsChange} />)}
                    </>
                )}
            </div>
        </div>
    );
}

function LikeButton({ liked, count, primary, onClick }) {
    const Heart = liked ? HeartSolid : HeartOutline;
    return (
        <button
            onClick={onClick}
            aria-label={liked ? "Unlike" : "Like"}
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold transition hover:bg-gray-100"
            style={liked ? { color: "#ef4444", backgroundColor: "#ef44441a" } : { color: "rgb(var(--c-gray-500))" }}
        >
            <Heart className="h-4 w-4" />
            {count > 0 ? count : "Like"}
        </button>
    );
}

/* Author-only "···" menu with Edit + Delete, used on lesson posts and comments. */
function EditDeleteMenu({ onEdit, onDelete }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        addOutsidePress(h);
        return () => removeOutsidePress(h);
    }, [open]);
    return (
        <div ref={ref} className="relative shrink-0">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label="More"
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                style={open ? { backgroundColor: "rgb(var(--c-gray-100))", color: "rgb(var(--c-gray-800))" } : undefined}
            >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]">
                    <circle cx="12" cy="5" r="1.6" />
                    <circle cx="12" cy="12" r="1.6" />
                    <circle cx="12" cy="19" r="1.6" />
                </svg>
            </button>
            {open && (
                <div className="absolute right-0 z-20 mt-1 w-32 overflow-hidden rounded-xl border border-gray-100 bg-surface py-1 shadow-lg ring-1 ring-black/5">
                    <button type="button" onClick={() => { setOpen(false); onEdit(); }} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] font-medium text-gray-700 transition hover:bg-gray-50">
                        <Icon d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button type="button" onClick={() => { setOpen(false); onDelete(); }} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] font-medium text-red-600 dark:text-red-300 transition hover:bg-red-50 dark:hover:bg-red-500/15">
                        <Icon d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" className="h-3.5 w-3.5" /> Delete
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * The discussion / reply a notification link points at (&post=, &comment=).
 * Scrolls it into view and flashes a brand ring once it is on screen.
 */
function useDiscussionFocus(kind, id) {
    const [sp] = useSearchParams();
    const ref = useRef(null);
    const target = sp.get("tab") === "discussion" && String(sp.get(kind) || "") === String(id)
        && (kind === "comment" || !sp.get("comment"));
    const [flash, setFlash] = useState(false);
    useEffect(() => {
        if (!target || !ref.current) return undefined;
        const t = setTimeout(() => {
            ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            setFlash(true);
        }, 350);
        const off = setTimeout(() => setFlash(false), 3200);
        return () => { clearTimeout(t); clearTimeout(off); };
    }, [target]);
    return { ref, flash };
}

function PostItem({ ctx, post, primary, member, mentionMembers = [], onReload, onPointsChange }) {
    const [liked, setLiked] = useState(Number(post.like) === 1);
    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState(post.post_title || "");
    const isAuthor = Number(member?.community_member_id) === Number(post.member_id);
    const saveEdit = async () => {
        const t = editText.trim();
        if (!t && !post.post_image) return;
        try { await createLessonPost(ctx, { title: t, postId: post.post_id, image: post.post_image || "" }); setEditing(false); onReload?.(); }
        catch (err) { toast.error(err?.response?.data?.msg || "Could not update post."); }
    };
    const removePost = async () => {
        if (!window.confirm("Delete this post and its comments?")) return;
        try { await deleteLessonDiscussion(ctx, { id: post.post_id, type: "post" }); onReload?.(); }
        catch (err) { toast.error(err?.response?.data?.msg || "Could not delete post."); }
    };
    const [likes, setLikes] = useState(Number(post.tot_likes || 0));
    // A link to a reply in this discussion opens its comments so the reply is there.
    const [linkSp] = useSearchParams();
    const [open, setOpen] = useState(() =>
        linkSp.get("tab") === "discussion" && String(linkSp.get("post") || "") === String(post.post_id) && !!linkSp.get("comment"));
    const focus = useDiscussionFocus("post", post.post_id);
    const [comments, setComments] = useState(post.comment || []);
    const [text, setText] = useState("");
    const [gif, setGif] = useState("");
    const [busy, setBusy] = useState(false);
    const commentMentions = useMentions({ value: text, setValue: setText, members: mentionMembers, primary, dropUp: true });
    // Phone/tablet: comments open in the same bottom sheet as the group feed
    // (CommentsSheet), with ONE pinned composer that also sends replies.
    const compact = useCompactViewport();
    const [replyTo, setReplyTo] = useState(null);   // top-level comment the sheet composer replies to
    const closeSheet = () => { setOpen(false); setReplyTo(null); };

    const like = async () => {
        const next = liked ? 0 : 1;
        setLiked(!liked); setLikes((n) => n + (next ? 1 : -1));
        try {
            const r = await toggleLessonLike(ctx, { type: 1, fkId: post.post_id, like: next });
            if (typeof r.total_like === "number") setLikes(r.total_like);
            if (toastEarned(r?.points_awarded, "Liked.")) onPointsChange?.();
        }
        catch { setLiked(liked); setLikes((n) => n + (next ? -1 : 1)); }
    };
    const reloadComments = async () => {
        try { const r = await fetchLessonDiscussion(ctx); const fresh = r.posts.find((p) => p.post_id === post.post_id); if (fresh) setComments(fresh.comment || []); }
        catch { /* ignore */ }
    };
    const addComment = async () => {
        const c = text.trim();
        if (!c && !gif) return;
        setBusy(true);
        try {
            const parentId = compact && replyTo ? replyTo.comment_id : 0;
            const r = await createLessonComment(ctx, { postId: post.post_id, parentId, comment: commentMentions.serialize(c), image: gif });
            setText(""); setGif(""); setReplyTo(null); await reloadComments(); setOpen(true);
            if (toastEarned(r?.points_awarded, parentId ? "Reply added." : "Comment added.")) onPointsChange?.();
        }
        catch (err) { toast.error(err?.response?.data?.msg || "Could not comment."); }
        finally { setBusy(false); }
    };

    return (
        <div
            ref={focus.ref}
            className="rounded-2xl border border-gray-100 bg-surface p-3 shadow-sm transition-shadow duration-500 hover:shadow-md sm:p-4"
            style={focus.flash ? { boxShadow: `0 0 0 3px ${primary}55` } : undefined}
        >
            <div className="flex items-start gap-2.5 sm:gap-3">
                <Avatar src={post.profile_img} name={post.profile_name} size={38} />
                <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2">
                        <div className="flex min-w-0 flex-1 items-baseline gap-2">
                            <p className="truncate text-sm font-bold text-gray-900">{post.profile_name || "Member"}</p>
                            <span className="text-gray-300 dark:text-gray-500">·</span>
                            <p className="shrink-0 text-[11px] text-gray-400">{post.created_at}</p>
                        </div>
                        {isAuthor && !editing && (
                            <div className="-mr-1 -mt-1 shrink-0">
                                <EditDeleteMenu onEdit={() => { setEditText(post.post_title || ""); setEditing(true); }} onDelete={removePost} />
                            </div>
                        )}
                    </div>
                    {editing ? (
                        <div className="mt-1.5">
                            <textarea
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                rows={2}
                                autoFocus
                                className="w-full resize-none rounded-xl border border-gray-200 bg-surface px-3 py-2 text-[13.5px] text-gray-800 outline-none"
                                {...brandFocus(primary)}
                            />
                            <div className="mt-1.5 flex items-center gap-2">
                                <button onClick={saveEdit} className="rounded-lg px-3 py-1.5 text-xs font-bold text-white shadow-sm transition hover:brightness-110" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>Save</button>
                                <button onClick={() => setEditing(false)} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-500 transition hover:bg-gray-100">Cancel</button>
                            </div>
                        </div>
                    ) : (
                        post.post_title && <p className="mt-1 whitespace-pre-wrap break-words text-[13.5px] leading-relaxed text-gray-700">{post.post_title}</p>
                    )}
                    {post.post_image && (
                        <div className="mt-2 overflow-hidden rounded-xl border border-gray-100">
                            <img src={post.post_image} alt="" className="max-h-72 w-auto" loading="lazy" />
                        </div>
                    )}
                    <div className="mt-2.5 flex items-center gap-1">
                        <LikeButton liked={liked} count={likes} primary={primary} onClick={like} />
                        <button
                            onClick={() => setOpen((o) => !o)}
                            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                            style={open ? { backgroundColor: `${primary}14`, color: primary } : undefined}
                        >
                            <Icon d="M8 12h8M8 8h8m-8 8h5M21 12a9 9 0 11-3.5-7.1L21 3v9z" className="w-4 h-4" />
                            {comments.length || Number(post.tot_comments) || ""} Comment
                        </button>
                    </div>

                    {open && compact && (
                        <CommentsSheet
                            compact
                            onClose={closeSheet}
                            count={comments.length}
                            footer={
                                <div className="pt-1">
                                    {replyTo && (
                                        <div className="mb-2 flex items-center gap-2 rounded-lg bg-gray-50 px-2.5 py-1.5 text-[12px] text-gray-500">
                                            <span className="min-w-0 flex-1 truncate font-medium">Replying to {replyTo.profile_name || "Member"}</span>
                                            <button type="button" onClick={() => setReplyTo(null)} aria-label="Cancel reply" className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-gray-400 transition hover:bg-gray-200 hover:text-gray-600">
                                                <Icon d="M6 18L18 6M6 6l12 12" className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    )}
                                    {gif && (
                                        <div className="relative mb-2 ml-10 inline-block overflow-hidden rounded-lg border border-gray-200">
                                            <img src={gif} alt="Selected GIF" className="max-h-24 w-auto" />
                                            <button onClick={() => setGif("")} aria-label="Remove GIF" className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80">
                                                <Icon d="M6 18L18 6M6 6l12 12" className="h-3 w-3" />
                                            </button>
                                        </div>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="shrink-0"><Avatar src={member?.img} name={member?.name} size={32} /></span>
                                        <div className="relative min-w-0 flex-1">
                                            <input
                                                ref={commentMentions.ref}
                                                value={text}
                                                onChange={commentMentions.onChange}
                                                onBlur={commentMentions.onBlur}
                                                onKeyDown={(e) => { commentMentions.onKeyDown(e); if (!e.defaultPrevented && e.key === "Enter") addComment(); }}
                                                placeholder={replyTo ? `Reply to ${replyTo.profile_name || "Member"}…` : "Write a comment… use @ to tag"}
                                                className="w-full rounded-full border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 outline-none transition"
                                                {...brandFocus(primary)}
                                            />
                                            {commentMentions.dropdown}
                                        </div>
                                        <div className="flex w-full items-center justify-end gap-3">
                                            <GifPicker onPick={(url) => setGif(url)} dropUp className="shrink-0" />
                                            <EmojiPicker onPick={(e) => setText((t) => t + e)} dropUp className="shrink-0" />
                                            <button onClick={addComment} disabled={busy || (!text.trim() && !gif)} aria-label={replyTo ? "Send reply" : "Send comment"} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>
                                                <Icon d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            }
                        >
                            {comments.filter((c) => Number(c.parent_id) === 0).length === 0 ? (
                                <p className="py-8 text-center text-sm text-gray-400">No comments yet. Be the first.</p>
                            ) : (
                                comments.filter((c) => Number(c.parent_id) === 0).map((c) => (
                                    <CommentItem key={c.comment_id} ctx={ctx} comment={c} primary={primary} member={member} mentionMembers={mentionMembers} onReload={reloadComments} onPointsChange={onPointsChange} compact onReply={setReplyTo} />
                                ))
                            )}
                        </CommentsSheet>
                    )}
                    {open && !compact && (
                        <div className="-ml-12 mt-2.5 border-t border-gray-100 pt-2.5 sm:ml-0">
                            <div className="flex items-center gap-2">
                                <Avatar src={member?.img} name={member?.name} size={30} />
                                <div className="min-w-0 flex-1">
                                    <div className="relative">
                                        <input
                                            ref={commentMentions.ref}
                                            value={text}
                                            onChange={commentMentions.onChange}
                                            onBlur={commentMentions.onBlur}
                                            onKeyDown={(e) => { commentMentions.onKeyDown(e); if (!e.defaultPrevented && e.key === "Enter") addComment(); }}
                                            placeholder="Write a comment… (type @ to mention)"
                                            className="w-full rounded-full border border-gray-200 bg-gray-50/70 py-2 pl-4 pr-[4.25rem] text-sm text-gray-800 placeholder-gray-400 outline-none transition-colors"
                                            {...brandFocus(primary)}
                                        />
                                        <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                                            <EmojiPicker onPick={(e) => setText((t) => t + e)} className="shrink-0" />
                                            <GifPicker onPick={(url) => setGif(url)} className="shrink-0" />
                                        </div>
                                        {commentMentions.dropdown}
                                    </div>
                                    {gif && (
                                        <div className="relative mt-2 inline-block overflow-hidden rounded-lg border border-gray-200">
                                            <img src={gif} alt="Selected GIF" className="max-h-28 w-auto" />
                                            <button onClick={() => setGif("")} aria-label="Remove GIF" className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80">
                                                <Icon d="M6 18L18 6M6 6l12 12" className="h-3 w-3" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <button onClick={addComment} disabled={busy || (!text.trim() && !gif)} aria-label="Send comment" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 sm:h-9 sm:w-9" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>
                                    <Icon d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                </button>
                            </div>
                            <div className="mt-3 space-y-2.5">
                                {comments.filter((c) => Number(c.parent_id) === 0).map((c) => (
                                    <CommentItem key={c.comment_id} ctx={ctx} comment={c} primary={primary} member={member} mentionMembers={mentionMembers} onReload={reloadComments} onPointsChange={onPointsChange} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// "@[Ann](42)" -> "@Ann" for the edit box; the ids are put back on save.
const mentionLabels = (t) => (t || "").replace(new RegExp(MENTION_RE.source, "g"), "@$1");
const mentionsIn = (t) => [...(t || "").matchAll(new RegExp(MENTION_RE.source, "g"))].map((m) => ({ name: m[1], id: Number(m[2]) }));

function CommentItem({ ctx, comment, primary, member, mentionMembers = [], onReload, onPointsChange, depth = 0, compact = false, onReply }) {
    const focus = useDiscussionFocus("comment", comment.comment_id);
    const [liked, setLiked] = useState(Number(comment.like) === 1);
    const [likes, setLikes] = useState(Number(comment.tot_likes || 0));
    const [replying, setReplying] = useState(false);
    const [text, setText] = useState("");
    const [gif, setGif] = useState("");
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState(mentionLabels(comment.comment));
    const isAuthor = Number(member?.community_member_id) === Number(comment.member_id);
    const replyMentions = useMentions({ value: text, setValue: setText, members: mentionMembers, primary, dropUp: true });
    const saveEdit = async () => {
        const t = serializeMentions(editText.trim(), mentionsIn(comment.comment));
        if (!t && !comment.comment_image) return;
        try { await createLessonComment(ctx, { postId: comment.post_id, commentId: comment.comment_id, comment: t, image: comment.comment_image || "" }); setEditing(false); await onReload(); }
        catch (err) { toast.error(err?.response?.data?.msg || "Could not update comment."); }
    };
    const deleteComment = async () => {
        try { await deleteLessonDiscussion(ctx, { id: comment.comment_id, type: "comment" }); await onReload(); }
        catch (err) { toast.error(err?.response?.data?.msg || "Could not delete comment."); }
    };
    const removeComment = async () => {
        if (!window.confirm("Delete this comment?")) return;
        await deleteComment();
    };
    // Compact: press-and-hold opens Edit / Delete, as in the group feed.
    const longPress = useLongPressBinder();
    const [actionsOpen, setActionsOpen] = useState(false);
    const startEdit = () => { setEditText(mentionLabels(comment.comment)); setEditing(true); };

    const like = async () => {
        const next = liked ? 0 : 1;
        setLiked(!liked); setLikes((n) => n + (next ? 1 : -1));
        try {
            const r = await toggleLessonLike(ctx, { type: 2, fkId: comment.comment_id, like: next });
            if (typeof r.total_like === "number") setLikes(r.total_like);
            if (toastEarned(r?.points_awarded, "Liked.")) onPointsChange?.();
        }
        catch { setLiked(liked); setLikes((n) => n + (next ? -1 : 1)); }
    };
    const reply = async () => {
        const c = text.trim();
        if (!c && !gif) return;
        setBusy(true);
        try {
            const r = await createLessonComment(ctx, { postId: comment.post_id, parentId: comment.comment_id, comment: replyMentions.serialize(c), image: gif });
            setText(""); setGif(""); setReplying(false); await onReload();
            if (toastEarned(r?.points_awarded, "Reply added.")) onPointsChange?.();
        }
        catch (err) { toast.error(err?.response?.data?.msg || "Could not reply."); }
        finally { setBusy(false); }
    };
    const children = comment.childcmt || [];
    const editBlock = (
        <div className="mt-1">
            <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={2}
                autoFocus
                className="w-full resize-none rounded-lg border border-gray-200 bg-surface px-2.5 py-1.5 text-sm text-gray-800 outline-none"
                {...brandFocus(primary)}
            />
            <div className="mt-1.5 flex items-center gap-2">
                <button onClick={saveEdit} className="rounded-lg px-2.5 py-1 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>Save</button>
                <button onClick={() => setEditing(false)} className="rounded-lg px-2.5 py-1 text-[11px] font-semibold text-gray-500 transition hover:bg-gray-200">Cancel</button>
            </div>
        </div>
    );

    // Phone/tablet row, inside the comments sheet — the group feed's layout:
    // name + time in the bubble, "Reply" under it, the heart as its own column
    // on the right, replies threaded behind a brand-tinted rule. Replying goes
    // through the sheet's pinned composer (onReply), never a nested input.
    if (compact) {
        return (
            <div
                ref={focus.ref}
                className="flex items-start gap-2.5 rounded-2xl transition-shadow duration-500"
                style={focus.flash ? { boxShadow: `0 0 0 2px ${primary}`, backgroundColor: `${primary}0d` } : undefined}
                {...longPress(() => setActionsOpen(true), { enabled: isAuthor && !editing })}
            >
                <CommentActionSheet
                    open={actionsOpen}
                    title={`${comment.profile_name || "Member"}'s comment`}
                    onClose={() => setActionsOpen(false)}
                    items={[
                        { label: "Edit", icon: EditIcon, onClick: () => { setActionsOpen(false); startEdit(); } },
                        {
                            label: "Delete",
                            icon: DeleteIcon,
                            danger: true,
                            confirm: { title: "Delete comment?", message: "This comment will be permanently removed.", confirmLabel: "Delete" },
                            onClick: async () => { setActionsOpen(false); await deleteComment(); },
                        },
                    ]}
                />
                <Avatar src={comment.profile_img} name={comment.profile_name} size={depth > 0 ? 28 : 32} />
                <div className="min-w-0 flex-1">
                    {editing ? editBlock : (
                        <div className="rounded-2xl bg-gray-50 px-3.5 py-2">
                            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                                <span className="truncate text-sm font-semibold text-gray-900">{comment.profile_name || "Member"}</span>
                                <span className="shrink-0 whitespace-nowrap text-[11px] text-gray-400">{comment.created_at}</span>
                            </div>
                            {comment.comment && <p className="whitespace-pre-wrap break-words text-sm text-gray-700"><MentionText text={comment.comment} primary={primary} /></p>}
                        </div>
                    )}
                    {comment.comment_image && (
                        <div className="mt-1.5 inline-block overflow-hidden rounded-lg border border-gray-200">
                            <img src={comment.comment_image} alt="" className="max-h-48 w-auto" loading="lazy" />
                        </div>
                    )}
                    {member && !editing && (
                        <div className="ml-1.5 mt-1 text-xs">
                            <button
                                type="button"
                                // A reply to a reply joins the same thread (one level deep).
                                onClick={() => onReply?.(depth > 0 ? { comment_id: comment.parent_id, profile_name: comment.profile_name } : comment)}
                                className="font-medium text-gray-400 transition hover:text-gray-600"
                            >
                                Reply
                            </button>
                        </div>
                    )}
                    {children.length > 0 && (
                        <div className="mt-2.5 space-y-2.5 border-l-2 pl-3" style={{ borderColor: `${primary}1f` }}>
                            {children.map((ch) => (
                                <CommentItem key={ch.comment_id} ctx={ctx} comment={ch} primary={primary} member={member} mentionMembers={mentionMembers} onReload={onReload} onPointsChange={onPointsChange} depth={depth + 1} compact onReply={onReply} />
                            ))}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={like}
                    aria-label={liked ? "Unlike comment" : "Like comment"}
                    aria-pressed={liked}
                    className={`mt-1 flex shrink-0 flex-col items-center gap-0.5 transition ${liked ? "text-red-500" : "text-gray-300 dark:text-gray-500"}`}
                >
                    {liked ? <HeartSolid className="h-[18px] w-[18px]" /> : <HeartOutline className="h-[18px] w-[18px]" />}
                    {likes > 0 && <span className="text-[10px] font-semibold leading-none tabular-nums text-gray-400">{likes}</span>}
                </button>
            </div>
        );
    }

    return (
        <div className={depth > 0 ? "ml-4 sm:ml-7" : ""}>
            <div
                ref={focus.ref}
                className="flex items-start gap-2 rounded-xl transition-shadow duration-500"
                style={focus.flash ? { boxShadow: `0 0 0 3px ${primary}55`, backgroundColor: `${primary}0d` } : undefined}
            >
                <Avatar src={comment.profile_img} name={comment.profile_name} size={28} />
                <div className="min-w-0 flex-1">
                    <div className="rounded-2xl bg-gray-50 px-3 py-2">
                        <p className="text-xs font-bold text-gray-900">{comment.profile_name || "Member"}</p>
                        {editing ? editBlock : (
                            <>
                                {comment.comment && <p className="whitespace-pre-wrap break-words text-sm text-gray-700"><MentionText text={comment.comment} primary={primary} /></p>}
                                {comment.comment_image && (
                                    <div className="mt-1.5 overflow-hidden rounded-lg border border-gray-200">
                                        <img src={comment.comment_image} alt="" className="max-h-48 w-auto" loading="lazy" />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="mt-1 flex items-center gap-x-3 pl-1">
                        <span className="whitespace-nowrap text-[11px] text-gray-400">{comment.created_at}</span>
                        <LikeButton liked={liked} count={likes} primary={primary} onClick={like} />
                        {depth === 0 && (
                            <button onClick={() => setReplying((r) => !r)} className="shrink-0 text-[12px] font-semibold text-gray-400 transition hover:text-gray-600">Reply</button>
                        )}
                        {isAuthor && !editing && (
                            <div className="ml-auto shrink-0">
                                <EditDeleteMenu onEdit={() => { setEditText(mentionLabels(comment.comment)); setEditing(true); }} onDelete={removeComment} />
                            </div>
                        )}
                    </div>
                    {replying && (
                        <div className="mt-2 flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                                <div className="relative">
                                    <input
                                        ref={replyMentions.ref}
                                        value={text}
                                        onChange={replyMentions.onChange}
                                        onBlur={replyMentions.onBlur}
                                        onKeyDown={(e) => { replyMentions.onKeyDown(e); if (!e.defaultPrevented && e.key === "Enter") reply(); }}
                                        placeholder="Write a reply… (type @ to mention)"
                                        className="w-full rounded-full border border-gray-200 bg-gray-50/70 py-2 pl-4 pr-[4.25rem] text-sm text-gray-800 placeholder-gray-400 outline-none transition-colors"
                                        {...brandFocus(primary)}
                                    />
                                    <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                                        <EmojiPicker onPick={(e) => setText((t) => t + e)} className="shrink-0" />
                                        <GifPicker onPick={(url) => setGif(url)} className="shrink-0" />
                                    </div>
                                    {replyMentions.dropdown}
                                </div>
                                {gif && (
                                    <div className="relative mt-2 inline-block overflow-hidden rounded-lg border border-gray-200">
                                        <img src={gif} alt="Selected GIF" className="max-h-24 w-auto" />
                                        <button onClick={() => setGif("")} aria-label="Remove GIF" className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80">
                                            <Icon d="M6 18L18 6M6 6l12 12" className="h-3 w-3" />
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button onClick={reply} disabled={busy || (!text.trim() && !gif)} aria-label="Send reply" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 sm:h-8 sm:w-8" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>
                                <Icon d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            </button>
                        </div>
                    )}
                    {children.length > 0 && (
                        <div className="mt-2 space-y-2">
                            {children.map((ch) => (
                                <CommentItem key={ch.comment_id} ctx={ctx} comment={ch} primary={primary} member={member} mentionMembers={mentionMembers} onReload={onReload} onPointsChange={onPointsChange} depth={depth + 1} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ---------- Resume-aware video player ---------- */
/* Seeks to the member's last saved position on load, and saves progress
   (throttled) while playing + on pause. Only used for direct file videos
   (<video src>); embedded iframes can't expose currentTime. */
function ResumableVideo({ src, startAt, onSave, videoRef, primary }) {
    const elRef = useRef(null);
    const lastSave = useRef(0);
    const seeked = useRef(false);
    const { transport, attach } = useNativeTransport(videoRef);
    const setNode = useCallback((el) => { elRef.current = el; attach(el); }, [attach]);

    const save = () => {
        const v = elRef.current;
        if (!v || !v.currentTime) return;
        onSave(v.currentTime, v.duration || null);
    };

    return (
        <VideoChrome transport={transport} primary={primary} mediaKey={src}>
            <video
                ref={setNode}
                src={src}
                /* No `controls`: the chrome above draws our own bar, so a file
                   lesson looks like every other lesson instead of wearing each
                   browser's native skin. controlsList/disablePictureInPicture
                   still matter for the one case the browser shows its own UI —
                   iOS native fullscreen. */
                controlsList="nodownload"
                disablePictureInPicture
                playsInline
                preload="metadata"
                className="absolute inset-0 h-full w-full bg-black object-contain"
                onLoadedMetadata={(e) => {
                    if (seeked.current) return;
                    seeked.current = true;
                    const v = e.currentTarget;
                    if (startAt > 0 && (!v.duration || startAt < v.duration - 2)) {
                        try { v.currentTime = startAt; } catch { /* ignore */ }
                    }
                }}
                onTimeUpdate={(e) => {
                    const now = Date.now();
                    if (now - lastSave.current < 10000) return; // throttle: save at most every 10s
                    lastSave.current = now;
                    onSave(e.currentTarget.currentTime, e.currentTarget.duration || null);
                }}
                onPause={save}
            />
        </VideoChrome>
    );
}

/* ---------- HLS (.m3u8) player ---------- */
/* Plays an adaptive HLS stream via hls.js (loaded on demand), with native-HLS
   fallback on Safari/iOS. Renders a real <video>, so PiP, resume and note
   timestamps all work through videoRef just like a direct file. Mirrors the old
   app's HlsVideoPlayer, including fatal-error recovery. */
function HlsVideo({ src, startAt, onSave, videoRef, primary }) {
    const elRef = useRef(null);
    const lastSave = useRef(0);
    const seeked = useRef(false);
    const hlsRef = useRef(null);
    const { transport, attach } = useNativeTransport(videoRef);
    const setNode = useCallback((el) => { elRef.current = el; attach(el); }, [attach]);

    useEffect(() => {
        const video = elRef.current;
        if (!video) return undefined;
        let cancelled = false;
        // Native HLS (Safari / iOS) — the <video> handles the manifest directly.
        if (video.canPlayType("application/vnd.apple.mpegurl")) {
            video.src = src;
            return () => {};
        }
        loadHlsJs().then((Hls) => {
            if (cancelled) return;
            if (!Hls || !Hls.isSupported()) {
                try { video.src = src; } catch { /* ignore */ }
                return;
            }
            const hls = new Hls({ enableWorker: true });
            hlsRef.current = hls;
            hls.loadSource(src);
            hls.attachMedia(video);
            hls.on(Hls.Events.ERROR, (_e, data) => {
                if (!data || !data.fatal) return;
                if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
                else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
                else { try { hls.destroy(); } catch { /* ignore */ } }
            });
        });
        return () => {
            cancelled = true;
            try { hlsRef.current?.destroy?.(); } catch { /* ignore */ }
            hlsRef.current = null;
        };
    }, [src]);

    const save = () => {
        const v = elRef.current;
        if (!v || !v.currentTime) return;
        onSave(v.currentTime, v.duration || null);
    };

    return (
        <VideoChrome transport={transport} primary={primary} mediaKey={src}>
            <video
                ref={setNode}
                /* See ResumableVideo — our own bar, so no native controls. */
                controlsList="nodownload"
                disablePictureInPicture
                playsInline
                preload="auto"
                className="absolute inset-0 h-full w-full bg-black object-contain"
                onLoadedMetadata={(e) => {
                    if (seeked.current) return;
                    seeked.current = true;
                    const v = e.currentTarget;
                    if (startAt > 0 && (!v.duration || startAt < v.duration - 2)) {
                        try { v.currentTime = startAt; } catch { /* ignore */ }
                    }
                }}
                onTimeUpdate={(e) => {
                    const now = Date.now();
                    if (now - lastSave.current < 10000) return;
                    lastSave.current = now;
                    onSave(e.currentTarget.currentTime, e.currentTarget.duration || null);
                }}
                onPause={save}
            />
        </VideoChrome>
    );
}

/* ================================================================== */
/* Achievements — points · level · streak · badges · rewards (redeem) */
/* Course-scoped (badges/rewards need funnel_page_id) + funnel-wide   */
/* points/level/streak. Uses the native gamification endpoints.       */
/* ================================================================== */
const DEFAULT_BADGE_IMAGE =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cdefs%3E%3ClinearGradient id='bg' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23fbbf24'/%3E%3Cstop offset='1' stop-color='%23f59e0b'/%3E%3C/linearGradient%3E%3C/defs%3E%3Ccircle cx='100' cy='100' r='72' fill='url(%23bg)'/%3E%3Cpath d='M100 58l12 25 27 3-20 19 5 27-24-13-24 13 5-27-20-19 27-3z' fill='white'/%3E%3C/svg%3E";
const DEFAULT_REWARD_IMAGE =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect width='400' height='400' fill='%23111827'/%3E%3Cg fill='none' stroke='%239ca3af' stroke-width='14' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='120' y='192' width='160' height='118' rx='12'/%3E%3Cpath d='M112 192h176M200 192v118M200 192c-18-38-66-38-66-8 0 18 38 8 66 8zM200 192c18-38 66-38 66-8 0 18-38 8-66 8z'/%3E%3C/g%3E%3C/svg%3E";

const GAMIFY_LABELS = {
    lessoncomplete: "Complete a lesson",
    lesson_complete: "Complete a lesson",
    quiz_pass: "Pass a quiz",
    coursecomplete: "Complete a course",
    course_complete: "Complete a course",
    resource_download: "Download a resource",
    resourcedownload: "Download a resource",
    comments: "Post a comment",
    likes: "Like a post",
    profilepic: "Upload a profile picture",
    upload_profile: "Upload a profile picture",
};

function Img({ src, fallback, className }) {
    const [err, setErr] = useState(false);
    return <img src={!err && src ? src : fallback} onError={() => setErr(true)} alt="" className={className} />;
}

function AchievementsTab({ ctx, primary }) {
    const [loading, setLoading] = useState(true);
    const [level, setLevel] = useState(null);
    const [streak, setStreak] = useState(null);
    const [badges, setBadges] = useState([]);
    const [nextBadge, setNextBadge] = useState(null);
    const [rewards, setRewards] = useState([]);
    const [points, setPoints] = useState(0);
    const [gamify, setGamify] = useState({});
    const [redeeming, setRedeeming] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [lv, st, bd, nb, rw, gp] = await Promise.all([
                fetchMemberLevel(ctx), fetchMemberStreak(ctx), fetchMemberBadges(ctx),
                fetchNextBadge(ctx), fetchMemberRewards(ctx), fetchGamifyPoints(ctx),
            ]);
            setLevel(lv); setStreak(st);
            setBadges(bd.badges); setNextBadge(nb.badge);
            setRewards(rw.rewards);
            setPoints(Number(lv.total_points ?? rw.totalPoints ?? 0));
            setGamify(gp);
        } catch { /* ignore */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => { load(); }, [load]);

    const onRedeem = async (reward) => {
        setRedeeming(reward.reward_id);
        try {
            const r = await redeemReward(ctx, reward.reward_id);
            if (r.success) {
                toast.success(`Redeemed! ${r.points_spent ? `-${r.points_spent} pts` : ""}`.trim());
                if (typeof r.total_points === "number") setPoints(r.total_points);
                await load();
            } else {
                toast.error(r.message || "Could not redeem this reward.");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Could not redeem this reward.");
        } finally { setRedeeming(0); }
    };

    if (loading) return <InlineLoader label="Loading achievements…" primary={primary} />;

    const cur = level?.current_level;
    const nxt = level?.next_level;
    const toNext = Number(level?.points_to_next || 0);
    const lvlPct = cur && nxt && nxt.points_required > cur.points_required
        ? Math.min(100, Math.round(((points - cur.points_required) / (nxt.points_required - cur.points_required)) * 100))
        : (cur && !nxt ? 100 : 0);
    const earnRows = Object.entries(gamify).filter(([, v]) => Number(v) > 0);

    const STAT_ICON = {
        points: "M12 2l2.6 6.9L22 9.3l-5.5 4.7L18.2 22 12 17.8 5.8 22l1.7-8L2 9.3l7.4-.4L12 2z",
        level: "M8 21h8m-4-4v4M5 4h14v4a7 7 0 11-14 0V4zM5 6H3v2a3 3 0 003 3M19 6h2v2a3 3 0 01-3 3",
        streak: "M12 2C9 6 6.5 8.8 6.5 12a5.5 5.5 0 0011 0C17.5 8.8 15 6 12 2z",
    };
    return (
        <div className="space-y-5">
            {/* Hero stats — points / level / streak with premium icons. */}
            <div className="grid grid-cols-3 gap-3">
                {[
                    { key: "points", value: points, label: "Points", color: primary, bg: `${primary}14` },
                    { key: "level", value: cur ? (cur.level_number ?? "—") : "—", label: cur ? (cur.name || "Level") : "No level", color: "#7c3aed", bg: "#7c3aed14" },
                    { key: "streak", value: Number(streak?.current_streak || 0), label: streak?.longest_streak ? `Streak · best ${streak.longest_streak}` : "Day streak", color: "#f97316", bg: "#f9731614" },
                ].map((s) => (
                    <div key={s.key} className="rounded-2xl border border-gray-100 bg-surface p-3.5 text-center shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
                        <span className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: s.bg, color: s.color }}>
                            <Icon d={STAT_ICON[s.key]} className="h-[18px] w-[18px]" />
                        </span>
                        <p className="text-2xl font-extrabold leading-none" style={{ color: s.color }}>{s.value}</p>
                        <p className="mt-1 truncate text-[11px] font-semibold text-gray-500">{s.label}</p>
                    </div>
                ))}
            </div>

            {/* Level progress */}
            {cur && nxt && (
                <div className="rounded-2xl border border-gray-100 bg-surface p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
                    <div className="flex items-center justify-between text-xs font-bold text-gray-700">
                        <span className="inline-flex items-center gap-1.5">
                            <span className="flex h-5 w-5 items-center justify-center rounded-md text-[10px] font-extrabold text-white" style={{ backgroundColor: primary }}>{cur.level_number ?? ""}</span>
                            {cur.name}
                        </span>
                        <span className="text-gray-400">{nxt.name}</span>
                    </div>
                    <div className="mt-2.5 h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full rounded-full transition-all" style={{ width: `${lvlPct}%`, background: `linear-gradient(90deg, ${primary}, var(--brand-secondary, ${primary}bb))` }} />
                    </div>
                    <p className="mt-2 text-[11px] font-medium text-gray-400">{toNext} pts to {nxt.name}</p>
                </div>
            )}

            {/* Badges */}
            <div>
                <p className="mb-2.5 flex items-center gap-1.5 text-sm font-bold text-gray-900">
                    <Icon d="M12 15a7 7 0 100-14 7 7 0 000 14zm-3.5-1.5L7 22l5-2.5L17 22l-1.5-8.5" className="h-4 w-4" style={{ color: primary }} /> Badges
                </p>
                {badges.length === 0 ? (
                    <TabEmpty icon="M12 15a7 7 0 100-14 7 7 0 000 14zm-3.5-1.5L7 22l5-2.5L17 22l-1.5-8.5" title="No badges yet" hint="Earn points to unlock badges." primary={primary} />
                ) : (
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                        {badges.map((b) => {
                            const earned = Number(b.show) === 1;
                            return (
                                <div key={b.badge_id} className={`rounded-2xl border p-3 text-center transition ${earned ? "border-gray-100 bg-surface shadow-[0_1px_2px_rgba(0,0,0,0.03)]" : "border-dashed border-gray-200 bg-gray-50/50"}`} title={earned ? b.badge_name : (b.need_points || b.badge_name)}>
                                    <div className="relative mx-auto h-14 w-14">
                                        <Img src={b.badge_image} fallback={DEFAULT_BADGE_IMAGE} className={`h-14 w-14 rounded-full object-cover ${earned ? "" : "opacity-40 grayscale"}`} />
                                        {earned && (
                                            <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full text-white ring-2 ring-surface" style={{ backgroundColor: primary }}>
                                                <Icon d="M5 13l4 4L19 7" className="h-3 w-3" />
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-1.5 truncate text-[11px] font-bold text-gray-700">{b.badge_name}</p>
                                    <p className="text-[10px] font-medium text-gray-400">{earned ? "Earned" : `${b.badge_points} pts`}</p>
                                </div>
                            );
                        })}
                    </div>
                )}
                {nextBadge && (
                    <div className="mt-3 flex items-center gap-3 rounded-2xl border p-3" style={{ borderColor: `${primary}26`, background: `${primary}0a` }}>
                        <Img src={nextBadge.badge_image} fallback={DEFAULT_BADGE_IMAGE} className="h-11 w-11 rounded-full object-cover" />
                        <div className="min-w-0">
                            <p className="truncate text-xs font-bold" style={{ color: primary }}>Up next · {nextBadge.badge_name}</p>
                            <p className="truncate text-[11px] text-gray-500">{nextBadge.need_points}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Rewards */}
            <div>
                <p className="mb-2.5 flex items-center gap-1.5 text-sm font-bold text-gray-900">
                    <Icon d="M20 12v9H4v-9M2 7h20v5H2V7zm10 0v14M12 7S9 7 8 5.5 9 3 10 3.5 12 7 12 7zm0 0s3 0 4-1.5S15 3 14 3.5 12 7 12 7z" className="h-4 w-4" style={{ color: primary }} /> Rewards
                </p>
                {rewards.length === 0 ? (
                    <TabEmpty icon="M20 12v9H4v-9M2 7h20v5H2V7zm10 0v14M12 7S9 7 8 5.5 9 3 10 3.5 12 7 12 7zm0 0s3 0 4-1.5S15 3 14 3.5 12 7 12 7z" title="No rewards yet" hint="Redeemable rewards appear here." primary={primary} />
                ) : (
                    <div className="space-y-2.5">
                        {rewards.map((r) => {
                            const earned = Number(r.show) === 1;
                            return (
                                <div key={r.reward_id} className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-surface p-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition hover:shadow-sm">
                                    <Img src={r.reward_image} fallback={DEFAULT_REWARD_IMAGE} className="h-12 w-12 shrink-0 rounded-xl object-cover" />
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-bold text-gray-900">{r.reward_name}</p>
                                        <p className="truncate text-[11px] font-medium text-gray-400">{earned ? (r.reward_desc || "Unlocked") : (r.need_points || `${r.reward_points} pts`)}</p>
                                    </div>
                                    <button
                                        onClick={() => onRedeem(r)}
                                        disabled={redeeming === r.reward_id}
                                        className="inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-bold text-white shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                                        style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                                    >
                                        <Icon d="M20 12v9H4v-9M2 7h20v5H2V7zm10 0v14" className="h-3.5 w-3.5" /> {redeeming === r.reward_id ? "…" : "Redeem"}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* How to earn */}
            {earnRows.length > 0 && (
                <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
                    <p className="mb-2.5 flex items-center gap-1.5 text-xs font-bold text-gray-700">
                        <Icon d="M13 10V3L4 14h7v7l9-11h-7z" className="h-3.5 w-3.5" style={{ color: primary }} /> How to earn points
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {earnRows.map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between rounded-lg bg-surface px-2.5 py-1.5 text-[11px] shadow-[0_1px_1px_rgba(0,0,0,0.03)]">
                                <span className="font-medium text-gray-600">{GAMIFY_LABELS[k] || k}</span>
                                <span className="font-extrabold" style={{ color: primary }}>+{v}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
